import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import DirectAIWalletDashboard, {
  type DirectWalletAccountSession,
  type DirectWalletCapacityUpdate,
  type DirectWalletDashboardProps,
  type DirectWalletGatewayMessage,
  type DirectWalletHardwareOption,
  type DirectWalletHFImportInput,
  type DirectWalletJob,
  type DirectWalletLaunchInput,
  type DirectWalletLaunchResult,
  type DirectWalletLedgerRow,
  type DirectWalletMarketOffer,
  type DirectWalletMember,
  type DirectWalletProviderInput,
  type DirectWalletProviderCreateResult,
  type DirectWalletClusterType,
  type DirectWalletProAccessMode,
  type DirectWalletProAccessOption,
  type DirectWalletProProviderLedgerRow,
  type DirectWalletProStats,
  type DirectWalletRuntimeSessionDetail,
  type DirectWalletRuntimeSessionTurnInput,
  type DirectWalletRuntimeSessionTurnStreamHandlers,
  type DirectWalletStorageFile,
  type DirectWalletStorageRoute,
  type DirectWalletStorageVolumeInput,
  type DirectWalletWorkspaceInfo,
  type DirectWalletWorkspaceInvite,
  type DirectWalletSession,
  type DirectWalletSnapshot,
  type DirectWalletTaskAccess,
  type DirectWalletTaskMessage,
  type DirectWalletTaskDetail,
  type DirectWalletUser,
  type DirectWalletVolume,
  type DirectWalletWorkspaceActivity,
} from "@/components/ai-wallet/DirectAIWalletDashboard";
import { APIError, apiRequest, buildAPIURL, readUISettings, resolveAPIBaseURLCandidates } from "@/lib/api";
import { AUTH_USER_STORAGE_KEY, readStoredAuthSession } from "@/lib/auth-storage";
import {
  createBillingIdempotencyKey,
  createWorkspaceTopupCheckout,
} from "@/lib/billing";
import {
  providerDescriptorForSlug,
  providerSupportsLegacyAPIKeySetup,
  readProviderRegistry,
} from "@/lib/provider-registry";
import {
  createProProvider,
  deleteProProvider,
  discoverProProviderCredit,
  readProAccountPlan,
  readProProviderLedger,
  readProProviders,
  resolveProProviderRefreshResult,
  rotateProProviderKey,
  storeProProviderCreditOnboarding,
  upsertProProviderConnection,
  updateProProvider,
  validateProProvider,
  type ProAccountPlan,
  type ProProviderConnection,
  type ProProviderLedgerEntry,
} from "@/lib/pro";
import {
  googleCreditOnboardingHref,
  googleCreditOnboardingRequiresInput,
  isGoogleProviderSlug,
} from "@/lib/provider-credit";
import { useAuth } from "@/lib/auth-context";
import { isWalletCloudMode } from "@/lib/runtime-mode";
import {
  inviteWorkspaceMember,
  acceptWorkspaceInvite,
  declineWorkspaceInvite,
  hasWorkspacePermission,
  readWorkspace,
  readWorkspaceOrg,
  resolveWorkspaceDashboardState,
  sendWorkspaceMemberCredits,
  setWorkspaceMemberBudget,
  updateWorkspaceProfile,
  workspaceErrorMessage,
  type WorkspaceOrgResponse,
  type WorkspacePendingInvite,
  type WorkspacePublic,
  type WorkspaceResponse,
  type WorkspaceRole,
} from "@/lib/workspace";
import {
  readWalletAppearance,
  readWalletDashboardSnapshot,
  writeWalletDashboardSnapshot,
} from "@/lib/wallet-ui-persistence";
import { mergePersistentWalletLedgerEntries } from "@/lib/wallet-ledger-persistence";
import { walletSpendChart } from "@/lib/wallet-spend-chart";
import { useNotifications, type Notification as AppNotification } from "@/lib/notifications-context";
import { walletDashboardModelID } from "@/lib/wallet-model-routing";
import {
  resolveWalletTopupWorkspace,
  walletTopupReturnPath,
} from "@/lib/wallet-panel-deeplink";
import {
  WALLET_PROVIDER_HARDWARE_API_PATH,
  WALLET_PROVIDER_HARDWARE_LEGACY_API_PATH,
  resolveWalletRunHardwareOptions,
  walletProviderSlugFromLabel,
} from "@/lib/wallet-provider-routing";
import {
  providerLaunchOutcomeMayBeUnknown,
  recoverProviderJobLaunch,
} from "@/lib/provider-launch-recovery";
import {
  walletProAccessLaunchContract,
  walletProAccessMissingRequiredParameters,
} from "@/lib/pro-access-contract";

export const Route = createFileRoute("/wallet")({
  head: () => ({ meta: [{ title: "Wallet · GENRL" }] }),
  validateSearch: (raw: Record<string, unknown>) => ({
    stripe_status: typeof raw.stripe_status === "string" ? raw.stripe_status : undefined,
    checkout_id: typeof raw.checkout_id === "string" ? raw.checkout_id : undefined,
    session_id: typeof raw.session_id === "string" ? raw.session_id : undefined,
    workspace_id: typeof raw.workspace_id === "string" ? raw.workspace_id : undefined,
  }),
  component: WalletRouteComponent,
});

const MICRO = 1_000_000;
const IDLE_REFRESH_MS = 20_000;
const ACTIVE_WORK_REFRESH_MS = 2_000;
const GATEWAY_WORK_REFRESH_MS = 1_000;
const GENRL_MANAGED_STORAGE_QUERY = "?route_id=genrl::managed-storage";
const RECENT_PROVIDER_JOB_PERSIST_MS = 6 * 60 * 60 * 1000;
const PROVIDER_JOBS_VISIBLE_REFRESH_TIMEOUT_MS = 12_000;
const PROVIDER_JOBS_RECONCILE_TIMEOUT_MS = 60_000;
const HOSTED_SESSIONS_API = "/api/auth/hosted/sessions";
const LOW_BALANCE_ALERT_START_CREDITS = 5;
const LOW_BALANCE_ALERT_STORAGE_KEY = "genrl.wallet.lowBalanceAlertThreshold.v1";

type BackendCreditBalance = {
  available_credit_micros?: number;
  reserved_credit_micros?: number;
  spent_credit_micros?: number;
  earned_credit_micros?: number;
  pending_payout_credit_micros?: number;
  total_credit_micros?: number;
  storage_protection_credit_micros?: number;
  protected_snapshot_count?: number;
  protected_volume_count?: number;
};

type WalletLedgerEntry = {
  id?: string;
  transaction_id?: string;
  entry_type?: string;
  debit_micros?: number;
  credit_micros?: number;
  reference_type?: string;
  reference_id?: string;
  metadata_json?: string;
  created_at?: string;
};

type WalletSpendChartPoint = {
  date?: string;
  d: string;
  v: number;
  amount_micros?: number;
};

type WalletProviderJobLease = {
  status?: string;
  last_error?: string;
  provider_lease_id?: string;
  remote_id?: string;
  public_ip?: string;
  runtime_url?: string;
  closed_at?: string;
  cleanup_requested_at?: string;
  next_cleanup_at?: string;
  [key: string]: unknown;
};

type WalletProviderJobAttestation = {
  status?: string;
  evidence_hash?: string;
  last_error?: string;
  verified_at?: string;
  expected?: Record<string, unknown>;
  evidence?: Record<string, unknown>;
  [key: string]: unknown;
};

type WalletProviderJobSummary = {
  id?: string;
  account_name?: string;
  provider_slug?: string;
  provider_name?: string;
  remote_name?: string;
  remote_id?: string;
  status?: string;
  provider_status?: string;
  region?: string;
  gpu?: string;
  image?: string;
  public_ip?: string;
  runtime_url?: string;
  last_error?: string;
  created_at?: string;
  updated_at?: string;
  web_terminal_supported?: boolean;
  launch_request?: Record<string, unknown>;
  lease?: WalletProviderJobLease;
  attestation?: WalletProviderJobAttestation;
};

type RuntimeSession = {
  id: string;
  provider_job_id?: string;
  provider_slug?: string;
  provider_name?: string;
  name?: string;
  model_id?: string;
  runtime_model?: string;
  state?: string;
  turn_count?: number;
  last_output_preview?: string;
  billing_status?: string;
  billing_summary?: Record<string, unknown>;
  metadata?: Record<string, unknown>;
  started_at?: string;
  last_active_at?: string;
  expires_at?: string;
  stopped_at?: string;
  last_error?: string;
  updated_at?: string;
};

type RuntimeSessionTurn = {
  id?: string;
  sequence?: number;
  input_text?: string;
  output_text?: string;
  output?: Record<string, unknown>;
  status?: string;
  started_at?: string;
  completed_at?: string;
  latency_ms?: number;
  error_code?: string;
  error_message?: string;
  created_at?: string;
  updated_at?: string;
};

type RuntimeSessionEvent = {
  id?: string;
  event_type?: string;
  severity?: string;
  message?: string;
  created_at?: string;
  metadata?: Record<string, unknown>;
};

type RuntimeSessionDetailPayload = {
  session?: RuntimeSession;
  turn?: RuntimeSessionTurn;
  turns?: RuntimeSessionTurn[];
  events?: RuntimeSessionEvent[];
};

type HostedSessionsResponse = {
  sessions?: DirectWalletAccountSession[];
};

export type WalletDashboardSearch = {
  stripe_status?: string;
  checkout_id?: string;
  session_id?: string;
  workspace_id?: string;
};

type DashboardLaunchResponse = {
  id?: string;
  provider_job_id?: string;
  genrl_job_id?: string;
  job?: WalletProviderJobSummary;
  provider_job?: WalletProviderJobSummary;
  launch_request?: Record<string, unknown>;
};

type BackendStorageVolume = {
  id?: string | number;
  provider_volume_id?: string;
  name?: string;
  status?: string;
  size_gb?: number;
  file_count?: number;
  used_bytes?: number;
  available_bytes?: number;
  provider?: string;
  provider_slug?: string;
  source_slug?: string;
  route_id?: string;
  region_name?: string;
  environment_name?: string;
  updated_at?: string;
};

type BackendStorageItem = {
  id?: string | number;
  filename?: string;
  provider?: string;
  provider_path?: string;
  provider_status?: string;
  provider_error?: string;
  size_bytes?: number;
  content_type?: string;
  created_at?: string;
  updated_at?: string;
  download_url?: string;
};

type BackendStorageRoute = {
  route_id?: string;
  source_slug?: string;
  provider_slug?: string;
  label?: string;
  detail?: string;
  driver?: string;
  managed?: boolean;
};

type BackendStorageVolumeTypesResponse = {
  volume_types?: string[];
  volume_type_pricing?: Array<{ volume_type?: string; price_per_gb_hour?: number }>;
};

type BackendStorageEnvironmentsResponse = {
  environments?: Array<{ name?: string; region?: string }>;
};

type BackendRunSnapshot = {
  id?: string;
  name?: string;
  status?: string;
  provider_job_id?: string;
  runtime_session_id?: string;
  provider_name?: string;
  hardware?: string;
  manifest_size_bytes?: number;
  output_size_bytes?: number;
  manifest_download_url?: string;
  output_download_url?: string;
  created_at?: string;
  runtime?: Record<string, unknown>;
  metadata?: Record<string, unknown>;
};

type DashboardWorkloadArtifactUpload = {
  artifact_ref?: string;
  filename: string;
  size_bytes?: number;
  content_type?: string;
  kind: "data";
  source: "browser_upload";
};

type DashboardUploadSession = {
  upload_url?: string;
  artifact_ref?: string;
  chunk_size_bytes?: number;
  max_size_bytes?: number;
};

type DashboardUploadStatus = {
  received_bytes?: number;
  total_bytes?: number;
  completed?: boolean;
  failed?: boolean;
  error?: string;
};

type BackendMarketOffer = {
  option_id?: string;
  source?: string;
  provider_slug?: string;
  provider_name?: string;
  gpu?: string;
  gpu_type?: string;
  gpu_count?: number;
  flavor_name?: string;
  region_name?: string;
  stock_available?: number;
  buyer_unit_price_micros?: number | string;
  price_per_hour?: number;
};

type BackendHardwareOption = {
  hardware_id?: string;
  hardware_label?: string;
  gpu_count?: number;
  stock_available?: number;
  launchable?: boolean;
  capacity_status?: string;
  best_price_per_hour?: number;
  best_buyer_price_micros?: number | string;
  original_buyer_price_micros?: number | string;
  discount_percent?: number;
  from_pro_account?: boolean;
  provider_count?: number;
  providers?: string[];
  regions?: string[];
  runtime_classes?: string[];
  pro_access_modes?: string[];
  pro_access_options?: BackendProAccessOption[];
  updated_at?: string;
};

type BackendHardwareResponse = {
  hardware_options?: BackendHardwareOption[];
  refresh_error?: string;
};

type BackendProAccessOption = {
  mode?: string;
  support_mode?: string;
  providers?: string[];
  regions?: string[];
  cluster_types?: string[];
  required_parameters?: string[];
  resolved_parameters?: Record<string, unknown>;
};

type WalletRouteSnapshot = {
  balance: BackendCreditBalance | null;
  ledgerEntries: WalletLedgerEntry[];
  spendChart: WalletSpendChartPoint[];
  jobs: WalletProviderJobSummary[];
  runtimeSessions: RuntimeSession[];
  storageVolumes: BackendStorageVolume[];
  snapshots: BackendRunSnapshot[];
  marketOffers: BackendMarketOffer[];
  hardwareOptions: BackendHardwareOption[];
  workspace?: WorkspacePublic | null;
  workspaceOrg: WorkspaceOrgResponse | null;
  workspaceInvites: WorkspacePendingInvite[];
  planMode: ProAccountPlan["mode"];
  proProviders: ProProviderConnection[];
};

function creditsFromMicros(value?: number | string) {
  return Number(value || 0) / MICRO;
}

function displayedWalletBalanceMicros(balance?: BackendCreditBalance | null) {
  const hasSpendAdjustedBalance =
    balance?.available_credit_micros !== undefined || balance?.reserved_credit_micros !== undefined;
  if (hasSpendAdjustedBalance) {
    return Number(balance?.available_credit_micros || 0) + Number(balance?.reserved_credit_micros || 0);
  }
  return Number(balance?.total_credit_micros || 0);
}

function availableWalletCreditMicros(balance?: BackendCreditBalance | null) {
  return Math.max(0, Number(balance?.available_credit_micros || 0));
}

function microsFromCredits(value: number) {
  return Math.max(1, Math.round(Math.max(0, Number(value || 0)) * MICRO));
}

function relTime(value?: string) {
  if (!value) return "just now";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const mins = Math.max(0, Math.floor((Date.now() - date.getTime()) / 60000));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

function initials(name: string, email = "") {
  const source = String(name || email || "Account").trim();
  const parts = source.includes("@") ? [source[0], source.split("@")[1]?.[0]] : source.split(/\s+/);
  return parts
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("") || "A";
}

function formatBytes(value?: number) {
  const bytes = Number(value || 0);
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let size = bytes;
  let unit = 0;
  while (size >= 1024 && unit < units.length - 1) {
    size /= 1024;
    unit += 1;
  }
  return `${size.toLocaleString(undefined, { maximumFractionDigits: unit === 0 ? 0 : 1 })} ${units[unit]}`;
}

function dashboardUploadTimeoutMs(file: File) {
  return Math.max(120_000, Math.ceil(file.size / 50));
}

function dashboardUploadChunkTimeoutMs(chunk: Blob) {
  return Math.max(60_000, Math.ceil(chunk.size / 50));
}

function normalizeDashboardUploadChunkSize(value?: number) {
  const fallback = 16 * 1024 * 1024;
  const size = Number(value || 0);
  if (!Number.isFinite(size) || size <= 0) return fallback;
  return Math.min(Math.max(Math.floor(size), 1024 * 1024), 64 * 1024 * 1024);
}

async function sha256Hex(blob: Blob) {
  const buffer = await blob.arrayBuffer();
  const digest = await window.crypto.subtle.digest("SHA-256", buffer);
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}

async function uploadDashboardWorkloadFile(file: File): Promise<DashboardWorkloadArtifactUpload> {
  const session = await apiRequest<DashboardUploadSession>("/api/v1/weight-uploads", {
    method: "POST",
    body: { artifact_kind: "data", expires_in_s: 86_400 },
    timeoutMs: 20_000,
    cache: "no-store",
  });
  const uploadURL = String(session.upload_url || "").trim();
  const artifactRef = String(session.artifact_ref || "").trim();
  if (!uploadURL || !artifactRef) {
    throw new APIError(500, "ERR_UPLOAD_SESSION_INVALID", "GENRL could not create an upload session.");
  }
  const maxSizeBytes = Number(session.max_size_bytes || 0);
  if (maxSizeBytes > 0 && file.size > maxSizeBytes) {
    throw new APIError(
      413,
      "ERR_UPLOAD_TOO_LARGE",
      `${file.name} is ${formatBytes(file.size)}. This deployment accepts browser uploads up to ${formatBytes(maxSizeBytes)}.`,
    );
  }

  const chunkSize = normalizeDashboardUploadChunkSize(session.chunk_size_bytes);
  if (file.size === 0) {
    await apiRequest(uploadURL, {
      method: "PUT",
      rawBody: file,
      headers: { "Content-Type": file.type || "application/octet-stream" },
      timeoutMs: dashboardUploadTimeoutMs(file),
      cache: "no-store",
    });
  } else {
    let offset = 0;
    while (offset < file.size) {
      const endExclusive = Math.min(offset + chunkSize, file.size);
      const chunk = file.slice(offset, endExclusive);
      const checksum = await sha256Hex(chunk);
      const status = await apiRequest<DashboardUploadStatus>(uploadURL, {
        method: "PUT",
        rawBody: chunk,
        headers: {
          "Content-Type": file.type || "application/octet-stream",
          "Content-Range": `bytes ${offset}-${endExclusive - 1}/${file.size}`,
          "X-Chunk-SHA256": checksum,
        },
        timeoutMs: dashboardUploadChunkTimeoutMs(chunk),
        cache: "no-store",
      });
      const received = Number(status.received_bytes || 0);
      offset = received > offset ? received : endExclusive;
    }
  }

  return {
    artifact_ref: artifactRef,
    filename: file.name,
    size_bytes: file.size,
    content_type: file.type || "application/octet-stream",
    kind: "data",
    source: "browser_upload",
  };
}

function dashboardDelay(ms: number) {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

async function waitForDashboardUploadCompletion(session: DashboardUploadSession, label: string) {
  const uploadURL = String(session.upload_url || "").trim();
  if (!uploadURL) {
    throw new APIError(500, "ERR_UPLOAD_SESSION_INVALID", "GENRL could not create an import session.");
  }
  const deadline = Date.now() + 30 * 60 * 1000;
  let lastStatus: DashboardUploadStatus | null = null;
  while (Date.now() < deadline) {
    const status = await apiRequest<DashboardUploadStatus>(uploadURL, {
      method: "GET",
      timeoutMs: 20_000,
      cache: "no-store",
    });
    lastStatus = status;
    if (status.failed) {
      throw new APIError(502, "ERR_HF_IMPORT_FAILED", status.error || `${label} import failed.`);
    }
    if (status.completed) return status;
    await dashboardDelay(1_500);
  }
  const received = Number(lastStatus?.received_bytes || 0);
  const total = Number(lastStatus?.total_bytes || 0);
  const progress = total > 0 ? ` (${formatBytes(received)} of ${formatBytes(total)})` : "";
  throw new APIError(504, "ERR_HF_IMPORT_TIMEOUT", `${label} import is still running${progress}.`);
}

function formatCredits(value: number, suffix = "UC") {
  return `${value.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${suffix}`;
}

function formatHourlyCreditsFromMicros(value?: number | string) {
  const credits = creditsFromMicros(value);
  if (!Number.isFinite(credits) || credits <= 0) return "";
  return `${credits.toLocaleString(undefined, { maximumFractionDigits: 4 })} UC/hr`;
}

function formatAdjustmentBps(value?: number) {
  const bps = Number(value || 0);
  if (!Number.isFinite(bps) || bps === 0) return "pass-through";
  const percent = Math.abs(bps / 100).toLocaleString(undefined, { maximumFractionDigits: 2 });
  return bps < 0 ? `${percent}% discount` : `${percent}% premium`;
}

function proProviderPricingSummary(provider: ProProviderConnection) {
  const mode = String(provider.pricing_mode || "").trim().toUpperCase();
  if (mode === "FIXED") return formatHourlyCreditsFromMicros(provider.fixed_unit_price_micros) || "fixed pricing";
  if (mode === "PREMIUM_OR_DISCOUNT") return formatAdjustmentBps(provider.provider_adjustment_bps);
  if (mode === "FLOOR_ADJUSTED") return formatHourlyCreditsFromMicros(provider.min_unit_price_micros) || "floor adjusted";
  return "pass-through";
}

function mapProProviderLedgerEntries(entries: ProProviderLedgerEntry[]): DirectWalletProProviderLedgerRow[] {
  return (entries || []).map((entry, index) => ({
    id: String(entry.id || `pro_provider_ledger_${index + 1}`),
    routeType: String(entry.route_type || "internal").toLowerCase() === "external" ? "external" : "internal",
    hardware: String(entry.hardware || "Provider GPU"),
    region: String(entry.region || ""),
    status: String(entry.status || "unknown"),
    earnedMicros: Number(entry.earned_credit_micros || 0),
    time: relTime(entry.updated_at || entry.created_at),
  }));
}

function modelFromLaunchRequest(launch?: Record<string, unknown>) {
  return String(
    launch?.runtime_model ||
      launch?.model_id ||
      launch?.model ||
      launch?.custom_model_ref ||
      "",
  ).trim();
}

function hardwareFromLaunchRequest(launch?: Record<string, unknown>, fallback = "") {
  return String(
    launch?.hardware_label ||
      launch?.gpu_type_id ||
      launch?.flavor_name ||
      launch?.gpu ||
      fallback ||
      "",
  ).trim();
}

function isGenericGpuLabel(value: unknown) {
  const text = lowerValue(value).replace(/\s+/g, " ");
  return text === "" || /^gpu x \d+$/.test(text) || /^gpu$/.test(text);
}

function concreteGpuLabel(...values: unknown[]) {
  for (const value of values) {
    const text = stringValue(value);
    if (text && !isGenericGpuLabel(text)) return text;
  }
  return firstString(...values);
}

function abbreviateRef(value: string, head = 8, tail = 4) {
  const text = String(value || "").trim();
  if (!text || text.length <= head + tail + 1) return text;
  const [prefix, rest] = text.includes("_") ? [text.slice(0, text.indexOf("_") + 1), text.slice(text.indexOf("_") + 1)] : ["", text];
  if (prefix && rest.length > 8) return `${prefix}${rest.slice(0, Math.max(4, head - prefix.length))}…${rest.slice(-tail)}`;
  return `${text.slice(0, head)}…${text.slice(-tail)}`;
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : {};
}

function stringValue(value: unknown) {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value.trim();
  if (typeof value === "number" || typeof value === "boolean") return String(value).trim();
  return "";
}

function lowerValue(value: unknown) {
  return stringValue(value).toLowerCase();
}

function booleanValue(value: unknown) {
  if (typeof value === "boolean") return value;
  const text = lowerValue(value);
  return ["1", "true", "yes", "verified", "complete", "completed"].includes(text);
}

function firstString(...values: unknown[]) {
  for (const value of values) {
    const text = stringValue(value);
    if (text) return text;
  }
  return "";
}

function gatewayMessagesFromLaunch(value: unknown): DirectWalletGatewayMessage[] {
  const rows = Array.isArray(value) ? value : [];
  return rows
    .map((row) => {
      const record = asRecord(row);
      const rawRole = lowerValue(record.role);
      const role = rawRole === "assistant" || rawRole === "system" ? rawRole : "user";
      const text = firstString(record.text, record.content, record.message);
      return text ? { role, text } : null;
    })
    .filter((message): message is DirectWalletGatewayMessage => Boolean(message));
}

function legacyGatewayPromptFromText(value: unknown): { messages: DirectWalletGatewayMessage[]; latestUserMessage: string } {
  const text = stringValue(value);
  const latestMarker = "\nLatest user message:\n";
  const latestIndex = text.lastIndexOf(latestMarker);
  if (latestIndex < 0 || !text.includes("Continue the conversation below")) {
    return { messages: [], latestUserMessage: "" };
  }
  const latestUserMessage = text.slice(latestIndex + latestMarker.length).trim();
  const beforeLatest = text.slice(0, latestIndex);
  const conversationMarker = "\nConversation so far:\n";
  const conversationIndex = beforeLatest.indexOf(conversationMarker);
  const conversation = conversationIndex >= 0
    ? beforeLatest.slice(conversationIndex + conversationMarker.length).trim()
    : "";
  const messages: DirectWalletGatewayMessage[] = [];
  const turnPattern = /(?:^|\n\n)(User|Assistant|System):\s*([\s\S]*?)(?=\n\n(?:User|Assistant|System):|$)/g;
  let match: RegExpExecArray | null;
  while ((match = turnPattern.exec(conversation)) !== null) {
    const roleText = match[1].toLowerCase();
    const role = roleText === "assistant" || roleText === "system" ? roleText : "user";
    const body = match[2].trim();
    if (body) messages.push({ role, text: body });
  }
  if (latestUserMessage) {
    const last = messages[messages.length - 1];
    if (last?.role !== "user" || last.text.trim() !== latestUserMessage) {
      messages.push({ role: "user", text: latestUserMessage });
    }
  }
  return { messages: messages.slice(-24), latestUserMessage };
}

function formattedStageTime(value: unknown) {
  const text = stringValue(value);
  if (!text) return "";
  const direct = text.match(/^(\d{4}-\d{2}-\d{2})[T\s](\d{2}:\d{2}:\d{2})/);
  if (direct) return `${direct[2]}  ${direct[1]}`;
  const date = new Date(text);
  if (Number.isNaN(date.getTime())) return "";
  const pad = (part: number) => String(part).padStart(2, "0");
  const day = `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`;
  const time = `${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}:${pad(date.getUTCSeconds())}`;
  return `${time}  ${day}`;
}

function firstStageTime(fallback: string, ...values: unknown[]) {
  for (const value of values) {
    const formatted = formattedStageTime(value);
    if (formatted) return formatted;
  }
  return fallback;
}

function shortText(value: unknown, max = 72) {
  const text = stringValue(value).replace(/\s+/g, " ");
  if (!text || text.length <= max) return text;
  return `${text.slice(0, Math.max(8, max - 1)).trim()}…`;
}

function truncateMiddle(value: unknown, max = 82, tail = 18) {
  const text = stringValue(value).replace(/\s+/g, " ");
  if (!text || text.length <= max) return text;
  const safeMax = Math.max(12, max);
  const safeTail = Math.max(4, Math.min(tail, safeMax - 8));
  const head = Math.max(4, safeMax - safeTail - 3);
  return `${text.slice(0, head).trimEnd()}...${text.slice(-safeTail).trimStart()}`;
}

function taskOutputFromLaunch(launch: Record<string, unknown>) {
  const result = asRecord(launch.managed_runtime_result);
  const direct = firstString(
    result.output_text,
    result.output,
    result.text,
    result.response,
    result.final_answer,
    result.content,
    launch.output_text,
    launch.output,
  );
  if (direct) return direct;
  if (Object.keys(result).length === 0) return "";
  try {
    return JSON.stringify(result, null, 2);
  } catch {
    return "";
  }
}

function readableStatus(value?: string) {
  const text = String(value || "").trim().replace(/[_-]+/g, " ");
  if (!text) return "";
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function runtimeSessionTurnOutput(turn: RuntimeSessionTurn) {
  const output = asRecord(turn.output);
  const direct = firstString(
    turn.output_text,
    output.output_text,
    output.output,
    output.text,
    output.response,
    output.final_answer,
    turn.error_message,
  );
  if (direct) return direct;
  if (Object.keys(output).length === 0) return "";
  try {
    return JSON.stringify(output, null, 2);
  } catch {
    return "";
  }
}

function mapRuntimeSession(session?: RuntimeSession, fallbackID = ""): DirectWalletSession {
  const billingSummary = asRecord(session?.billing_summary);
  const status = String(session?.state || "running");
  return {
    id: String(session?.id || fallbackID || "session"),
    model: String(session?.runtime_model || session?.model_id || session?.name || "Runtime"),
    status,
    turns: Math.max(0, Number(session?.turn_count || 0)),
    started: relTime(session?.started_at || session?.last_active_at),
    rate: readableStatus(session?.billing_status) || "Live",
    provider: String(session?.provider_name || session?.provider_slug || "GENRL"),
    hw: hardwareFromLaunchRequest(billingSummary, "Runtime"),
    providerJobId: stringValue(session?.provider_job_id),
    lastActive: relTime(session?.last_active_at || session?.updated_at),
    lastOutput: stringValue(session?.last_output_preview),
    billingStatus: stringValue(session?.billing_status),
    lastError: stringValue(session?.last_error),
    expires: relTime(session?.expires_at),
  };
}

function mapRuntimeSessionMessages(turns: RuntimeSessionTurn[]): DirectWalletTaskMessage[] {
  return turns
    .slice()
    .sort((a, b) => Number(a.sequence || 0) - Number(b.sequence || 0))
    .flatMap((turn, index) => {
      const id = String(turn.id || `turn_${turn.sequence || index}`);
      const status = stringValue(turn.status);
      const input = stringValue(turn.input_text);
      const output = runtimeSessionTurnOutput(turn);
      const messages: DirectWalletTaskMessage[] = [];
      if (input) {
        messages.push({
          id: `${id}:input`,
          role: "user",
          text: input,
          status,
          time: relTime(turn.created_at || turn.started_at),
        });
      }
      if (output) {
        messages.push({
          id: `${id}:output`,
          role: "assistant",
          text: output,
          status,
          time: relTime(turn.completed_at || turn.updated_at),
        });
      } else if (["queued", "running", "running_input"].includes(lowerValue(status))) {
        messages.push({
          id: `${id}:output-pending`,
          role: "assistant",
          text: "Working on it...",
          status,
          time: relTime(turn.updated_at || turn.started_at || turn.created_at),
        });
      }
      return messages;
    });
}

function mapRuntimeSessionDetail(payload: RuntimeSessionDetailPayload, fallbackID = ""): DirectWalletRuntimeSessionDetail {
  const turns = Array.isArray(payload.turns)
    ? payload.turns
    : payload.turn
      ? [payload.turn]
      : [];
  return {
    session: mapRuntimeSession(payload.session, fallbackID),
    messages: mapRuntimeSessionMessages(turns),
    lastError: firstString(payload.session?.last_error, payload.turn?.error_message),
  };
}

function runtimeSessionStreamUserIDHint() {
  if (typeof window === "undefined") return "";
  try {
    const raw = window.localStorage.getItem(AUTH_USER_STORAGE_KEY);
    if (!raw) return "";
    const parsed = JSON.parse(raw) as { id?: unknown };
    return String(parsed?.id || "").trim();
  } catch {
    return "";
  }
}

function runtimeSessionStreamURL(path: string, baseURL: string) {
  if (/^https?:\/\//i.test(path)) return path;
  const base = String(baseURL || "").trim().replace(/\/$/, "");
  return `${base}${path.startsWith("/") ? path : `/${path}`}`;
}

function runtimeSessionStreamHeaders() {
  const settings = readUISettings();
  const headers: Record<string, string> = {
    Accept: "text/event-stream",
    "Content-Type": "application/json",
  };
  if (settings.api_key) headers["X-API-Key"] = settings.api_key;
  if (settings.tenant_id) headers["X-Tenant-Id"] = settings.tenant_id;
  if (!headers["X-Tenant-Id"]) {
    const hintedUserID = runtimeSessionStreamUserIDHint();
    if (hintedUserID) headers["X-Tenant-Id"] = hintedUserID;
  }
  const session = readStoredAuthSession();
  if (session?.token) headers.Authorization = `Bearer ${session.token}`;
  return headers;
}

function parseRuntimeSessionSSEBlock(block: string) {
  let event = "message";
  const data: string[] = [];
  for (const line of block.split("\n")) {
    if (line.startsWith("event:")) event = line.slice("event:".length).trim() || event;
    else if (line.startsWith("data:")) data.push(line.slice("data:".length).trimStart());
  }
  return { event, data: data.join("\n") };
}

function runtimeSessionRawString(value: unknown) {
  if (typeof value === "string") return value;
  return stringValue(value);
}

async function runtimeSessionStreamError(response: Response) {
  const text = await response.text().catch(() => "");
  try {
    const payload = JSON.parse(text) as Record<string, unknown>;
    return new APIError(
      response.status,
      String(payload.error_code || payload.code || "HTTP_ERROR"),
      String(payload.error_detail || payload.detail || payload.message || `HTTP ${response.status}`),
    );
  } catch {
    return new APIError(response.status, "HTTP_ERROR", text || `HTTP ${response.status}`);
  }
}

async function readRuntimeSessionTurnStream(
  response: Response,
  sessionID: string,
  handlers?: DirectWalletRuntimeSessionTurnStreamHandlers,
) {
  if (!response.body) {
    throw new APIError(500, "ERR_RUNTIME_STREAM_EMPTY", "Runtime stream did not include a response body.");
  }
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let doneDetail: DirectWalletRuntimeSessionDetail | null = null;

  async function processBlock(rawBlock: string) {
    const block = rawBlock.trim();
    if (!block) return;
    const { event, data } = parseRuntimeSessionSSEBlock(block);
    if (!data) return;
    const payload = JSON.parse(data) as RuntimeSessionDetailPayload & Record<string, unknown>;
    if (event === "phase") {
      handlers?.onPhase?.({
        turnId: stringValue(payload.turn_id),
        stage: stringValue(payload.stage),
        message: stringValue(payload.message),
      });
    } else if (event === "token") {
      handlers?.onToken?.({
        turnId: stringValue(payload.turn_id),
        delta: runtimeSessionRawString(payload.delta),
      });
    } else if (event === "done") {
      doneDetail = mapRuntimeSessionDetail(payload, sessionID);
      handlers?.onDetail?.(doneDetail);
    } else if (event === "error") {
      throw new APIError(
        500,
        stringValue(payload.error_code) || "ERR_RUNTIME_STREAM_FAILED",
        stringValue(payload.error_detail) || stringValue(payload.message) || "Runtime stream failed.",
      );
    }
  }

  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, "\n");
    let boundary = buffer.indexOf("\n\n");
    while (boundary >= 0) {
      const block = buffer.slice(0, boundary);
      buffer = buffer.slice(boundary + 2);
      await processBlock(block);
      if (doneDetail) return doneDetail;
      boundary = buffer.indexOf("\n\n");
    }
  }
  buffer += decoder.decode();
  if (buffer.trim()) await processBlock(buffer);
  if (doneDetail) return doneDetail;
  throw new APIError(500, "ERR_RUNTIME_STREAM_ENDED", "Runtime stream ended before completion.");
}

function runtimeStreamCanFallback(error: unknown) {
  return error instanceof APIError && (
    error.code === "ERR_RUNTIME_STREAM_UNAVAILABLE" ||
    error.code === "ERR_NETWORK" ||
    error.code === "ERR_TIMEOUT" ||
    error.status === 404 ||
    error.status === 405 ||
    error.status === 501
  );
}

async function streamRuntimeSessionTurn(
  sessionID: string,
  body: { input_text: string; input_artifacts: DashboardWorkloadArtifactUpload[]; restore_context?: Record<string, unknown> },
  handlers?: DirectWalletRuntimeSessionTurnStreamHandlers,
) {
  const path = `/api/v1/runtime/sessions/${encodeURIComponent(sessionID)}/turns/stream`;
  const settings = readUISettings();
  const candidates = resolveAPIBaseURLCandidates(settings);
  let lastError: APIError | null = null;
  for (let index = 0; index < candidates.length; index += 1) {
    try {
      const response = await fetch(runtimeSessionStreamURL(path, candidates[index]), {
        method: "POST",
        headers: runtimeSessionStreamHeaders(),
        body: JSON.stringify(body),
        credentials: "include",
      });
      if (!response.ok) {
        const error = await runtimeSessionStreamError(response);
        if ([404, 405, 501].includes(response.status)) {
          throw new APIError(response.status, "ERR_RUNTIME_STREAM_UNAVAILABLE", error.detail);
        }
        throw error;
      }
      try {
        return await readRuntimeSessionTurnStream(response, sessionID, handlers);
      } catch (error) {
        if (error instanceof APIError) throw error;
        throw new APIError(500, "ERR_RUNTIME_STREAM_FAILED", error instanceof Error ? error.message : "Runtime stream failed.");
      }
    } catch (error) {
      lastError = error instanceof APIError
        ? error
        : new APIError(500, "ERR_NETWORK", error instanceof Error ? error.message : "Network request failed");
      const canTryNext = index < candidates.length - 1 && runtimeStreamCanFallback(lastError);
      if (canTryNext) continue;
      throw lastError;
    }
  }
  throw lastError ?? new APIError(500, "ERR_NETWORK", "Runtime stream request failed.");
}

function ledgerMetadata(entry: WalletLedgerEntry): Record<string, unknown> {
  const raw = entry.metadata_json;
  if (!raw) return {};
  try {
    return asRecord(JSON.parse(raw));
  } catch {
    return {};
  }
}

function addLookupKey(keys: Set<string>, value: unknown) {
  const text = stringValue(value);
  if (text) keys.add(text.toLowerCase());
}

function launchAttribution(launch: Record<string, unknown>) {
  return asRecord(launch.genrl_attribution);
}

function launchReconciliation(launch: Record<string, unknown>) {
  return asRecord(launch.wallet_reconciliation);
}

function ledgerLookupKeys(entry: WalletLedgerEntry) {
  const keys = new Set<string>();
  const metadata = ledgerMetadata(entry);
  addLookupKey(keys, entry.reference_id);
  for (const key of [
    "provider_job_id",
    "job_id",
    "genrl_job_id",
    "wallet_reservation_id",
    "reservation_id",
    "wallet_quote_id",
    "quote_id",
    "client_launch_id",
    "remote_id",
    "provider_remote_id",
  ]) {
    addLookupKey(keys, metadata[key]);
  }
  return keys;
}

function providerJobLookupKeys(job: WalletProviderJobSummary) {
  const keys = new Set<string>();
  const launch = job.launch_request || {};
  const attribution = launchAttribution(launch);
  const reconciliation = launchReconciliation(launch);
  for (const value of [
    job.id,
    job.remote_id,
    launch.genrl_job_id,
    launch.provider_job_id,
    launch.job_id,
    launch.remote_id,
    launch.wallet_reservation_id,
    launch.reservation_id,
    launch.wallet_quote_id,
    launch.quote_id,
    launch.client_launch_id,
    attribution.genrl_job_id,
    attribution.wallet_reservation_id,
    attribution.quote_id,
    attribution.remote_id,
    reconciliation.provider_job_id,
    reconciliation.wallet_reservation_id,
    reconciliation.remote_id,
  ]) {
    addLookupKey(keys, value);
  }
  return keys;
}

function runSnapshotPolicyEnabled(value: unknown) {
  const policy = asRecord(value);
  const mode = lowerValue(policy.mode);
  return mode !== "" && mode !== "none";
}

function runSnapshotLookupKeys(snapshot: BackendRunSnapshot) {
  const keys = new Set<string>();
  const metadata = snapshot.metadata || {};
  const runtime = snapshot.runtime || {};
  for (const value of [
    snapshot.id,
    snapshot.provider_job_id,
    snapshot.runtime_session_id,
    metadata.provider_job_id,
    metadata.genrl_job_id,
    metadata.client_launch_id,
    metadata.runtime_session_id,
    runtime.provider_job_id,
    runtime.genrl_job_id,
    runtime.client_launch_id,
    runtime.runtime_session_id,
  ]) {
    addLookupKey(keys, value);
  }
  return keys;
}

function runtimeSessionLookupKeys(session: RuntimeSession) {
  const keys = new Set<string>();
  const metadata = session.metadata || {};
  const billingSummary = session.billing_summary || {};
  for (const value of [
    session.id,
    session.provider_job_id,
    metadata.provider_job_id,
    metadata.genrl_job_id,
    metadata.client_launch_id,
    metadata.runtime_session_id,
    billingSummary.provider_job_id,
    billingSummary.genrl_job_id,
    billingSummary.client_launch_id,
    billingSummary.runtime_session_id,
  ]) {
    addLookupKey(keys, value);
  }
  return keys;
}

function findRunSnapshotForProviderJob(job: WalletProviderJobSummary, snapshots: BackendRunSnapshot[]) {
  if (snapshots.length === 0) return undefined;
  const keys = providerJobLookupKeys(job);
  const launch = job.launch_request || {};
  for (const value of [launch.runtime_session_id, launch.client_launch_id]) {
    addLookupKey(keys, value);
  }
  return snapshots.find((snapshot) => {
    for (const key of runSnapshotLookupKeys(snapshot)) {
      if (keys.has(key)) return true;
    }
    return false;
  });
}

function findRunSnapshotForRuntimeSession(session: RuntimeSession, snapshots: BackendRunSnapshot[]) {
  if (snapshots.length === 0) return undefined;
  const keys = runtimeSessionLookupKeys(session);
  return snapshots.find((snapshot) => {
    for (const key of runSnapshotLookupKeys(snapshot)) {
      if (keys.has(key)) return true;
    }
    return false;
  });
}

function findLedgerProviderJob(entry: WalletLedgerEntry, jobs: WalletProviderJobSummary[]) {
  const ledgerKeys = ledgerLookupKeys(entry);
  if (ledgerKeys.size === 0) return undefined;
  return jobs.find((job) => {
    for (const key of providerJobLookupKeys(job)) {
      if (ledgerKeys.has(key)) return true;
    }
    return false;
  });
}

const WORKLOAD_STAGE_LABELS = [
  { id: "pending", label: "Pending" },
  { id: "provisioning", label: "VM provisioning" },
  { id: "runtime_ready", label: "Runtime ready" },
  { id: "processing", label: "Processing" },
  { id: "output_recorded", label: "Output recorded" },
  { id: "closing", label: "Closing" },
] as const;

const PRO_VM_STAGE_LABELS = [
  { id: "request_received", label: "Request received" },
  { id: "ssh_key_ready", label: "SSH key generated" },
  { id: "vm_provisioning", label: "Provider VM provisioning" },
  { id: "network_ready", label: "Public IP assigned" },
  { id: "ssh_access_ready", label: "SSH access ready" },
  { id: "vm_lifecycle", label: "VM running" },
] as const;

const PRO_ACCESS_STAGE_LABELS = {
  container: [
    { id: "request_received", label: "Request received" },
    { id: "resource_provisioning", label: "Container provisioning" },
    { id: "access_url_assigned", label: "Service URL assigned" },
    { id: "access_ready", label: "Container access ready" },
    { id: "resource_lifecycle", label: "Container running" },
  ],
  notebook: [
    { id: "request_received", label: "Request received" },
    { id: "resource_provisioning", label: "Notebook provisioning" },
    { id: "access_url_assigned", label: "Notebook URL assigned" },
    { id: "access_ready", label: "Notebook ready" },
    { id: "resource_lifecycle", label: "Notebook running" },
  ],
  api: [
    { id: "request_received", label: "Request received" },
    { id: "resource_provisioning", label: "API runtime provisioning" },
    { id: "access_url_assigned", label: "API base URL assigned" },
    { id: "access_ready", label: "API access ready" },
    { id: "resource_lifecycle", label: "API endpoint running" },
  ],
  cluster: [
    { id: "request_received", label: "Request received" },
    { id: "resource_provisioning", label: "Cluster provisioning" },
    { id: "access_url_assigned", label: "Cluster access assigned" },
    { id: "access_ready", label: "Cluster access ready" },
    { id: "resource_lifecycle", label: "Cluster running" },
  ],
} as const;

const GENERIC_PRO_ACCESS_STAGE_LABELS = [
  { id: "request_received", label: "Request received" },
  { id: "resource_provisioning", label: "Resource provisioning" },
  { id: "access_url_assigned", label: "Access details assigned" },
  { id: "access_ready", label: "Access ready" },
  { id: "resource_lifecycle", label: "Resource running" },
] as const;

function hasLaunchValue(launch: Record<string, unknown>, ...keys: string[]) {
  return keys.some((key) => {
    const value = launch[key];
    if (typeof value === "boolean") return value;
    if (Array.isArray(value)) return value.length > 0;
    if (value && typeof value === "object") return Object.keys(value).length > 0;
    return stringValue(value) !== "";
  });
}

function proAccessModeFromLaunch(launch: Record<string, unknown>) {
  const mode = lowerValue(launch.pro_access_mode);
  return mode === "vm" || mode === "container" || mode === "notebook" || mode === "api" || mode === "cluster" ? mode : "";
}

function proAccessCloseAlreadyScheduledError(value: unknown) {
  const text = stringValue(value).toLowerCase();
  return text.includes("status=409") && (
    text.includes("scheduled for deletion") ||
    text.includes("deleting it again") ||
    (text.includes("deletion process") && text.includes("completed"))
  );
}

function providerLaunchCheckpoint(launch: Record<string, unknown>) {
  const operation = asRecord(launch.provider_operation);
  const execution = asRecord(operation.execution);
  const selectedTarget = firstString(execution.primary_remote_id, execution.PrimaryRemoteID);
  const rows = Array.isArray(operation.checkpoints) ? operation.checkpoints.map(asRecord) : [];
  const launchRows = rows.filter((row) => lowerValue(row.kind || row.Kind) === "instance_create");
  const selectedRows = selectedTarget
    ? launchRows.filter((row) => firstString(row.target_resource_id, row.TargetResourceID) === selectedTarget)
    : launchRows;
  const candidates = selectedRows.length > 0 ? selectedRows : launchRows;
  return candidates.sort((left, right) =>
    firstString(left.updated_at, left.UpdatedAt).localeCompare(firstString(right.updated_at, right.UpdatedAt)),
  ).at(-1) || {};
}

function providerLaunchOperationState(launch: Record<string, unknown>) {
  const checkpoint = providerLaunchCheckpoint(launch);
  return lowerValue(checkpoint.state || checkpoint.State || launch.provider_launch_status);
}

function providerLaunchOperationError(launch: Record<string, unknown>) {
  const checkpoint = providerLaunchCheckpoint(launch);
  if (lowerValue(checkpoint.state || checkpoint.State) !== "failed") {
    return firstString(launch.provider_launch_error);
  }
  const failure = asRecord(checkpoint.last_error || checkpoint.LastError);
  return firstString(
    launch.provider_launch_error,
    failure.Message,
    failure.message,
    failure.Code,
    failure.code,
  );
}

function proAccessLifecycleError(job: WalletProviderJobSummary, launch: Record<string, unknown>) {
  for (const value of [providerLaunchOperationError(launch), launch.provider_launch_error, job.last_error, launch.managed_runtime_error, job.lease?.last_error]) {
    const error = stringValue(value);
    if (!error || proAccessCloseAlreadyScheduledError(error)) continue;
    return error;
  }
  return "";
}

function proAccessRunningLabel(mode: string) {
  if (mode === "container") return "Container running";
  if (mode === "notebook") return "Notebook running";
  if (mode === "api") return "API endpoint running";
  if (mode === "cluster") return "Cluster running";
  return "Resource running";
}

function proAccessClosingLabel(mode: string) {
  if (mode === "container") return "Closing container";
  if (mode === "notebook") return "Closing notebook";
  if (mode === "api") return "Closing API endpoint";
  if (mode === "cluster") return "Closing cluster";
  return "Closing resource";
}

function proAccessClosedLabel(mode: string) {
  if (mode === "container") return "Container closed";
  if (mode === "notebook") return "Notebook closed";
  if (mode === "api") return "API endpoint closed";
  if (mode === "cluster") return "Cluster closed";
  return "Resource closed";
}

function proVMStageDetails(job: WalletProviderJobSummary): Pick<DirectWalletTaskDetail, "stageLabel" | "stages"> {
  const launch = job.launch_request || {};
  const access = asRecord(launch.access);
  const ssh = asRecord(access.ssh);
  const resource = asRecord(access.resource);
  const status = lowerValue(job.status);
  const providerStatus = lowerValue(job.provider_status);
  const accessStatus = lowerValue(access.status);
  const sshStatus = lowerValue(ssh.reachability_status || ssh.status || launch.ssh_access_status || access.status);
  const priceState = lowerValue(launch.wallet_price_state || launch.wallet_settlement_status);
  const cleanupStatus = lowerValue(launch.cleanup_status || launch.managed_runtime_cleanup_status || launch.managed_runtime_reservation_release_status);
  const leaseStatus = lowerValue(job.lease?.status);
  const sshError = firstString(ssh.last_error, launch.ssh_access_error);
  const lastError = firstString(proAccessLifecycleError(job, launch), sshError, job.attestation?.last_error);
  const hasJobRecord = Boolean(firstString(job.id, launch.genrl_job_id));
  const credentialStatus = lowerValue(ssh.credential_status);
  const callerManagedKey = credentialStatus === "caller_managed_public_key";
  const backendManagedKey = credentialStatus.startsWith("generated_per_");
  const networkScope = lowerValue(ssh.network_scope);
  const sshKey = firstString(ssh.public_key, ssh.fingerprint, ssh.key_name, launch.key_name);
  const remoteId = firstString(job.remote_id, launch.remote_id, job.lease?.remote_id, resource.id);
  const providerLaunchState = providerLaunchOperationState(launch);
  const providerLaunchFailed = providerLaunchState === "failed";
  const providerProvisioned = Boolean(remoteId) && (!providerLaunchState || ["succeeded", "completed", "done"].includes(providerLaunchState));
  const sshHost = firstString(job.public_ip, launch.public_ip, job.lease?.public_ip, ssh.host, resource.public_ip, resource.private_ip);
  const sshCommand = firstString(access.primary_value, ssh.command);
  const sshVerified = booleanValue(ssh.verified) || sshStatus === "ready" || sshStatus === "verified" || accessStatus === "ready";
  const sshAccessReady = Boolean(sshCommand && sshHost && (callerManagedKey || backendManagedKey) && sshVerified);
  const sshAccessFailed = ["failed", "error", "not_ready", "unreachable"].some((token) => sshStatus === token || accessStatus === token);
  const cleanupRequested = cleanupStatus.includes("pending") ||
    cleanupStatus.includes("closing") ||
    cleanupStatus.includes("cleanup") ||
    priceState === "pending_provider_cleanup" ||
    leaseStatus === "cleanup_pending" ||
    Boolean(firstString(job.lease?.cleanup_requested_at, job.lease?.next_cleanup_at));
  const closingComplete = Boolean(firstString(job.lease?.closed_at)) ||
    ["terminated", "completed", "done", "closed"].some((token) => status === token) ||
    booleanValue(launch.wallet_price_finalized) ||
    ["settled", "final", "released"].includes(priceState) ||
    ["complete", "completed", "released"].includes(cleanupStatus);
  const failed = providerLaunchFailed || sshAccessFailed ||
    Boolean(lastError) ||
    ["failed", "error", "not_ready"].some((token) => status.includes(token) || providerStatus.includes(token) || priceState.includes(token));
  const finalLabel = closingComplete ? "VM closed" : cleanupRequested ? "Closing VM" : "VM running";
  const done = [
    hasJobRecord,
    Boolean(sshKey),
    providerProvisioned,
    Boolean(sshHost),
    sshAccessReady,
    closingComplete,
  ];
  const active = [
    !hasJobRecord,
    hasJobRecord && !sshKey,
    Boolean(sshKey) && !providerProvisioned,
    providerProvisioned && !sshHost,
    Boolean(sshHost) && !sshAccessReady,
    sshAccessReady && !closingComplete,
  ];
  const activeIndex = closingComplete && !failed ? -1 : active.findIndex(Boolean);
  const failedStageIndex = failed ? done.findIndex((isDone, index) => !isDone && index < PRO_VM_STAGE_LABELS.length - 1) : -1;
  let currentIndex = Math.max(0, done.lastIndexOf(true) + 1);
  if (failed && failedStageIndex >= 0) currentIndex = failedStageIndex;
  else if (activeIndex >= 0) currentIndex = activeIndex;
  else if (closingComplete) currentIndex = PRO_VM_STAGE_LABELS.length - 1;
  currentIndex = Math.min(currentIndex, PRO_VM_STAGE_LABELS.length - 1);
  const details = [
    firstStageTime("VM request accepted", job.created_at),
    sshKey
      ? firstStageTime(callerManagedKey ? "caller-managed SSH public key registered" : "generated per-VM SSH credential", job.created_at)
      : callerManagedKey
        ? "waiting for caller-managed SSH public key"
        : "creating per-VM SSH credential",
    providerLaunchFailed
      ? `Provider rejected launch: ${truncateMiddle(firstString(providerLaunchOperationError(launch), "provider launch failed"), 96, 24)}`
      : providerProvisioned
        ? firstStageTime("provider instance created", job.updated_at, job.created_at)
        : "waiting for provider instance",
    sshHost
      ? `${networkScope === "private" ? "private" : "public"} IP ${sshHost}`
      : "waiting for network address",
    callerManagedKey && sshCommand && networkScope === "private"
      ? "Private-network SSH command is available; backend reachability is not verified"
      : callerManagedKey && sshCommand
        ? "SSH command is available for the caller-managed key; backend reachability is not verified"
        : sshAccessReady
          ? "SSH verified with the generated PEM"
          : sshError
            ? `SSH check failed: ${truncateMiddle(sshError, 82, 18)}`
            : sshHost
              ? "checking SSH reachability with the generated PEM"
              : "waiting for SSH command and PEM key",
    closingComplete
      ? firstStageTime("VM closed", job.lease?.closed_at, job.updated_at)
      : cleanupRequested
        ? firstStageTime("close requested", job.lease?.cleanup_requested_at, job.lease?.next_cleanup_at, job.updated_at)
        : "VM is running; close it when finished",
  ];
  const stages = PRO_VM_STAGE_LABELS.map((stage, index) => {
    let stageStatus: DirectWalletTaskDetail["stages"][number]["status"] = "pending";
    if (failed && index === currentIndex) stageStatus = "failed";
    else if (done[index] || (closingComplete && !failed)) stageStatus = "done";
    else if (index === currentIndex) stageStatus = "active";
    const stageLabel = stage.id === "vm_lifecycle"
      ? finalLabel
      : stage.id === "network_ready" && !sshHost
        ? "Waiting for network address"
        : stage.id === "network_ready" && networkScope === "private"
          ? "Private network attached"
          : stage.id === "ssh_access_ready" && callerManagedKey
            ? "SSH details available"
            : stage.label;
    return {
      ...stage,
      label: stageLabel,
      status: stageStatus,
      detail: details[index],
    };
  });
  const activeStage = stages[currentIndex] || stages[0];
  return {
    stageLabel: failed ? `${activeStage.label} failed` : closingComplete ? "VM closed" : cleanupRequested ? "Closing VM" : activeStage.label,
    stages,
  };
}

function proAccessStageDetails(job: WalletProviderJobSummary): Pick<DirectWalletTaskDetail, "stageLabel" | "stages"> {
  const launch = job.launch_request || {};
  const mode = proAccessModeFromLaunch(launch);
  if (mode === "vm") {
    return proVMStageDetails(job);
  }
  const labels = PRO_ACCESS_STAGE_LABELS[mode as keyof typeof PRO_ACCESS_STAGE_LABELS] || GENERIC_PRO_ACCESS_STAGE_LABELS;
  const access = asRecord(launch.access);
  const web = asRecord(access.web);
  const api = asRecord(access.api);
  const container = asRecord(access.container);
  const cluster = asRecord(access.cluster);
  const headNode = asRecord(cluster.head_node);
  const ssh = asRecord(access.ssh);
  const resource = asRecord(access.resource);
  const executionPlan = asRecord(launch.execution_plan);
  const runtimeResult = asRecord(launch.managed_runtime_result);
  const status = lowerValue(job.status);
  const providerStatus = lowerValue(job.provider_status);
  const accessStatus = lowerValue(access.status);
  const webStatus = lowerValue(web.reachability_status || web.status || launch.notebook_access_status || launch.container_access_status || access.status);
  const apiStatus = lowerValue(api.reachability_status || api.status || launch.api_access_status);
  const containerStatus = lowerValue(container.reachability_status || container.status || launch.container_access_status);
  const sshStatus = lowerValue(ssh.reachability_status || ssh.status || headNode.reachability_status || headNode.status || launch.ssh_access_status);
  const priceState = lowerValue(launch.wallet_price_state || launch.wallet_settlement_status);
  const cleanupStatus = lowerValue(launch.cleanup_status || launch.managed_runtime_cleanup_status || launch.managed_runtime_reservation_release_status);
  const leaseStatus = lowerValue(job.lease?.status);
  const lastError = firstString(proAccessLifecycleError(job, launch), web.last_error, ssh.last_error, access.last_error, job.attestation?.last_error);
  const hasJobRecord = Boolean(firstString(job.id, launch.genrl_job_id));
  const remoteId = firstString(job.remote_id, launch.remote_id, job.lease?.remote_id, resource.id, container.id, headNode.remote_id);
  const providerLaunchState = providerLaunchOperationState(launch);
  const providerLaunchFailed = providerLaunchState === "failed";
  const publicIp = firstString(job.public_ip, launch.public_ip, job.lease?.public_ip, resource.public_ip, container.public_ip, headNode.host);
  const runtimeUrl = firstString(job.runtime_url, launch.runtime_url, job.lease?.runtime_url, web.runtime_url, runtimeResult.runtime_url);
  const clusterID = firstString(cluster.cluster_id, launch.cluster_id);
  const clusterPrimary = firstString(access.primary_value);
  const clusterSSH = firstString(headNode.ssh_command, ssh.command, clusterPrimary.trim().startsWith("ssh ") ? clusterPrimary : "");
  const accessURL = mode === "api"
    ? firstString(api.base_url, access.primary_value, runtimeUrl)
    : mode === "container"
      ? firstString(container.service_url, web.runtime_url, access.primary_value, runtimeUrl)
      : mode === "cluster"
        ? firstString(clusterSSH)
        : firstString(web.notebook_url, web.runtime_url, access.primary_value, runtimeUrl);
  const resourceProvisioned = !providerLaunchFailed && Boolean(remoteId || publicIp || runtimeUrl || accessURL || clusterID) &&
    (!providerLaunchState || ["succeeded", "completed", "done"].includes(providerLaunchState));
  const accessVerified = accessStatus === "ready" ||
    accessStatus === "verified" ||
    webStatus === "ready" ||
    webStatus === "verified" ||
    apiStatus === "ready" ||
    apiStatus === "verified" ||
    containerStatus === "ready" ||
    containerStatus === "verified" ||
    sshStatus === "ready" ||
    sshStatus === "verified" ||
    booleanValue(web.verified) ||
    booleanValue(api.verified) ||
    booleanValue(container.verified) ||
    booleanValue(ssh.verified) ||
    booleanValue(headNode.verified);
  const requiresVerifiedAccess = ["container", "notebook", "api", "cluster"].includes(mode);
  const accessReady = requiresVerifiedAccess ? Boolean(accessURL && accessVerified) : Boolean(accessURL || accessVerified);
  const cleanupRequested = cleanupStatus.includes("pending") ||
    cleanupStatus.includes("closing") ||
    cleanupStatus.includes("cleanup") ||
    priceState === "pending_provider_cleanup" ||
    leaseStatus === "cleanup_pending" ||
    Boolean(firstString(job.lease?.cleanup_requested_at, job.lease?.next_cleanup_at));
  const closingComplete = Boolean(firstString(job.lease?.closed_at)) ||
    ["terminated", "completed", "done", "closed"].some((token) => status === token) ||
    booleanValue(launch.wallet_price_finalized) ||
    ["settled", "final", "released"].includes(priceState) ||
    ["complete", "completed", "released"].includes(cleanupStatus);
  const failed = providerLaunchFailed || Boolean(lastError) ||
    ["failed", "error", "not_ready"].some((token) => status.includes(token) || providerStatus.includes(token) || accessStatus.includes(token) || webStatus.includes(token) || apiStatus.includes(token) || containerStatus.includes(token) || sshStatus.includes(token) || priceState.includes(token));
  const lifecycleLabel = closingComplete ? proAccessClosedLabel(mode) : cleanupRequested ? proAccessClosingLabel(mode) : proAccessRunningLabel(mode);
  const accessLabel = mode === "api" ? "API base URL" : mode === "container" ? "service URL" : mode === "cluster" ? "cluster SSH" : "notebook URL";
  const provisionedByPlan = executionPlan.provider_vm_provisioned === false ? "no dedicated VM required" : "provider resource created";
  const done = [
    hasJobRecord,
    resourceProvisioned,
    Boolean(accessURL),
    accessReady,
    closingComplete,
  ];
  const active = [
    !hasJobRecord,
    hasJobRecord && !resourceProvisioned,
    resourceProvisioned && !accessURL,
    Boolean(accessURL) && !accessReady,
    accessReady && !closingComplete,
  ];
  const activeIndex = closingComplete && !failed ? -1 : active.findIndex(Boolean);
  const failedStageIndex = failed ? done.findIndex((isDone, index) => !isDone && index < labels.length - 1) : -1;
  let currentIndex = Math.max(0, done.lastIndexOf(true) + 1);
  if (failed && failedStageIndex >= 0) currentIndex = failedStageIndex;
  else if (activeIndex >= 0) currentIndex = activeIndex;
  else if (closingComplete) currentIndex = labels.length - 1;
  currentIndex = Math.min(currentIndex, labels.length - 1);

  const details = [
    firstStageTime("access request accepted", job.created_at),
    providerLaunchFailed
      ? `Provider rejected launch: ${truncateMiddle(firstString(providerLaunchOperationError(launch), "provider launch failed"), 96, 24)}`
      : resourceProvisioned
        ? firstStageTime(provisionedByPlan, job.updated_at, job.created_at)
        : "waiting for provider resource",
    accessURL
      ? `${accessLabel}: ${truncateMiddle(accessURL, 82, 18)}`
      : `waiting for ${accessLabel}`,
    accessReady
      ? "access details are ready"
      : accessURL
        ? `checking ${accessLabel} reachability`
        : "waiting for access details",
    closingComplete
      ? firstStageTime(proAccessClosedLabel(mode), job.lease?.closed_at, job.updated_at)
      : cleanupRequested
        ? firstStageTime("close requested", job.lease?.cleanup_requested_at, job.lease?.next_cleanup_at, job.updated_at)
        : `${proAccessRunningLabel(mode)}; close it when finished`,
  ];

  const stages = labels.map((stage, index) => {
    let stageStatus: DirectWalletTaskDetail["stages"][number]["status"] = "pending";
    if (failed && index === currentIndex) stageStatus = "failed";
    else if (done[index] || (closingComplete && !failed)) stageStatus = "done";
    else if (index === currentIndex) stageStatus = "active";
    return {
      ...stage,
      label: stage.id === "resource_lifecycle" ? lifecycleLabel : stage.label,
      status: stageStatus,
      detail: details[index],
    };
  });
  const activeStage = stages[currentIndex] || stages[0];
  return {
    stageLabel: failed ? `${activeStage.label} failed` : closingComplete ? proAccessClosedLabel(mode) : cleanupRequested ? proAccessClosingLabel(mode) : activeStage.label,
    stages,
  };
}

function workloadStageDetails(job: WalletProviderJobSummary): Pick<DirectWalletTaskDetail, "stageLabel" | "stages"> {
  const launch = job.launch_request || {};
  if (proAccessModeFromLaunch(launch)) {
    return proAccessStageDetails(job);
  }
  const executionPlan = asRecord(launch.execution_plan);
  const runtimeResult = asRecord(launch.managed_runtime_result);
  const status = lowerValue(job.status);
  const providerStatus = lowerValue(job.provider_status);
  const runtimeStatus = lowerValue(launch.managed_runtime_status);
  const runtimeProbe = asRecord(launch.managed_runtime_probe);
  const priceState = lowerValue(launch.wallet_price_state || launch.wallet_settlement_status);
  const cleanupStatus = lowerValue(launch.managed_runtime_cleanup_status || launch.cleanup_status || launch.managed_runtime_reservation_release_status);
  const leaseStatus = lowerValue(job.lease?.status);
  const hasJobRecord = Boolean(firstString(job.id, launch.genrl_job_id));
  const providerRuntimeSkipReason = lowerValue(firstString(launch.provider_runtime_skipped, executionPlan.provider_runtime_skipped, runtimeResult.provider_runtime_skipped));
  const providerVMProvisioned = launch.provider_vm_provisioned ?? executionPlan.provider_vm_provisioned ?? runtimeResult.provider_vm_provisioned;
  const providerVMExplicitlySkipped = providerVMProvisioned === false || ["false", "0", "no"].includes(lowerValue(providerVMProvisioned));
  const modelGatewayMode = Boolean(firstString(launch.model_gateway_provider, executionPlan.model_gateway_provider, runtimeResult.model_gateway_provider)) ||
    lowerValue(runtimeResult.mode).includes("model_gateway") ||
    providerStatus.includes("model_gateway") ||
    lowerValue(job.gpu).includes("model gateway");
  const providerRuntimeSkipped = modelGatewayMode ||
    (Boolean(providerRuntimeSkipReason) && !["false", "0", "no"].includes(providerRuntimeSkipReason)) ||
    providerVMExplicitlySkipped;
  const hasRemote = providerRuntimeSkipped || Boolean(firstString(job.remote_id, launch.remote_id, job.public_ip, launch.public_ip, job.runtime_url, launch.runtime_url));
  const runtimeStarted = providerRuntimeSkipped || Boolean(firstString(launch.managed_runtime_started_at, launch.managed_runtime_worker_started_at)) || runtimeStatus === "starting";
  const outputText = taskOutputFromLaunch(launch);
  const runtimeCompleted = runtimeStatus === "completed" || runtimeStatus.endsWith("_completed") || runtimeStatus.includes("completed_with_gateway_fallback");
  const runtimeProbeReachable = booleanValue(runtimeProbe.reachable);
  const runtimeReady = Boolean(outputText) || runtimeCompleted || ["ready", "completed"].includes(runtimeStatus) || runtimeProbeReachable;
  const runtimeOutput = Boolean(outputText) || runtimeCompleted || booleanValue(launch.runtime_output_verified) || hasLaunchValue(launch, "managed_runtime_result");
  const cleanupRequested = cleanupStatus.includes("pending") ||
    cleanupStatus.includes("closing") ||
    cleanupStatus.includes("cleanup") ||
    priceState === "pending_provider_cleanup" ||
    leaseStatus === "cleanup_pending" ||
    Boolean(firstString(job.lease?.cleanup_requested_at, job.lease?.next_cleanup_at));
  const closingComplete = Boolean(firstString(job.lease?.closed_at)) ||
    ["terminated", "completed", "done", "closed"].some((token) => status === token) ||
    booleanValue(launch.wallet_price_finalized) ||
    ["settled", "final", "released"].includes(priceState) ||
    ["complete", "completed", "released"].includes(cleanupStatus);
  const cleanTerminalClose = closingComplete &&
    ["settled", "final", "released"].includes(priceState) &&
    !firstString(job.last_error, job.lease?.last_error, job.attestation?.last_error) &&
    (
      providerStatus.includes("remote_not_found") ||
      providerStatus.includes("user_requested") ||
      providerStatus === "terminated"
    );
  const lastError = cleanTerminalClose ? "" : firstString(job.last_error, launch.managed_runtime_error, job.lease?.last_error, job.attestation?.last_error);
  const failed = !cleanTerminalClose && (
    Boolean(lastError) ||
    ["failed", "error", "not_ready", "prompt_failed"].some((token) => status.includes(token) || runtimeStatus.includes(token) || providerStatus.includes(token) || priceState.includes(token))
  );

  const details = [
    firstStageTime("waiting for provider job", job.created_at),
    providerRuntimeSkipped
      ? firstStageTime("No VM required", launch.managed_runtime_started_at, launch.managed_runtime_worker_started_at)
      : firstStageTime("waiting for provider remote", launch.managed_runtime_started_at, launch.managed_runtime_worker_started_at, job.created_at),
    firstStageTime("waiting for runtime readiness", runtimeProbe.checked_at, launch.managed_runtime_checked_at, launch.managed_runtime_started_at, launch.managed_runtime_worker_started_at),
    firstStageTime("waiting for prompt execution", launch.managed_runtime_checked_at, launch.managed_runtime_completed_at, runtimeProbe.checked_at),
    firstStageTime("waiting for output record", launch.managed_runtime_completed_at, launch.managed_runtime_checked_at),
    firstStageTime("waiting for cleanup", job.lease?.closed_at, job.lease?.cleanup_requested_at, launch.managed_runtime_completed_at, job.updated_at),
  ];

  const baseDone = [hasJobRecord, hasRemote, runtimeReady, runtimeOutput, runtimeOutput, closingComplete];
  const done = closingComplete && !failed ? WORKLOAD_STAGE_LABELS.map(() => true) : baseDone;
  const active = [
    !hasJobRecord,
    hasJobRecord && !hasRemote,
    hasRemote && !runtimeReady,
    runtimeReady && !runtimeOutput,
    runtimeOutput && !cleanupRequested && !closingComplete,
    cleanupRequested && !closingComplete,
  ];
  const activeIndex = closingComplete && !failed ? -1 : active.findIndex(Boolean);
  const failedStageIndex = failed
    ? baseDone.findIndex((isDone, index) => !isDone && index < WORKLOAD_STAGE_LABELS.length - 1)
    : -1;
  let currentIndex = Math.max(0, done.lastIndexOf(true) + 1);
  if (failed && failedStageIndex >= 0) currentIndex = failedStageIndex;
  else if (closingComplete) currentIndex = WORKLOAD_STAGE_LABELS.length - 1;
  else if (activeIndex >= 0) currentIndex = activeIndex;
  const skippedRuntimeStageLabel = modelGatewayMode ? "Model gateway" : "No VM required";

  const stages = WORKLOAD_STAGE_LABELS.map((stage, index) => {
    let stageStatus: DirectWalletTaskDetail["stages"][number]["status"] = "pending";
    if (failed && index === currentIndex) stageStatus = "failed";
    else if (done[index]) stageStatus = "done";
    else if (index === currentIndex) stageStatus = "active";
    let label = stage.id === "provisioning" && providerRuntimeSkipped ? skippedRuntimeStageLabel : stage.label;
    if (stage.id === "runtime_ready" && !runtimeReady) {
      label = "Runtime booting";
    }
    return {
      ...stage,
      label,
      status: stageStatus,
      detail: details[index],
    };
  });
  const activeStage = stages[currentIndex] || stages[0];
  return {
    stageLabel: failed ? `${activeStage.label} failed` : closingComplete ? "Closing complete" : activeStage.label,
    stages,
  };
}

function taskStatusFromProviderJob(job: WalletProviderJobSummary, launch: Record<string, unknown>) {
  const runtimeStatusRaw = firstString(launch.managed_runtime_status);
  const providerStatusRaw = firstString(job.provider_status);
  const jobStatusRaw = firstString(job.status);
  const runtimeStatus = lowerValue(runtimeStatusRaw);
  const providerStatus = lowerValue(providerStatusRaw);

  if (
    runtimeStatus === "completed" ||
    runtimeStatus.endsWith("_completed") ||
    runtimeStatus.includes("completed_with_gateway_fallback") ||
    providerStatus.includes("prompt_completed")
  ) {
    return "completed";
  }

  if (
    runtimeStatus.includes("failed") ||
    runtimeStatus.includes("error") ||
    providerStatus.includes("failed") ||
    providerStatus.includes("error")
  ) {
    return firstString(runtimeStatusRaw, providerStatusRaw, jobStatusRaw, "failed");
  }

  return firstString(jobStatusRaw, runtimeStatusRaw, providerStatusRaw);
}

function buildLedgerTaskDetail(entry: WalletLedgerEntry, job: WalletProviderJobSummary, snapshots: BackendRunSnapshot[] = []): DirectWalletTaskDetail {
  const launch = job.launch_request || {};
  const attribution = launchAttribution(launch);
  const stage = workloadStageDetails(job);
  const prompt = firstString(launch.prompt, launch.input_prompt, launch.task_prompt);
  const legacyGateway = legacyGatewayPromptFromText(prompt);
  const structuredGatewayMessages = gatewayMessagesFromLaunch(launch.gateway_messages);
  const gatewayMessages = structuredGatewayMessages.length > 0 ? structuredGatewayMessages : legacyGateway.messages;
  const displayPrompt = firstString(launch.gateway_latest_user_message, legacyGateway.latestUserMessage, prompt);
  const model = modelFromLaunchRequest(launch);
  const provider = firstString(job.provider_name, launch.provider_name, job.provider_slug, launch.provider_slug, job.account_name, "Provider");
  const gpu = concreteGpuLabel(
    job.gpu,
    attribution.hardware_label,
    launch.hardware_label,
    launch.gpu_label,
    launch.gpu_type_id,
    launch.flavor_name,
    launch.instance_type,
    launch.gpu,
    "GPU",
  );
  const publicIp = firstString(job.public_ip, launch.public_ip, job.lease?.public_ip);
  const runtimeUrl = firstString(job.runtime_url, launch.runtime_url, job.lease?.runtime_url);
  const remoteId = firstString(job.remote_id, launch.remote_id, job.lease?.remote_id);
  const runtimeSessionId = firstString(launch.runtime_session_id);
  const persistent = Boolean(runtimeSessionId) || booleanValue(launch.persist_vm) || lowerValue(launch.session_mode) === "persistent";
  const snapshot = findRunSnapshotForProviderJob(job, snapshots);
  const runtimeResult = asRecord(launch.managed_runtime_result);
  const executionPlan = asRecord(launch.execution_plan);
  const storageContext = asRecord(launch.storage_context);
  const access = asRecord(launch.access);
  const gateway = booleanValue(launch.model_gateway_requested) ||
    lowerValue(launch.execution_mode) === "gateway" ||
    lowerValue(launch.wallet_route_type).includes("gateway") ||
    Boolean(firstString(launch.model_gateway_provider, executionPlan.model_gateway_provider, runtimeResult.model_gateway_provider)) ||
    lowerValue(runtimeResult.mode).includes("model_gateway") ||
    lowerValue(job.provider_status).includes("model_gateway") ||
    lowerValue(gpu).includes("model gateway");
  const snapshotId = firstString(
    snapshot?.id,
    launch.run_snapshot_id,
    runtimeResult.run_snapshot_id,
    runtimeResult.snapshot_id,
    asRecord(launch.run_snapshot).id,
    asRecord(runtimeResult.run_snapshot).id,
  );
  const snapshotEnabled =
    Boolean(snapshotId) ||
    Boolean(snapshot) ||
    booleanValue(launch.save_run_context) ||
    runSnapshotPolicyEnabled(launch.snapshot_policy) ||
    booleanValue(storageContext.enabled);
  const gpuVerified = !isGenericGpuLabel(gpu) && (booleanValue(launch.hardware_verified) || Boolean(publicIp || runtimeUrl || remoteId));
  const title =
    firstString(launch.service_name) ||
    shortText(displayPrompt, 64) ||
    firstString(launch.name, job.remote_name) ||
    model ||
    ledgerTitle(entry);
  return {
    id: firstString(job.id, launch.genrl_job_id, entry.reference_id, entry.id),
    title,
    input: displayPrompt,
    output: taskOutputFromLaunch(launch),
    prompt: displayPrompt,
    model,
    provider,
    providerStatus: firstString(job.provider_status, launch.managed_runtime_status),
    status: taskStatusFromProviderJob(job, launch),
    stageLabel: stage.stageLabel,
    gpu,
    gpuVerified,
    publicIp,
    runtimeUrl,
    remoteId,
    region: firstString(job.region, launch.provider_region, launch.environment_name, attribution.region),
    image: firstString(job.image, launch.image_name, launch.docker_image),
    verificationStatus: firstString(launch.verification_status),
    verificationSummary: firstString(launch.verification_summary),
    attestationStatus: firstString(job.attestation?.status),
    attestationHash: firstString(job.attestation?.evidence_hash),
    attestationError: firstString(job.attestation?.last_error),
    lastError: proAccessModeFromLaunch(launch)
      ? proAccessLifecycleError(job, launch)
      : firstString(job.last_error, launch.managed_runtime_error, job.lease?.last_error),
    leaseStatus: firstString(job.lease?.status),
    providerJobId: firstString(job.id, launch.genrl_job_id, launch.provider_job_id),
    clientLaunchId: firstString(launch.client_launch_id),
    runtimeSessionId,
    persistent,
    gateway,
    sshTerminal: booleanValue(launch.ssh_terminal_requested),
    webTerminalSupported: Boolean(job.web_terminal_supported),
    accessMode: firstString(launch.pro_access_mode),
    snapshotEnabled,
    snapshotId,
    gatewayMessages: gatewayMessages.length > 0 ? gatewayMessages : undefined,
    access: Object.keys(access).length > 0 ? access as DirectWalletTaskAccess : undefined,
    stages: stage.stages,
  };
}

function ledgerEntryNetMicros(entry: WalletLedgerEntry) {
  return Number(entry.credit_micros || 0) - Number(entry.debit_micros || 0);
}

function providerJobStableID(job: WalletProviderJobSummary) {
  const launch = job.launch_request || {};
  const attribution = launchAttribution(launch);
  return firstString(job.id, launch.genrl_job_id, launch.provider_job_id, attribution.genrl_job_id, job.remote_id, launch.remote_id);
}

function providerJobPersistenceKey(job: WalletProviderJobSummary) {
  const launch = job.launch_request || {};
  return providerJobStableID(job) || firstString(launch.client_launch_id, launch.runtime_session_id, job.remote_name);
}

function providerJobSortMs(job: WalletProviderJobSummary) {
  const launch = job.launch_request || {};
  const text = firstString(
    job.created_at,
    launch.created_at,
    launch.started_at,
    launch.client_launched_at,
    job.updated_at,
    launch.updated_at,
  );
  const ms = new Date(text).getTime();
  return Number.isFinite(ms) ? ms : 0;
}

function ledgerEntrySortMs(entry: WalletLedgerEntry) {
  const ms = new Date(String(entry.created_at || "")).getTime();
  return Number.isFinite(ms) ? ms : 0;
}

function shouldPersistProviderJob(job: WalletProviderJobSummary) {
  const status = lowerValue(job.status);
  if (["launching", "running", "queued", "unknown"].includes(status)) return true;
  const sortMs = providerJobSortMs(job);
  return sortMs === 0 || Date.now() - sortMs <= RECENT_PROVIDER_JOB_PERSIST_MS;
}

function runtimeSessionLooksLedgerActive(session: RuntimeSession) {
  const states = [session.state, session.billing_status].map(lowerValue);
  if (session.last_error || session.stopped_at || states.some(state =>
    state.includes("failed") ||
    state.includes("error") ||
    state.includes("closing") ||
    state.includes("closed") ||
    state.includes("terminated") ||
    state.includes("stopped") ||
    state.includes("expired")
  )) return false;
  return states.some(state =>
    state.includes("active") ||
    state.includes("idle") ||
    state.includes("running") ||
    state.includes("reserved")
  );
}

function runtimeSessionLooksLedgerHistory(session: RuntimeSession) {
  if (runtimeSessionLooksLedgerActive(session)) return true;
  const sortMs = runtimeSessionSortMs(session);
  if (sortMs > 0 && Date.now() - sortMs > RECENT_PROVIDER_JOB_PERSIST_MS) return false;
  const states = [session.state, session.billing_status].map(lowerValue);
  const stopped = Boolean(session.stopped_at) || states.some(state =>
    state.includes("closing") ||
    state.includes("closed") ||
    state.includes("terminated") ||
    state.includes("stopped") ||
    state.includes("settled")
  );
  if (!stopped) return false;
  return Boolean(session.id || session.provider_job_id || session.last_output_preview || Number(session.turn_count || 0) > 0);
}

function mergePersistentProviderJobs(previous: WalletProviderJobSummary[], incoming: WalletProviderJobSummary[]) {
  const byKey = new Map<string, WalletProviderJobSummary>();
  const anonymous: WalletProviderJobSummary[] = [];
  const incomingKeys = new Set<string>();
  incoming.forEach((job) => addProviderJobKeys(incomingKeys, job));

  const add = (job: WalletProviderJobSummary, allowPersisted: boolean) => {
    if (allowPersisted && !shouldPersistProviderJob(job)) return;
    if (allowPersisted && providerJobIsRepresented(incomingKeys, job)) return;
    const key = providerJobPersistenceKey(job);
    if (!key) {
      if (!allowPersisted) anonymous.push(job);
      return;
    }
    if (!byKey.has(key)) byKey.set(key, job);
  };

  incoming.forEach((job) => add(job, false));
  previous.forEach((job) => add(job, true));

  return [...byKey.values(), ...anonymous]
    .sort((a, b) => providerJobSortMs(b) - providerJobSortMs(a))
    .slice(0, 48);
}

function smallTextHash(value: string) {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(36);
}

type WalletTaskOutputNotification = {
  jobID: string;
  signature: string;
  title: string;
  detail: string;
};
type WalletNotificationPush = (notification: Omit<AppNotification, "id" | "createdAt" | "read">) => void;

function walletTaskOutputNotification(job: WalletProviderJobSummary): WalletTaskOutputNotification | null {
  const launch = job.launch_request || {};
  const output = taskOutputFromLaunch(launch);
  if (!output) return null;
  const attribution = launchAttribution(launch);
  const jobID = providerJobStableID(job);
  if (!jobID) return null;
  const prompt = firstString(launch.prompt, launch.input_prompt, launch.task_prompt);
  const model = modelFromLaunchRequest(launch);
  const title =
    firstString(launch.service_name) ||
    shortText(prompt, 72) ||
    model ||
    firstString(launch.name, job.remote_name, "GENRL workload");
  const provider = firstString(job.provider_name, launch.provider_name, job.provider_slug, launch.provider_slug, "Provider");
  const gpu = concreteGpuLabel(
    job.gpu,
    attribution.hardware_label,
    launch.hardware_label,
    launch.gpu_label,
    launch.gpu_type_id,
    launch.flavor_name,
    launch.instance_type,
    launch.gpu,
  );
  const outputVersion = firstString(
    launch.runtime_session_last_turn_id,
    launch.runtime_turn_id,
    launch.managed_runtime_completed_at,
    launch.managed_runtime_checked_at,
    smallTextHash(output),
  );
  return {
    jobID,
    signature: `${jobID}:${outputVersion}`,
    title,
    detail: [provider, gpu].filter(Boolean).join(" · "),
  };
}

function workloadNotificationsEnabled(userID?: string) {
  return readWalletAppearance("", userID).notifications.workload;
}

function requestWorkloadNotificationPermission(userID?: string) {
  if (typeof window === "undefined" || !workloadNotificationsEnabled(userID)) return;
  if (!("Notification" in window)) return;
  // Launching work should not trigger a browser permission prompt that blocks
  // the production run flow. Settings still control in-app workload notices;
  // browser-level notices are sent only after the user has already granted them.
}

function pushWalletNotification(pushNotification: WalletNotificationPush, notification: Omit<AppNotification, "id" | "createdAt" | "read">, userID?: string) {
  if (!workloadNotificationsEnabled(userID)) return;
  pushNotification(notification);
}

function billingNotificationsEnabled(userID?: string) {
  return readWalletAppearance("", userID).notifications.billing;
}

function workspaceNotificationsEnabled(userID?: string) {
  return readWalletAppearance("", userID).notifications.team;
}

function lowBalanceAlertStorageKey(userID?: string) {
  return `${LOW_BALANCE_ALERT_STORAGE_KEY}:${userID || "anonymous"}`;
}

function readLowBalanceAlertThreshold(userID?: string) {
  if (typeof window === "undefined") return null;
  const raw = window.localStorage.getItem(lowBalanceAlertStorageKey(userID));
  if (!raw) return null;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
}

function writeLowBalanceAlertThreshold(userID: string | undefined, threshold: number | null) {
  if (typeof window === "undefined") return;
  const key = lowBalanceAlertStorageKey(userID);
  if (threshold === null) {
    window.localStorage.removeItem(key);
    return;
  }
  window.localStorage.setItem(key, String(threshold));
}

function lowBalanceAlertThreshold(balance?: BackendCreditBalance | null) {
  if (!balance) return null;
  const availableCredits = availableWalletCreditMicros(balance) / MICRO;
  if (availableCredits >= LOW_BALANCE_ALERT_START_CREDITS) return null;
  if (availableCredits <= 0) return 0;
  return Math.max(1, Math.min(LOW_BALANCE_ALERT_START_CREDITS, Math.floor(availableCredits) + 1));
}

function lowBalanceAlertTitle(threshold: number) {
  return threshold <= 0 ? "Wallet balance depleted" : `Wallet balance under $${threshold}`;
}

function lowBalanceAlertBody(balance: BackendCreditBalance, threshold: number) {
  const availableCredits = availableWalletCreditMicros(balance) / MICRO;
  const protectedCredits = Number(balance.storage_protection_credit_micros || 0) / MICRO;
  const protectedCount = Number(balance.protected_snapshot_count || 0) + Number(balance.protected_volume_count || 0);
  const balanceText = `$${availableCredits.toFixed(2)} available`;
  const protectionText = protectedCredits > 0 && protectedCount > 0
    ? ` $${protectedCredits.toFixed(2)} is protected for ${protectedCount} snapshot/volume resource${protectedCount === 1 ? "" : "s"}.`
    : "";
  return threshold <= 0
    ? `No spendable credits remain.${protectionText}`
    : `${balanceText}.${protectionText} Add credits to keep workloads and storage healthy.`;
}

function showWorkloadOutputNotification(candidate: WalletTaskOutputNotification, userID?: string, pushNotification?: WalletNotificationPush) {
  if (!workloadNotificationsEnabled(userID)) return;
  pushNotification?.({
    kind: "job",
    title: "Output ready",
    body: candidate.detail ? `${candidate.title} · ${candidate.detail}` : candidate.title,
  });
  toast.success(`Output ready: ${shortText(candidate.title, 44)}`);
  if (typeof window === "undefined" || !("Notification" in window)) return;
  if (window.Notification.permission !== "granted") return;
  if (typeof document !== "undefined" && document.visibilityState === "visible") return;
  const notification = new window.Notification("GENRL output ready", {
    body: candidate.detail ? `${candidate.title}\n${candidate.detail}` : candidate.title,
    tag: candidate.signature,
  });
  notification.onclick = () => {
    window.focus();
    notification.close();
  };
}

function ledgerTaskGroupKey(entry: WalletLedgerEntry, job: WalletProviderJobSummary) {
  return providerJobStableID(job) || firstString(entry.reference_id, entry.id);
}

function addProviderJobKeys(keys: Set<string>, job: WalletProviderJobSummary) {
  for (const key of providerJobLookupKeys(job)) {
    if (key) keys.add(key);
  }
}

function providerJobIsRepresented(keys: Set<string>, job: WalletProviderJobSummary) {
  for (const key of providerJobLookupKeys(job)) {
    if (keys.has(key)) return true;
  }
  return false;
}

function providerJobSyntheticNetMicros(job: WalletProviderJobSummary) {
  const launch = job.launch_request || {};
  const reconciliation = asRecord(launch.wallet_reconciliation);
  const settlementStatus = firstString(
    reconciliation.settlement_status,
    reconciliation.status,
    launch.wallet_settlement_status,
  ).toLowerCase();
  if (["released", "refunded", "cancelled", "canceled"].includes(settlementStatus)) return 0;

  for (const value of [launch.wallet_captured_amount_micros, reconciliation.captured_amount_micros]) {
    if (value === undefined || value === null || value === "") continue;
    const micros = Number(value);
    if (Number.isFinite(micros) && micros >= 0) return -Math.round(micros);
  }
  if (["settled", "captured", "completed"].includes(settlementStatus)) return 0;

  for (const value of [
    launch.wallet_estimated_debit_micros,
    launch.wallet_reserve_amount_micros,
    launch.max_spend_micros,
  ]) {
    const micros = Number(value || 0);
    if (Number.isFinite(micros) && micros > 0) return -Math.round(micros);
  }
  const maxCredits = Number(launch.max_price_credits || 0);
  return Number.isFinite(maxCredits) && maxCredits > 0 ? -Math.round(maxCredits * MICRO) : 0;
}

function syntheticLedgerEntryForProviderJob(job: WalletProviderJobSummary, index: number): WalletLedgerEntry {
  const launch = job.launch_request || {};
  const jobID = providerJobPersistenceKey(job) || `provider_job_${index + 1}`;
  const createdAt = firstString(
    job.created_at,
    launch.created_at,
    launch.started_at,
    launch.client_launched_at,
    job.updated_at,
    launch.updated_at,
  );
  return {
    id: `job:${jobID}`,
    entry_type: "job_pending",
    reference_type: "provider_job",
    reference_id: jobID,
    metadata_json: JSON.stringify({
      provider_job_id: providerJobStableID(job),
      client_launch_id: firstString(launch.client_launch_id),
      runtime_session_id: firstString(launch.runtime_session_id),
    }),
    created_at: createdAt,
  };
}

function optimisticLedgerEntryForProviderJob(job: WalletProviderJobSummary, index: number): WalletLedgerEntry {
  const entry = syntheticLedgerEntryForProviderJob(job, index);
  const debitMicros = Math.max(0, -providerJobSyntheticNetMicros(job));
  return {
    ...entry,
    debit_micros: debitMicros,
    credit_micros: 0,
  };
}

function mapLedgerEntryRow(
  entry: WalletLedgerEntry,
  index: number,
  options?: { job?: WalletProviderJobSummary; netMicros?: number; snapshots?: BackendRunSnapshot[] },
): DirectWalletLedgerRow {
  const netMicros = options?.netMicros ?? ledgerEntryNetMicros(entry);
  const net = netMicros / MICRO;
  const type = String(entry.entry_type || "").toLowerCase();
  const referenceType = String(entry.reference_type || "").trim();
  const referenceID = String(entry.reference_id || "").trim();
  const task = options?.job ? buildLedgerTaskDetail(entry, options.job, options.snapshots || []) : undefined;
  const taskID = task?.id || (options?.job ? providerJobStableID(options.job) : "");
  const rowID = String(taskID || entry.id || referenceID || `ledger_${index + 1}`);
  const fallbackMeta = referenceType && referenceID ? `${referenceType} · ${abbreviateRef(referenceID, 10, 4)}` : abbreviateRef(referenceID) || referenceType || "GENRL wallet";
  return {
    id: rowID,
    displayId: abbreviateRef(rowID),
    kind: task ? "run" : type.includes("purchase") ? "topup" : type.includes("transfer") ? "send" : type.includes("storage") ? "store" : "run",
    label: task?.title || ledgerTitle(entry),
    meta: task ? [task.stageLabel, task.provider, task.gpu].filter(Boolean).join(" · ") : fallbackMeta,
    amt: Number(net.toFixed(2)),
    time: relTime(entry.created_at),
    task,
  };
}

function runtimeSessionSortMs(session: RuntimeSession) {
  const ms = new Date(String(session.last_active_at || session.updated_at || session.started_at || "")).getTime();
  return Number.isFinite(ms) ? ms : 0;
}

function runtimeSessionStableKey(session: RuntimeSession) {
  const metadata = session.metadata || {};
  const billingSummary = session.billing_summary || {};
  return firstString(
    session.id,
    session.provider_job_id,
    metadata.runtime_session_id,
    metadata.provider_job_id,
    metadata.genrl_job_id,
    metadata.client_launch_id,
    billingSummary.runtime_session_id,
    billingSummary.provider_job_id,
    billingSummary.genrl_job_id,
    billingSummary.client_launch_id,
  );
}

function mergePersistentRuntimeSessions(previous: RuntimeSession[], incoming: RuntimeSession[]) {
  const byKey = new Map<string, RuntimeSession>();
  const anonymous: RuntimeSession[] = [];
  const incomingKeys = new Set<string>();

  incoming.forEach((session) => {
    const key = runtimeSessionStableKey(session);
    if (key) incomingKeys.add(key.toLowerCase());
  });

  const add = (session: RuntimeSession, allowPersisted: boolean) => {
    if (allowPersisted && !runtimeSessionLooksLedgerHistory(session)) return;
    const key = runtimeSessionStableKey(session);
    if (!key) {
      if (!allowPersisted) anonymous.push(session);
      return;
    }
    if (allowPersisted && incomingKeys.has(key.toLowerCase())) return;
    byKey.set(key, session);
  };

  incoming.forEach((session) => add(session, false));
  previous.forEach((session) => add(session, true));

  return [...byKey.values(), ...anonymous]
    .sort((a, b) => runtimeSessionSortMs(b) - runtimeSessionSortMs(a))
    .slice(0, 96);
}

function mapRuntimeSessionLedgerRow(session: RuntimeSession, index: number, snapshots: BackendRunSnapshot[] = []): DirectWalletLedgerRow {
  const snapshot = findRunSnapshotForRuntimeSession(session, snapshots);
  const sessionID = stringValue(session.id) || `runtime_session_${index + 1}`;
  const providerJobID = stringValue(session.provider_job_id);
  const model = stringValue(session.runtime_model || session.model_id) || "Runtime";
  const provider = stringValue(session.provider_name || session.provider_slug) || "GENRL";
  const hw = hardwareFromLaunchRequest({ ...(session.metadata || {}), ...(session.billing_summary || {}) }, "Runtime");
  const state = stringValue(session.state) || "idle";
  const billingStatus = stringValue(session.billing_status);
  const task: DirectWalletTaskDetail = {
    id: providerJobID || sessionID,
    title:
      stringValue(session.name) ||
      shortText(stringValue(session.last_output_preview), 64) ||
      "Persistent VM session",
    output: stringValue(session.last_output_preview),
    model,
    provider,
    providerStatus: state,
    status: state,
    stageLabel: readableStatus(state) || "Idle",
    gpu: hw,
    gpuVerified: hw !== "Runtime",
    leaseStatus: billingStatus,
    providerJobId: providerJobID,
    runtimeSessionId: sessionID,
    persistent: true,
    snapshotEnabled: Boolean(snapshot?.id),
    snapshotId: stringValue(snapshot?.id),
    stages: [
      { id: "session", label: "Persistent session", status: "done", detail: `session ${sessionID}` },
      { id: "runtime", label: readableStatus(state) || "Runtime", status: "done", detail: billingStatus ? readableStatus(billingStatus) : undefined },
    ],
  };
  return {
    id: providerJobID || sessionID,
    displayId: abbreviateRef(providerJobID || sessionID),
    kind: "run",
    label: task.title,
    meta: [task.stageLabel, task.provider, task.gpu].filter(Boolean).join(" · "),
    amt: 0,
    time: relTime(session.last_active_at || session.updated_at || session.started_at),
    task,
  };
}

function addLedgerRowTaskKeys(keys: Set<string>, row: DirectWalletLedgerRow) {
  const task = row.task;
  if (!task) return;
  for (const value of [
    row.id,
    task.id,
    task.providerJobId,
    task.runtimeSessionId,
    task.clientLaunchId,
    task.remoteId,
  ]) {
    const key = stringValue(value).toLowerCase();
    if (key) keys.add(key);
  }
}

function mapLedgerEntries(entries: WalletLedgerEntry[], jobs: WalletProviderJobSummary[], snapshots: BackendRunSnapshot[] = [], runtimeSessions: RuntimeSession[] = []): DirectWalletLedgerRow[] {
  type LedgerOutput = { index: number; sortMs: number; row: DirectWalletLedgerRow };
  type TaskGroup = {
    index: number;
    sortMs: number;
    entry: WalletLedgerEntry;
    job: WalletProviderJobSummary;
    netMicros: number;
  };

  const passthrough: LedgerOutput[] = [];
  const taskGroups = new Map<string, TaskGroup>();
  const representedJobKeys = new Set<string>();

  entries.forEach((entry, index) => {
    const sortMs = ledgerEntrySortMs(entry);
    const providerJob = findLedgerProviderJob(entry, jobs);
    if (!providerJob) {
      passthrough.push({ index, sortMs, row: mapLedgerEntryRow(entry, index) });
      return;
    }
    addProviderJobKeys(representedJobKeys, providerJob);

    const groupKey = ledgerTaskGroupKey(entry, providerJob);
    const existing = taskGroups.get(groupKey);
    if (!existing) {
      taskGroups.set(groupKey, {
        index,
        sortMs,
        entry,
        job: providerJob,
        netMicros: ledgerEntryNetMicros(entry),
      });
      return;
    }

    existing.netMicros += ledgerEntryNetMicros(entry);
    if (index < existing.index) {
      existing.index = index;
      existing.sortMs = sortMs;
      existing.entry = entry;
      existing.job = providerJob;
    }
  });

  const taskRows = Array.from(taskGroups.values()).map((group) => ({
    index: group.index,
    sortMs: group.sortMs,
    row: mapLedgerEntryRow(group.entry, group.index, {
      job: group.job,
      netMicros: group.netMicros,
      snapshots,
    }),
  }));

  const syntheticJobRows = jobs
    .filter((job) => shouldPersistProviderJob(job) && !providerJobIsRepresented(representedJobKeys, job))
    .map((job, syntheticIndex) => {
      const entry = syntheticLedgerEntryForProviderJob(job, syntheticIndex);
      return {
        index: entries.length + syntheticIndex,
        sortMs: providerJobSortMs(job),
        row: mapLedgerEntryRow(entry, entries.length + syntheticIndex, {
          job,
          netMicros: providerJobSyntheticNetMicros(job),
          snapshots,
        }),
      };
    });

  const representedRuntimeKeys = new Set<string>();
  [...taskRows, ...syntheticJobRows].forEach((item) => addLedgerRowTaskKeys(representedRuntimeKeys, item.row));
  const syntheticSessionRows = runtimeSessions
    .filter((session) => runtimeSessionLooksLedgerHistory(session))
    .filter((session) => {
      for (const key of runtimeSessionLookupKeys(session)) {
        if (representedRuntimeKeys.has(key)) return false;
      }
      return true;
    })
    .map((session, syntheticIndex) => ({
      index: entries.length + jobs.length + syntheticIndex,
      sortMs: runtimeSessionSortMs(session),
      row: mapRuntimeSessionLedgerRow(session, entries.length + jobs.length + syntheticIndex, snapshots),
    }));

  return [...passthrough, ...taskRows, ...syntheticJobRows, ...syntheticSessionRows]
    .sort((a, b) => {
      if (b.sortMs !== a.sortMs) return b.sortMs - a.sortMs;
      return a.index - b.index;
    })
    .map((item) => item.row);
}

function ledgerTitle(entry: WalletLedgerEntry) {
  const type = String(entry.entry_type || "").toLowerCase();
  if (type.includes("purchase")) return "Credits added";
  if (type.includes("transfer")) return "Credit transfer";
  if (type.includes("job_cost")) return "Compute workload";
  if (type.includes("reserve") && !type.includes("release")) return "Compute reserved";
  if (type.includes("release") || type.includes("refund")) return "Credits released";
  if (type.includes("payout")) return "Payout";
  if (type.includes("storage")) return "Storage usage";
  return "Wallet activity";
}

function mapJobs(jobs: WalletProviderJobSummary[]): DirectWalletJob[] {
  return jobs.slice(0, 8).map((job) => ({
    id: String(job.id || job.remote_id || "job"),
    model: modelFromLaunchRequest(job.launch_request) || String(job.remote_name || "Provider job"),
    provider: String(job.provider_name || job.provider_slug || "Provider"),
    hw: hardwareFromLaunchRequest(job.launch_request, job.gpu || "GPU"),
    status: String(job.status || "unknown"),
    cost: Number(job.launch_request?.max_price_credits || 0),
    dur: "—",
    time: relTime(job.updated_at || job.created_at),
    clientLaunchId: firstString(job.launch_request?.client_launch_id),
    webTerminalSupported: Boolean(job.web_terminal_supported),
  }));
}

function mapSessions(sessions: RuntimeSession[], activeJobs: WalletProviderJobSummary[]): DirectWalletSession[] {
  const runtimeRows = sessions.slice(0, 6).map((session) => mapRuntimeSession(session));
  if (runtimeRows.length > 0) return runtimeRows;
  return activeJobs.slice(0, 4).map((job) => ({
    id: String(job.id || job.remote_id || "job"),
    model: modelFromLaunchRequest(job.launch_request) || String(job.remote_name || "Provider job"),
    status: String(job.status || "running"),
    turns: 0,
    started: relTime(job.created_at || job.updated_at),
    rate: "Live",
    provider: String(job.provider_name || job.provider_slug || "Provider"),
    hw: hardwareFromLaunchRequest(job.launch_request, job.gpu || "GPU"),
  }));
}

function mapStorageVolumes(volumes: BackendStorageVolume[]): DirectWalletVolume[] {
  return managedBackendStorageVolumes(volumes).map((volume) => {
    const usedBytes = Number(volume.used_bytes || 0);
    const availableBytes = Number(volume.available_bytes || 0);
    const capacityBytes = usedBytes + availableBytes;
    const sizeGB = Number(volume.size_gb || 0);
    const usedPct = capacityBytes > 0
      ? Math.min(100, Math.round((usedBytes / capacityBytes) * 100))
      : sizeGB > 0
        ? 0
        : undefined;
    return {
      id: String(volume.id || volume.provider_volume_id || volume.name || "volume"),
      name: String(volume.name || volume.provider_volume_id || "Storage volume"),
      size: sizeGB > 0 ? `${sizeGB.toLocaleString()} GB` : formatBytes(capacityBytes),
      files: Math.max(0, Number(volume.file_count || 0)),
      mod: relTime(volume.updated_at),
      provider: "GENRL S3",
      providerSlug: String(volume.provider_slug || ""),
      sourceSlug: String(volume.source_slug || ""),
      routeID: String(volume.route_id || ""),
      region: String(volume.region_name || volume.environment_name || "global"),
      status: String(volume.status || "available"),
      usedPct,
    };
  });
}

function mapWalletStorageFile(item: BackendStorageItem): DirectWalletStorageFile {
  const fallbackID = item.provider_path || item.filename || "file";
  return {
    id: String(item.id || fallbackID),
    filename: String(item.filename || item.provider_path || "file"),
    provider: item.provider ? String(item.provider) : undefined,
    providerPath: item.provider_path ? String(item.provider_path) : undefined,
    providerStatus: item.provider_status ? String(item.provider_status) : undefined,
    providerError: item.provider_error ? String(item.provider_error) : undefined,
    sizeBytes: Number.isFinite(Number(item.size_bytes)) ? Number(item.size_bytes) : undefined,
    contentType: item.content_type ? String(item.content_type) : undefined,
    createdAt: item.created_at ? String(item.created_at) : undefined,
    updatedAt: item.updated_at ? String(item.updated_at) : undefined,
    downloadURL: normalizeWalletStorageDownloadURL(item.download_url),
  };
}

function normalizeWalletStorageDownloadURL(value?: string) {
  const raw = String(value || "").trim();
  if (!raw) return undefined;
  if (/^https?:\/\//i.test(raw)) return raw;
  return raw.startsWith("/") ? raw : `/${raw}`;
}

function storageDataProviderForWalletVolume(volume: DirectWalletVolume) {
  const token = normalizeStorageRouteToken(`${volume.providerSlug || volume.provider} ${volume.sourceSlug} ${volume.routeID}`);
  if (token.includes("hyperstack")) return "hyperstack";
  if (token.includes("genrl") || token.includes("managed")) return "genrl";
  return normalizeStorageRouteToken(volume.providerSlug || volume.sourceSlug) || "genrl";
}

function isLiveBackendStorageVolume(volume: BackendStorageVolume) {
  const token = String(volume.status || "available").trim().toLowerCase().replace(/[_\s]+/g, "-");
  return !["deleted", "deleting", "terminated", "terminating", "failed", "error", "unavailable", "offline"].includes(token);
}

function isGENRLManagedBackendStorageVolume(volume: BackendStorageVolume) {
  const routeID = String(volume.route_id || "").trim().toLowerCase();
  const sourceSlug = normalizeStorageRouteToken(volume.source_slug);
  const providerSlug = normalizeStorageRouteToken(volume.provider_slug);
  if (routeID === "genrl::managed-storage") return true;
  if (sourceSlug === "genrl" && providerSlug === "genrl") return true;
  if (routeID || sourceSlug || providerSlug) return false;
  const provider = normalizeStorageRouteToken(volume.provider);
  return provider === "genrl" || provider === "genrl-s3";
}

function managedBackendStorageVolumes(volumes: BackendStorageVolume[]) {
  return volumes.filter((volume) => isGENRLManagedBackendStorageVolume(volume) && isLiveBackendStorageVolume(volume));
}

function backendStorageVolumeIdentityKeys(volume: BackendStorageVolume) {
  const keys = new Set<string>();
  const routeID = String(volume.route_id || "").trim().toLowerCase();
  const sourceSlug = String(volume.source_slug || "").trim().toLowerCase();
  const providerSlug = String(volume.provider_slug || "").trim().toLowerCase();
  for (const value of [volume.id, volume.provider_volume_id]) {
    const token = String(value || "").trim().toLowerCase();
    if (token) keys.add(`id:${token}`);
  }
  const name = String(volume.name || "").trim().toLowerCase();
  if (name) {
    keys.add(`name:${name}`);
    keys.add(`name-route:${name}:${routeID}:${sourceSlug}:${providerSlug}`);
  }
  return keys;
}

function backendStorageVolumesMatch(left: BackendStorageVolume, right: BackendStorageVolume) {
  const leftKeys = backendStorageVolumeIdentityKeys(left);
  for (const key of backendStorageVolumeIdentityKeys(right)) {
    if (leftKeys.has(key)) return true;
  }
  return false;
}

function mergePersistentStorageVolumes(previous: BackendStorageVolume[], incoming: BackendStorageVolume[]) {
  const merged: BackendStorageVolume[] = [];
  const deleted = incoming.filter((volume) => isGENRLManagedBackendStorageVolume(volume) && !isLiveBackendStorageVolume(volume));
  managedBackendStorageVolumes(incoming).forEach((volume) => {
    if (!merged.some((item) => backendStorageVolumesMatch(item, volume))) merged.push(volume);
  });
  managedBackendStorageVolumes(previous).forEach((volume) => {
    if (deleted.some((item) => backendStorageVolumesMatch(item, volume))) return;
    if (merged.some((item) => backendStorageVolumesMatch(item, volume))) return;
    merged.push(volume);
  });
  return merged;
}

function storageVolumeQuery(volume: DirectWalletVolume) {
  const params = new URLSearchParams();
  if (volume.routeID) params.set("route_id", volume.routeID);
  if (volume.sourceSlug) params.set("source_slug", volume.sourceSlug);
  if (volume.providerSlug) params.set("provider_slug", volume.providerSlug);
  const query = params.toString();
  return query ? `?${query}` : "";
}

function storageCreateRouteQuery(route: Pick<DirectWalletStorageRoute, "routeID" | "sourceSlug" | "providerSlug">) {
  const params = new URLSearchParams();
  if (route.routeID) params.set("route_id", route.routeID);
  else if (route.sourceSlug) params.set("source_slug", route.sourceSlug);
  else if (route.providerSlug) params.set("provider_slug", route.providerSlug);
  const query = params.toString();
  return query ? `?${query}` : "";
}

function normalizeStorageRouteToken(value?: string) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function synthesizeStorageRouteID(sourceSlug: string, providerSlug: string) {
  const source = normalizeStorageRouteToken(sourceSlug);
  const provider = normalizeStorageRouteToken(providerSlug);
  if (source && provider) return `${source}::${provider}`;
  return source || provider || "";
}

function storageLabel(value: string) {
  return String(value || "")
    .replace(/[-_]+/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function storageVolumeTypeKey(value: string) {
  const token = String(value || "").trim().toLowerCase();
  if (!token) return "volume";
  if (token.includes("network")) return "network-volume";
  if (token.includes("filesystem")) return "filesystem";
  if (token.includes("nvme")) return "nvme";
  if (token.includes("archive")) return "archive";
  if (token.includes("object")) return "object";
  if (token.includes("ssd")) return "ssd";
  return token.replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || "volume";
}

function mapWalletStorageVolumeTypes(payload: BackendStorageVolumeTypesResponse) {
  const pricing = new Map<string, number>();
  const offered = new Map<string, string>();
  (payload.volume_type_pricing || []).forEach((row) => {
    const raw = String(row.volume_type || "").trim();
    if (raw) offered.set(storageVolumeTypeKey(raw), raw);
    const price = Number(row.price_per_gb_hour || 0);
    if (raw && Number.isFinite(price) && price > 0) {
      pricing.set(storageVolumeTypeKey(raw), price);
      pricing.set(raw.toLowerCase(), price);
    }
  });
  (payload.volume_types || []).forEach((value) => {
    const raw = String(value || "").trim();
    if (raw) offered.set(storageVolumeTypeKey(raw), raw);
  });
  const sourceTypes = offered.size > 0 ? Array.from(offered.values()) : ["object"];
  return sourceTypes.map((volumeType) => {
    const id = storageVolumeTypeKey(volumeType);
    return {
      id,
      label: storageLabel(volumeType || id),
      volumeType,
      pricePerGBHour: pricing.get(id) || pricing.get(volumeType.toLowerCase()) || 0,
    };
  });
}

function mapWalletStorageEnvironments(payload: BackendStorageEnvironmentsResponse) {
  const seen = new Set<string>();
  return (payload.environments || [])
    .map((row) => ({
      name: String(row.name || "").trim(),
      region: String(row.region || "").trim(),
    }))
    .filter((row) => {
      const key = row.name.toLowerCase();
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

function walletStorageRouteFromBackend(route: BackendStorageRoute): DirectWalletStorageRoute | null {
  const sourceSlug = normalizeStorageRouteToken(route.source_slug);
  const providerSlug = normalizeStorageRouteToken(route.provider_slug) || sourceSlug;
  const routeID = String(route.route_id || synthesizeStorageRouteID(sourceSlug, providerSlug)).trim();
  const driver = normalizeStorageRouteToken(route.driver);
  if (!sourceSlug || !routeID) return null;
  if (!isGENRLManagedStorageRoute(routeID, sourceSlug, providerSlug, driver, route.managed)) return null;
  return {
    id: routeID,
    routeID,
    sourceSlug,
    providerSlug,
    label: "GENRL S3",
    detail: "Object storage through the GENRL account",
    environments: [],
    volumeTypes: [],
  };
}

function isGENRLManagedStorageRoute(routeID: string, sourceSlug: string, providerSlug: string, driver?: string, managed?: boolean) {
  return (
    managed ||
    routeID.toLowerCase() === "genrl::managed-storage" ||
    normalizeStorageRouteToken(driver) === "managed" ||
    (sourceSlug === "genrl" && providerSlug === "genrl")
  );
}

function managedObjectStorageTypes(payload: BackendStorageVolumeTypesResponse) {
  const objectType = mapWalletStorageVolumeTypes(payload).find((item) => item.id === "object" || item.volumeType.toLowerCase() === "object");
  return [objectType || { id: "object", label: "S3 object storage", volumeType: "object", pricePerGBHour: 0 }];
}

async function loadWalletStorageCreateRoutes(): Promise<DirectWalletStorageRoute[]> {
  const payload = await apiRequest<{ routes?: BackendStorageRoute[] }>("/api/v1/storage/routes", {
    method: "GET",
    timeoutMs: 12000,
    cacheMaxAgeMs: 60000,
  });
  const routes = (payload.routes || [])
    .map(walletStorageRouteFromBackend)
    .filter((route): route is DirectWalletStorageRoute => route !== null);
  const withMetadata = await Promise.all(routes.map(async (route): Promise<DirectWalletStorageRoute | null> => {
    const query = storageCreateRouteQuery(route);
    try {
      const [typePayload, environmentPayload] = await Promise.all([
        apiRequest<BackendStorageVolumeTypesResponse>(`/api/v1/storage/volume-types${query}`, {
          method: "GET",
          timeoutMs: 20000,
          cacheMaxAgeMs: 60000,
        }),
        apiRequest<BackendStorageEnvironmentsResponse>(`/api/v1/storage/environments${query}`, {
          method: "GET",
          timeoutMs: 20000,
          cacheMaxAgeMs: 60000,
        }),
      ]);
      const volumeTypes = managedObjectStorageTypes(typePayload);
      const environments = mapWalletStorageEnvironments(environmentPayload);
      if (volumeTypes.length === 0 || environments.length === 0) return null;
      return { ...route, volumeTypes, environments };
    } catch {
      return null;
    }
  }));
  return withMetadata.filter((route): route is DirectWalletStorageRoute => route !== null);
}

async function loadWalletStorageVolumes(): Promise<BackendStorageVolume[]> {
  const payload = await apiRequest<{ volumes?: BackendStorageVolume[] }>(
    `/api/v1/storage/volumes${GENRL_MANAGED_STORAGE_QUERY}`,
    { method: "GET", timeoutMs: 20000 },
  );
  return managedBackendStorageVolumes(Array.isArray(payload.volumes) ? payload.volumes : []);
}

async function loadWalletProviderHardware(): Promise<BackendHardwareResponse> {
  try {
    return await apiRequest<BackendHardwareResponse>(WALLET_PROVIDER_HARDWARE_API_PATH, {
      method: "GET",
      timeoutMs: 36000,
    });
  } catch (error) {
    if (!(error instanceof APIError) || (error.status !== 404 && error.status !== 405)) {
      throw error;
    }
    return apiRequest<BackendHardwareResponse>(WALLET_PROVIDER_HARDWARE_LEGACY_API_PATH, {
      method: "GET",
      timeoutMs: 36000,
    });
  }
}

function sameBackendStorageVolume(volume: BackendStorageVolume, target: DirectWalletVolume) {
  return backendStorageVolumesMatch(volume, {
    id: target.id,
    name: target.name,
    route_id: target.routeID,
    source_slug: target.sourceSlug,
    provider_slug: target.providerSlug,
  });
}

function snapshotDisplayTitle(snapshot: BackendRunSnapshot, runtime: Record<string, unknown>, metadata: Record<string, unknown>) {
  const fallbackName = firstString(snapshot.name);
  const genericName = fallbackName.toLowerCase() === "dashboard-workload" ? "" : fallbackName;
  return (
    shortText(
      firstString(
        runtime.prompt,
        runtime.input_prompt,
        runtime.task_prompt,
        runtime.source_title,
        metadata.prompt,
        metadata.input_prompt,
        metadata.task_prompt,
        metadata.source_title,
      ),
      72,
    ) ||
    firstString(metadata.title, runtime.title, genericName, runtime.model, metadata.model, metadata.runtime_model) ||
    "Run snapshot"
  );
}

function mapSnapshots(snapshots: BackendRunSnapshot[]): DirectWalletSnapshot[] {
  return snapshots.map((snapshot) => {
    const runtime = snapshot.runtime || {};
    const metadata = snapshot.metadata || {};
    const id = String(snapshot.id || snapshot.provider_job_id || snapshot.runtime_session_id || "snapshot");
    const outputDownloadURL = normalizeWalletStorageDownloadURL(snapshot.output_download_url) || `/api/v1/run-snapshots/${encodeURIComponent(id)}/files/output`;
    const manifestDownloadURL = normalizeWalletStorageDownloadURL(snapshot.manifest_download_url) || `/api/v1/run-snapshots/${encodeURIComponent(id)}/files/manifest`;
    const model = snapshotDisplayTitle(snapshot, runtime, metadata);
    return {
      id,
      model,
      created: relTime(snapshot.created_at),
      size: formatBytes(Number(snapshot.output_size_bytes || 0) + Number(snapshot.manifest_size_bytes || 0)),
      jobId: String(snapshot.provider_job_id || snapshot.runtime_session_id || ""),
      status: String(snapshot.status || "available"),
      outputDownloadURL: buildAPIURL(outputDownloadURL),
      manifestDownloadURL: buildAPIURL(manifestDownloadURL),
    };
  });
}

function mapMarketOffers(offers: BackendMarketOffer[]): DirectWalletMarketOffer[] {
  return offers.slice(0, 12).map((offer, index) => {
    const buyerHourly = Number(offer.buyer_unit_price_micros || 0) / MICRO || Number(offer.price_per_hour || 0);
    const gpuCount = Math.max(1, Number(offer.gpu_count || 1));
    const ratePerHour = buyerHourly > 0 ? buyerHourly * gpuCount : 0;
    return {
      id: String(offer.option_id || `${offer.provider_slug || "offer"}_${index}`),
      name: String(offer.provider_name || offer.provider_slug || offer.source || "Provider"),
      region: String(offer.region_name || "global"),
      gpu: String(offer.gpu || offer.gpu_type || offer.flavor_name || "GPU"),
      vram: gpuCount > 1 ? `${gpuCount} GPUs` : "GPU",
      rate: Number(ratePerHour.toFixed(2)),
      up: Number(offer.stock_available || 0) > 0,
      uptime: "live",
    };
  });
}

function normalizeWalletRouteProAccessMode(value: unknown): DirectWalletProAccessMode | "" {
  const mode = String(value || "").trim().toLowerCase().replace(/[\s-]+/g, "_");
  return mode === "vm" || mode === "container" || mode === "notebook" || mode === "api" || mode === "cluster" ? mode : "";
}

function normalizeWalletRouteClusterType(value: unknown): DirectWalletClusterType | "" {
  const profile = String(value || "").trim().toLowerCase().replace(/[\s-]+/g, "_");
  if (profile === "genrl" || profile === "genrl_managed" || profile === "managed" || profile === "head_node") return "genrl-managed";
  if (profile === "k8s" || profile === "kube" || profile === "kubernetes") return "kubernetes";
  if (profile === "slurm") return "slurm";
  if (profile === "ray") return "ray";
  return "";
}

function mapProAccessOptions(options?: BackendProAccessOption[]): DirectWalletProAccessOption[] {
  return (Array.isArray(options) ? options : []).map((option): DirectWalletProAccessOption | null => {
    const mode = normalizeWalletRouteProAccessMode(option.mode);
    if (!mode) return null;
    const resolvedParameters = Object.fromEntries(
      Object.entries(option.resolved_parameters || {})
        .map(([key, value]) => [String(key).trim(), String(value ?? "").trim()])
        .filter(([key, value]) => key && value),
    );
    return {
      mode,
      supportMode: firstString(option.support_mode),
      providers: Array.isArray(option.providers) ? option.providers.map(String).filter(Boolean) : [],
      regions: Array.isArray(option.regions) ? option.regions.map(String).filter(Boolean) : [],
      clusterTypes: Array.isArray(option.cluster_types)
        ? option.cluster_types.map(normalizeWalletRouteClusterType).filter((value): value is DirectWalletClusterType => Boolean(value))
        : [],
      requiredParameters: Array.isArray(option.required_parameters) ? option.required_parameters.map(String).filter(Boolean) : [],
      resolvedParameters: Object.keys(resolvedParameters).length > 0 ? resolvedParameters : undefined,
    };
  }).filter((option): option is DirectWalletProAccessOption => Boolean(option));
}

function mapHardwareOptions(options: BackendHardwareOption[]): DirectWalletHardwareOption[] {
  return options.map((option, index) => {
    const hardwareLabel = firstString(option.hardware_label, option.hardware_id, "GPU");
    const hardwareId = firstString(option.hardware_id, hardwareLabel);
    return {
      id: `${hardwareId || hardwareLabel || "hardware"}_${index}`,
      hardwareId,
      hardwareLabel,
      gpuCount: Math.max(1, Number(option.gpu_count || 1)),
      stockAvailable: Math.max(0, Number(option.stock_available || 0)),
      launchable: typeof option.launchable === "boolean"
        ? option.launchable
        : Number(option.stock_available || 0) > 0,
      capacityStatus: firstString(option.capacity_status) || undefined,
      bestPricePerHour: Number(option.best_price_per_hour || 0),
      bestBuyerMicros: Number(option.best_buyer_price_micros || 0),
      originalBuyerMicros: Number(option.original_buyer_price_micros || 0),
      discountPercent: Math.max(0, Math.round(Number(option.discount_percent || 0))),
      fromProAccount: Boolean(option.from_pro_account),
      providerCount: Math.max(0, Number(option.provider_count || 0)),
      providers: Array.isArray(option.providers) ? option.providers.map(String).filter(Boolean) : [],
      regions: Array.isArray(option.regions) ? option.regions.map(String).filter(Boolean) : [],
      runtimeClasses: Array.isArray(option.runtime_classes) ? option.runtime_classes.map(String).filter(Boolean) : [],
      proAccessModes: Array.isArray(option.pro_access_modes)
        ? option.pro_access_modes.map(normalizeWalletRouteProAccessMode).filter((value): value is DirectWalletProAccessMode => Boolean(value))
        : [],
      proAccessOptions: mapProAccessOptions(option.pro_access_options),
      updatedAt: firstString(option.updated_at),
    };
  });
}

function mapMembers(org: WorkspaceOrgResponse | null, workspace: WorkspacePublic | null): DirectWalletMember[] {
  const usage = org?.memberUsage || [];
  if (usage.length > 0) {
    return usage.map((member) => ({
      id: member.memberId,
      email: member.email,
      name: member.name || member.email,
      role: member.role === "owner" ? "Owner" : member.role === "admin" ? "Admin" : "Viewer",
      roleKey: member.role === "owner" || member.role === "admin" ? member.role : "viewer",
      status: member.status,
      canBudget: Boolean(
        workspace?.currentRole === "owner" &&
          member.status === "active" &&
          member.userId &&
          member.memberId &&
          member.memberId !== workspace.currentMemberId,
      ),
      spend: creditsFromMicros(member.spentCreditMicros),
      active: member.status === "active" || member.activeWorkloadCount > 0 || member.activeRuntimeCount > 0,
      initials: initials(member.name, member.email),
      tasks: Math.max(0, Number(member.workloadCount || 0) + Number(member.runtimeTurnCount || 0)),
      budget: creditsFromMicros(member.budgetLimitMicros || member.totalCreditMicros || MICRO),
      budgetFunded: creditsFromMicros(member.budgetFundedMicros),
      budgetRemaining: creditsFromMicros(member.budgetRemainingMicros),
      budgetStatus: member.budgetStatus,
    }));
  }
  return (workspace?.members || []).map((member) => ({
    id: member.id,
    email: member.email,
    name: member.name || member.email,
    role: member.role === "owner" ? "Owner" : member.role === "admin" ? "Admin" : "Viewer",
    roleKey: member.role,
    status: member.status,
    canBudget: Boolean(
      workspace?.currentRole === "owner" &&
        member.status === "active" &&
        member.userId &&
        member.id &&
        member.id !== workspace.currentMemberId,
    ),
    spend: 0,
    active: member.status === "active",
    initials: initials(member.name, member.email),
    tasks: 0,
    budget: 0,
    budgetFunded: 0,
    budgetRemaining: 0,
  }));
}

function mapWorkspaceActivity(org: WorkspaceOrgResponse | null): DirectWalletWorkspaceActivity[] {
  return [...(org?.memberUsage || [])]
    .filter((member) => member.lastActivityAt || member.workloadCount || member.runtimeTurnCount)
    .sort((a, b) => new Date(b.lastActivityAt || 0).getTime() - new Date(a.lastActivityAt || 0).getTime())
    .slice(0, 8)
    .map((member) => ({
      u: initials(member.name, member.email),
      name: member.name || member.email,
      act: member.activeRuntimeCount > 0
        ? `has ${member.activeRuntimeCount} active runtime${member.activeRuntimeCount === 1 ? "" : "s"}`
        : `ran ${member.workloadCount || member.runtimeTurnCount || 0} workload${(member.workloadCount || member.runtimeTurnCount) === 1 ? "" : "s"}`,
      cost: creditsFromMicros(member.spentCreditMicros),
      jobId: null,
      t: relTime(member.lastActivityAt),
    }));
}

function buildWorkspaceInfo(org: WorkspaceOrgResponse | null, fallbackWorkspace: WorkspacePublic | null): DirectWalletWorkspaceInfo {
  const workspace = fallbackWorkspace ?? org?.workspace;
  const currentRole = workspace?.currentRole === "owner" || workspace?.currentRole === "admin" ? workspace.currentRole : "viewer";
  const isOwner = currentRole === "owner";
  const rawWorkspaceType = String(workspace?.workspaceType || "").trim().toLowerCase();
  return {
    id: workspace?.id || "",
    name: workspace?.companyName || workspace?.name || "Workspace",
    workspaceType: ["organization", "enterprise", "org", "team"].includes(rawWorkspaceType)
      ? "organization"
      : "personal",
    billingEmail: workspace?.billingEmail || "",
    currentRole,
    canManage: Boolean(workspace && hasWorkspacePermission(workspace, "manage_workspace")),
    canManageBilling: Boolean(workspace && hasWorkspacePermission(workspace, "billing")),
    canInvite: Boolean(workspace && hasWorkspacePermission(workspace, "invite_users")),
    canBudget: isOwner,
    budgetLimit: creditsFromMicros(org?.stats?.budgetLimitMicros),
    budgetFunded: creditsFromMicros(org?.stats?.budgetFundedMicros),
    budgetRemaining: creditsFromMicros(org?.stats?.budgetRemainingMicros),
  };
}

function mapWorkspaceInvites(invites?: WorkspacePendingInvite[]): DirectWalletWorkspaceInvite[] {
  return (Array.isArray(invites) ? invites : []).map((invite): DirectWalletWorkspaceInvite => ({
    id: String(invite.id || ""),
    workspaceId: String(invite.workspaceId || ""),
    workspaceName: invite.workspaceName || invite.companyName || "Workspace",
    companyName: invite.companyName || invite.workspaceName || "Workspace",
    email: invite.email || "",
    role: invite.role === "owner" || invite.role === "admin" ? invite.role : "viewer",
    invitedAt: invite.invitedAt || "",
  })).filter((invite) => Boolean(invite.id && invite.workspaceId));
}

function proStats(providers: ProProviderConnection[]): DirectWalletProStats {
  const visible = providers.filter((provider) => String(provider.status || "").toLowerCase() !== "revoked");
  const earnings = visible.reduce((sum, provider) => sum + creditsFromMicros(provider.earned_credit_micros), 0);
  const pending = visible.reduce((sum, provider) => sum + creditsFromMicros(provider.owner_pending_payout_micros), 0);
  const proCredits = visible.reduce((sum, provider) => sum + creditsFromMicros(provider.provider_balance_micros), 0);
  const postpaidProviders = visible.filter((provider) => String(provider.provider_balance_kind || "").toLowerCase() === "postpaid");
  const active = visible.filter((provider) => ["active", "validating"].includes(String(provider.status || "").toLowerCase()));
  return {
    proEnabled: visible.length > 0,
    proCredits: formatCredits(proCredits),
    proCreditsHint:
      proCredits > 0
        ? postpaidProviders.length > 0
          ? "prepaid balances; postpaid billing shown below"
          : "connected provider API balance"
        : postpaidProviders.length > 0
          ? "postpaid billing connected; no prepaid credits"
          : "no connected provider API balance detected",
    earnings: formatCredits(earnings),
    earningsHint: pending > 0 ? `${formatCredits(pending)} pending payout` : visible.length ? "connected provider earnings" : "no provider earnings",
    activeNodes: String(active.length),
    activeNodesHint: `${visible.length} connected`,
    uptime: active.length > 0 ? "Live" : "—",
    uptimeHint: "provider status",
    providers: visible.map((provider) => {
      const hardware = (provider.allowed_hardware || []).slice(0, 2).join(", ") || "Provider capacity";
      return {
        id: provider.id,
        slug: provider.provider_slug,
        n: provider.display_name || provider.provider_name,
        g: hardware,
        m: ["network", "marketplace", "public"].includes(String(provider.visibility || "").toLowerCase())
          ? "Network"
          : "Private",
        e: proProviderPricingSummary(provider),
        active: String(provider.status || "").toLowerCase() === "active",
        status: provider.status,
        credentialLast4: provider.credential_last4,
        hasCredential: provider.has_credential,
        authMode: provider.auth_mode,
        principalMask: provider.principal_mask,
        providerName: provider.provider_name,
        visibility: provider.visibility,
        broadcastMode: provider.broadcast_mode,
        pricingMode: provider.pricing_mode,
        providerAdjustmentBps: Number(provider.provider_adjustment_bps || 0),
        fixedUnitPriceMicros: Number(provider.fixed_unit_price_micros || 0),
        minUnitPriceMicros: Number(provider.min_unit_price_micros || 0),
        maxUnitPriceMicros: Number(provider.max_unit_price_micros || 0),
        reserveBalanceThresholdMicros: Number(provider.reserve_balance_threshold_micros || 0),
        broadcastStockLimit: Number(provider.broadcast_stock_limit || 0),
        allowedRegions: Array.isArray(provider.allowed_regions) ? provider.allowed_regions : [],
        allowedHardware: Array.isArray(provider.allowed_hardware) ? provider.allowed_hardware : [],
        providerBalanceMicros: Number(provider.provider_balance_micros || 0),
        providerBalanceCurrency: provider.provider_balance_currency,
        providerBalanceLastCheckedAt: provider.provider_balance_last_checked_at,
        providerBalanceKind: provider.provider_balance_kind,
        providerBalanceStatus: provider.provider_balance_status,
        creditStatus: provider.credit_status,
        earnedCreditMicros: Number(provider.earned_credit_micros || 0),
        pendingPayoutMicros: Number(provider.owner_pending_payout_micros || 0),
        settledJobs: Number(provider.settled_jobs || 0),
        lastValidatedAt: provider.last_validated_at,
        lastSettlementAt: provider.last_settlement_at,
        lastError: provider.last_error,
        updatedAt: provider.updated_at,
      };
    }),
    payoutDestination: pending > 0 ? `${formatCredits(pending)} pending` : "Configure payout in Store",
  };
}

function cleanDisplayName(user: ReturnType<typeof useAuth>["user"]) {
  const email = String(user?.email || "").trim().toLowerCase();
  const name = String(user?.name || "").trim();
  const daemonName = String(user?.daemon?.userName || "").trim();
  if (name && name.toLowerCase() !== email) return name;
  if (daemonName && daemonName.toLowerCase() !== email) return daemonName;
  return name || user?.username || "Account";
}

function currentUser(user: ReturnType<typeof useAuth>["user"], planName: string): DirectWalletUser {
  const name = cleanDisplayName(user);
  return {
    name,
    handle: user?.username ? `@${user.username}` : user?.email ? `@${user.email.split("@")[0]}` : "@account",
    id: user?.id || "wallet",
    initials: initials(name || user?.username || "", user?.email || ""),
    plan: planName || "Workspace",
    email: user?.email || "",
  };
}

function providerJobHasRecordedOutput(job: WalletProviderJobSummary) {
  return Boolean(taskOutputFromLaunch(job.launch_request || {}));
}

function providerJobUsesGateway(job: WalletProviderJobSummary) {
  const launch = job.launch_request || {};
  return Boolean(launch.model_gateway_requested) ||
    lowerValue(launch.execution_mode).includes("gateway") ||
    lowerValue(launch.wallet_route_type).includes("gateway") ||
    lowerValue(launch.wallet_route_label) === "gateway" ||
    lowerValue(launch.hardware_label).includes("model gateway") ||
    lowerValue(job.gpu).includes("model gateway");
}

function providerJobNeedsFastRefresh(job: WalletProviderJobSummary) {
  if (providerJobHasRecordedOutput(job)) return false;
  const launch = job.launch_request || {};
  const states = [
    lowerValue(job.status),
    lowerValue(job.provider_status),
    lowerValue(launch.managed_runtime_status),
    lowerValue(job.lease?.status),
  ].filter(Boolean);
  if (states.length === 0) return false;
  if (states.some((state) => ["failed", "terminated", "completed", "done", "closed", "stopped", "expired"].includes(state))) return false;
  return states.some((state) =>
    ["launching", "running", "queued", "unknown", "starting", "pending", "provision", "runtime_ready", "ready", "processing", "streaming", "prompt", "loading"].some((token) =>
      state.includes(token),
    ),
  );
}

function runtimeSessionNeedsFastRefresh(session: RuntimeSession) {
  const state = lowerValue(session.state);
  if (!state) return false;
  return ["provisioning", "runtime_ready", "running_input", "extending_reserve", "stopping"].includes(state) ||
    ["provision", "running", "processing", "extending", "stopping"].some((token) => state.includes(token));
}

function walletRefreshIntervalMs(jobs: WalletProviderJobSummary[], sessions: RuntimeSession[]) {
  if (jobs.some((job) => providerJobUsesGateway(job) && providerJobNeedsFastRefresh(job))) return GATEWAY_WORK_REFRESH_MS;
  if (jobs.some(providerJobNeedsFastRefresh) || sessions.some(runtimeSessionNeedsFastRefresh)) return ACTIVE_WORK_REFRESH_MS;
  return IDLE_REFRESH_MS;
}

function walletHasActiveWork(jobs: WalletProviderJobSummary[], sessions: RuntimeSession[]) {
  return walletRefreshIntervalMs(jobs, sessions) !== IDLE_REFRESH_MS;
}

function activeProviderJobs(jobs: WalletProviderJobSummary[]) {
  return jobs.filter(providerJobNeedsFastRefresh);
}

function hardwareRoutingPreferenceFromLabel(value: unknown) {
  const text = String(value || "").trim().toLowerCase().replace(/[^a-z0-9]+/g, " ");
  if (/\bh100\b|\bh200\b/.test(text)) return "fastest";
  if (/\ba100\b/.test(text)) return "balanced";
  if (/\ba10g?\b|\ba10\b|\bl4\b|\bt4\b|\bl40s?\b|\ba40\b|\brtx\b|\b4090\b|\b3090\b/.test(text)) return "cheapest";
  return "balanced";
}

function hardwareEstimatedMinutesFromLabel(value: unknown) {
  const text = String(value || "").trim().toLowerCase().replace(/[^a-z0-9]+/g, " ");
  return /\bh100\b|\bh200\b/.test(text) ? 4 : 8;
}

function hardwareLaunchLabelFromValue(value: unknown) {
  const text = String(value || "").trim();
  const normalized = text.toLowerCase().replace(/[^a-z0-9]+/g, " ");
  if (/\bh200\b/.test(normalized)) return "H200 141 GB";
  if (/\bh100\b/.test(normalized)) return "H100 80 GB";
  if (/\ba100\b/.test(normalized) && /\b40\b/.test(normalized)) return "A100 40 GB";
  if (/\ba100\b/.test(normalized)) return "A100 80 GB";
  if (/\bl40s\b/.test(normalized)) return "L40S 48 GB";
  if (/\bl40\b/.test(normalized)) return "L40 48 GB";
  if (/\brtx\b.*\ba6000\b|\ba6000\b/.test(normalized)) return "RTX A6000 48 GB";
  if (/\ba10g?\b|\ba10\b/.test(normalized)) return "A10G 24 GB";
  if (/\bl4\b/.test(normalized)) return "L4 24 GB";
  if (/\bt4\b/.test(normalized)) return "T4 16 GB";
  return text;
}

function hardwareLabelFromDashboard(input: DirectWalletLaunchInput) {
  const explicitLabel = firstString(input.hardwareLabel);
  const hardwareValue = firstString(input.hardware);
  const label = hardwareLaunchLabelFromValue(explicitLabel || hardwareValue) || "GPU";
  const maxPriceCredits = Number(input.maxPriceCredits || 0);
  const estimatedMinutes = Number(input.estimatedMinutes || 0);
  const gpuCount = Number(input.gpuCount || 0);
  return {
    label,
    max: Number.isFinite(maxPriceCredits) && maxPriceCredits > 0 ? maxPriceCredits : 5,
    preference: hardwareRoutingPreferenceFromLabel(label),
    estimatedMinutes: Number.isFinite(estimatedMinutes) && estimatedMinutes > 0 ? estimatedMinutes : hardwareEstimatedMinutesFromLabel(label),
    gpuCount: Number.isFinite(gpuCount) && gpuCount > 0 ? Math.max(1, Math.round(gpuCount)) : 1,
  };
}

function providerSlugFromDashboard(input: DirectWalletLaunchInput) {
  return walletProviderSlugFromLabel(input.providerSlug);
}

function normalizeRuntimeSessionIdleSeconds(value: unknown) {
  const seconds = Math.floor(Number(value));
  if (!Number.isFinite(seconds) || seconds <= 0) return 30 * 60;
  return Math.min(24 * 60 * 60, Math.max(5 * 60, seconds));
}

function runtimeSessionTTLSecondsForIdle(idleSeconds: number) {
  return Math.min(24 * 60 * 60, Math.max(4 * 60 * 60, normalizeRuntimeSessionIdleSeconds(idleSeconds)));
}

function createClientLaunchID() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return `ui_${crypto.randomUUID().replace(/-/g, "")}`;
  }
  return `ui_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function csvCell(value: unknown) {
  const text = String(value ?? "");
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

function normalizeAccountSessions(value: unknown): DirectWalletAccountSession[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((item: any): DirectWalletAccountSession => {
      const status = String(item?.status || "").toLowerCase();
      return {
        id: String(item?.id || ""),
        current: Boolean(item?.current),
        status: status === "revoked" || status === "expired" || status === "invalid" ? status : "active",
        userAgent: String(item?.userAgent || ""),
        ipAddress: String(item?.ipAddress || ""),
        createdAt: String(item?.createdAt || ""),
        lastSeenAt: String(item?.lastSeenAt || ""),
        expiresAt: String(item?.expiresAt || ""),
        revokedAt: String(item?.revokedAt || ""),
      };
    })
    .filter((session) => Boolean(session.id));
}

function WalletRouteComponent() {
  return <WalletDashboardRoute search={Route.useSearch()} />;
}

export function WalletDashboardRoute({ search }: { search: WalletDashboardSearch }) {
  const { user, logout, updateUser } = useAuth();
  const notifications = useNotifications();
  const {
    stripe_status: stripeStatus,
    checkout_id: stripeCheckoutID,
    session_id: stripeSessionID,
    workspace_id: topupWorkspaceID,
  } = search;
  const initialSnapshot = useMemo(
    () => readWalletDashboardSnapshot<WalletRouteSnapshot>(user?.id),
    [user?.id],
  );
  const initialStorageVolumes = useMemo(
    () => managedBackendStorageVolumes(initialSnapshot?.storageVolumes ?? []),
    [initialSnapshot],
  );
  const [balance, setBalance] = useState<BackendCreditBalance | null>(initialSnapshot?.balance ?? null);
  const [ledgerEntries, setLedgerEntries] = useState<WalletLedgerEntry[]>(initialSnapshot?.ledgerEntries ?? []);
  const [spendChart, setSpendChart] = useState<WalletSpendChartPoint[]>(initialSnapshot?.spendChart ?? []);
  const [jobs, setJobs] = useState<WalletProviderJobSummary[]>(initialSnapshot?.jobs ?? []);
  const [runtimeSessions, setRuntimeSessions] = useState<RuntimeSession[]>(initialSnapshot?.runtimeSessions ?? []);
  const [storageVolumes, setStorageVolumes] = useState<BackendStorageVolume[]>(initialStorageVolumes);
  const [storageCreateRoutes, setStorageCreateRoutes] = useState<DirectWalletStorageRoute[]>([]);
  const [snapshots, setSnapshots] = useState<BackendRunSnapshot[]>(initialSnapshot?.snapshots ?? []);
  const [marketOffers, setMarketOffers] = useState<BackendMarketOffer[]>(initialSnapshot?.marketOffers ?? []);
  const [hardwareOptions, setHardwareOptions] = useState<BackendHardwareOption[]>(initialSnapshot?.hardwareOptions ?? []);
  const [workspace, setWorkspace] = useState<WorkspacePublic | null>(initialSnapshot?.workspace ?? initialSnapshot?.workspaceOrg?.workspace ?? null);
  const [workspaceOrg, setWorkspaceOrg] = useState<WorkspaceOrgResponse | null>(initialSnapshot?.workspaceOrg ?? null);
  const [workspaceInvites, setWorkspaceInvites] = useState<WorkspacePendingInvite[]>(initialSnapshot?.workspaceInvites ?? initialSnapshot?.workspaceOrg?.pendingInvites ?? []);
  const [planMode, setPlanMode] = useState<ProAccountPlan["mode"]>(initialSnapshot?.planMode ?? "standard");
  const [proProviders, setProProviders] = useState<ProProviderConnection[]>(initialSnapshot?.proProviders ?? []);
  const [loading, setLoading] = useState(!initialSnapshot);
  const [refreshing, setRefreshing] = useState(false);
  const [launchBusy, setLaunchBusy] = useState(false);
  const [accountSessions, setAccountSessions] = useState<DirectWalletAccountSession[]>([]);
  const [accountSessionsLoading, setAccountSessionsLoading] = useState(false);
  const [accountSessionsError, setAccountSessionsError] = useState("");
  const [revokingAccountSessionId, setRevokingAccountSessionId] = useState("");
  const dashboardRefreshInFlightRef = useRef<Promise<void> | null>(null);
  const refreshRef = useRef<(options?: { silent?: boolean; background?: boolean }) => Promise<void>>(async () => {});
  const workRefreshRef = useRef<(options?: { background?: boolean }) => Promise<void>>(async () => {});
  const ledgerEntriesRef = useRef<WalletLedgerEntry[]>(initialSnapshot?.ledgerEntries ?? []);
  const jobsRef = useRef<WalletProviderJobSummary[]>(initialSnapshot?.jobs ?? []);
  const providerJobsReconcileRef = useRef<Promise<void> | null>(null);
  const runtimeSessionsRef = useRef<RuntimeSession[]>(initialSnapshot?.runtimeSessions ?? []);
  const storageVolumesRef = useRef<BackendStorageVolume[]>(initialStorageVolumes);
  const proProvidersRef = useRef<ProProviderConnection[]>(initialSnapshot?.proProviders ?? []);
  const proProviderMutationVersionRef = useRef(0);
  const observedTaskJobIDsRef = useRef<Set<string>>(new Set(
    (initialSnapshot?.jobs || []).map((job) => providerJobStableID(job)).filter(Boolean),
  ));
  const notifiedTaskOutputSignaturesRef = useRef<Set<string>>(new Set(
    (initialSnapshot?.jobs || [])
      .map((job) => walletTaskOutputNotification(job)?.signature || "")
      .filter(Boolean),
  ));
  const notifiedWorkspaceInviteIDsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!billingNotificationsEnabled(user?.id)) return;
    const threshold = lowBalanceAlertThreshold(balance);
    const previous = readLowBalanceAlertThreshold(user?.id);
    if (threshold === null) {
      if (previous !== null) writeLowBalanceAlertThreshold(user?.id, null);
      return;
    }
    if (previous === null || threshold < previous) {
      const title = lowBalanceAlertTitle(threshold);
      const body = lowBalanceAlertBody(balance as BackendCreditBalance, threshold);
      notifications.push({ kind: "wallet", title, body });
      toast.warning(title, { description: body });
      writeLowBalanceAlertThreshold(user?.id, threshold);
      return;
    }
    if (threshold > previous) {
      writeLowBalanceAlertThreshold(user?.id, threshold);
    }
  }, [balance, notifications, user?.id]);

  useEffect(() => {
    if (!workspaceNotificationsEnabled(user?.id)) return;
    for (const invite of workspaceInvites) {
      if (!invite.id || notifiedWorkspaceInviteIDsRef.current.has(invite.id)) continue;
      notifiedWorkspaceInviteIDsRef.current.add(invite.id);
      const workspaceName = invite.companyName || invite.workspaceName || "a workspace";
      notifications.push({
        kind: "connect",
        title: "Workspace invite",
        body: `${workspaceName} invited you as ${invite.role === "owner" ? "workspace owner" : invite.role}.`,
      });
    }
  }, [notifications, user?.id, workspaceInvites]);

  function applySnapshot(snapshot: WalletRouteSnapshot) {
    setBalance(snapshot.balance);
    ledgerEntriesRef.current = snapshot.ledgerEntries;
    setLedgerEntries(snapshot.ledgerEntries);
    setSpendChart(Array.isArray(snapshot.spendChart) ? snapshot.spendChart : []);
    jobsRef.current = snapshot.jobs;
    setJobs(snapshot.jobs);
    runtimeSessionsRef.current = snapshot.runtimeSessions;
    setRuntimeSessions(snapshot.runtimeSessions);
    storageVolumesRef.current = managedBackendStorageVolumes(snapshot.storageVolumes);
    setStorageVolumes(storageVolumesRef.current);
    setSnapshots(snapshot.snapshots);
    setMarketOffers(snapshot.marketOffers);
    setHardwareOptions(snapshot.hardwareOptions || []);
    setWorkspace(snapshot.workspace ?? snapshot.workspaceOrg?.workspace ?? null);
    setWorkspaceOrg(snapshot.workspaceOrg);
    setWorkspaceInvites(snapshot.workspaceInvites || snapshot.workspaceOrg?.pendingInvites || []);
    setPlanMode(snapshot.planMode);
    proProvidersRef.current = snapshot.proProviders;
    setProProviders(snapshot.proProviders);
  }

  function writeProProvidersSnapshot(nextProProviders: ProProviderConnection[]) {
    const persisted = readWalletDashboardSnapshot<WalletRouteSnapshot>(user?.id);
    writeWalletDashboardSnapshot<WalletRouteSnapshot>({
      balance: persisted?.balance ?? balance,
      ledgerEntries: persisted?.ledgerEntries ?? ledgerEntriesRef.current,
      spendChart: persisted?.spendChart ?? spendChart,
      jobs: persisted?.jobs ?? jobsRef.current,
      runtimeSessions: persisted?.runtimeSessions ?? runtimeSessionsRef.current,
      storageVolumes: managedBackendStorageVolumes(persisted?.storageVolumes ?? storageVolumesRef.current),
      snapshots: persisted?.snapshots ?? snapshots,
      marketOffers: persisted?.marketOffers ?? marketOffers,
      hardwareOptions: persisted?.hardwareOptions ?? hardwareOptions,
      workspace: persisted?.workspace ?? workspace,
      workspaceOrg: persisted?.workspaceOrg ?? workspaceOrg,
      workspaceInvites: persisted?.workspaceInvites ?? workspaceInvites,
      planMode: persisted?.planMode ?? planMode,
      proProviders: nextProProviders,
    }, user?.id);
  }

  function applyProProviderMutation(nextProProviders: ProProviderConnection[]) {
    proProviderMutationVersionRef.current += 1;
    proProvidersRef.current = nextProProviders;
    setProProviders(nextProProviders);
    writeProProvidersSnapshot(nextProProviders);
  }

  function writeStorageSnapshot(nextStorageVolumes: BackendStorageVolume[]) {
    writeWalletDashboardSnapshot<WalletRouteSnapshot>({
      balance,
      ledgerEntries: ledgerEntriesRef.current,
      spendChart,
      jobs: jobsRef.current,
      runtimeSessions: runtimeSessionsRef.current,
      storageVolumes: nextStorageVolumes,
      snapshots,
      marketOffers,
      hardwareOptions,
      workspace,
      workspaceOrg,
      workspaceInvites,
      planMode,
      proProviders,
    }, user?.id);
  }

  function notifyNewTaskOutputs(nextJobs: WalletProviderJobSummary[]) {
    const observed = observedTaskJobIDsRef.current;
    const notified = notifiedTaskOutputSignaturesRef.current;
    nextJobs.forEach((job) => {
      const jobID = providerJobStableID(job);
      const candidate = walletTaskOutputNotification(job);
      if (candidate && observed.has(candidate.jobID) && !notified.has(candidate.signature)) {
        notified.add(candidate.signature);
        showWorkloadOutputNotification(candidate, user?.id, notifications.push);
      } else if (candidate) {
        notified.add(candidate.signature);
      }
      if (jobID) observed.add(jobID);
    });
  }

  function writeProviderJobsSnapshot(nextJobs: WalletProviderJobSummary[]) {
    const persisted = readWalletDashboardSnapshot<WalletRouteSnapshot>(user?.id);
    writeWalletDashboardSnapshot<WalletRouteSnapshot>({
      balance: persisted?.balance ?? balance,
      ledgerEntries: persisted?.ledgerEntries ?? ledgerEntriesRef.current,
      spendChart: persisted?.spendChart ?? spendChart,
      jobs: nextJobs,
      runtimeSessions: persisted?.runtimeSessions ?? runtimeSessionsRef.current,
      storageVolumes: managedBackendStorageVolumes(persisted?.storageVolumes ?? storageVolumesRef.current),
      snapshots: persisted?.snapshots ?? snapshots,
      marketOffers: persisted?.marketOffers ?? marketOffers,
      hardwareOptions: persisted?.hardwareOptions ?? hardwareOptions,
      workspace: persisted?.workspace ?? workspace,
      workspaceOrg: persisted?.workspaceOrg ?? workspaceOrg,
      workspaceInvites: persisted?.workspaceInvites ?? workspaceInvites,
      planMode: persisted?.planMode ?? planMode,
      proProviders: persisted?.proProviders ?? proProviders,
    }, user?.id);
  }

  function applyProviderJobs(nextJobs: WalletProviderJobSummary[]) {
    notifyNewTaskOutputs(nextJobs);
    jobsRef.current = nextJobs;
    setJobs(nextJobs);
    writeProviderJobsSnapshot(nextJobs);
  }

  function reconcileProviderJobsInBackground() {
    if (providerJobsReconcileRef.current) return providerJobsReconcileRef.current;
    const run = (async () => {
      try {
        const payload = await apiRequest<{ jobs?: WalletProviderJobSummary[] }>(
          "/api/wallet/accounts?action=provider_job_reconcile",
          { method: "GET", timeoutMs: PROVIDER_JOBS_RECONCILE_TIMEOUT_MS },
        );
        if (!Array.isArray(payload.jobs)) return;
        applyProviderJobs(mergePersistentProviderJobs(jobsRef.current, payload.jobs));
      } catch {
        // The visible refresh path already has fallback data; long reconciliation is best-effort.
      }
    })();
    const tracked = run.finally(() => {
      if (providerJobsReconcileRef.current === tracked) providerJobsReconcileRef.current = null;
    });
    providerJobsReconcileRef.current = tracked;
    return tracked;
  }

  const loadAccountSessions = useCallback(async () => {
    if (!user?.id) {
      setAccountSessions([]);
      setAccountSessionsError("");
      setAccountSessionsLoading(false);
      return;
    }
    setAccountSessionsLoading(true);
    setAccountSessionsError("");
    try {
      const payload = await apiRequest<HostedSessionsResponse>(HOSTED_SESSIONS_API, {
        method: "GET",
        timeoutMs: 12000,
      });
      setAccountSessions(normalizeAccountSessions(payload.sessions));
    } catch (error) {
      setAccountSessionsError(error instanceof Error ? error.message : "Could not load sessions.");
    } finally {
      setAccountSessionsLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    void loadAccountSessions();
  }, [loadAccountSessions]);

  async function handleRevokeAccountSession(sessionId: string) {
    const target = accountSessions.find((session) => session.id === sessionId);
    if (!target || target.current || target.status !== "active") return;
    setRevokingAccountSessionId(sessionId);
    try {
      await apiRequest(`${HOSTED_SESSIONS_API}/${encodeURIComponent(sessionId)}/revoke`, {
        method: "POST",
        timeoutMs: 12000,
      });
      toast.success("Session revoked");
      await loadAccountSessions();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not revoke session.");
    } finally {
      setRevokingAccountSessionId("");
    }
  }

  async function handleRevokeOtherAccountSessions() {
    const activeOthers = accountSessions.filter((session) => !session.current && session.status === "active");
    if (activeOthers.length === 0) return;
    setRevokingAccountSessionId("all");
    try {
      const payload = await apiRequest<{ revoked?: number }>(`${HOSTED_SESSIONS_API}/revoke-all`, {
        method: "POST",
        timeoutMs: 12000,
      });
      const revoked = Number(payload.revoked || 0);
      toast.success(`${revoked} session${revoked === 1 ? "" : "s"} revoked`);
      await loadAccountSessions();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not revoke sessions.");
    } finally {
      setRevokingAccountSessionId("");
    }
  }

  async function runDashboardRefresh(options?: { silent?: boolean; background?: boolean }) {
    const proProviderMutationVersion = proProviderMutationVersionRef.current;
    const hasVisibleData =
      balance !== null ||
      ledgerEntries.length > 0 ||
      jobs.length > 0 ||
      runtimeSessions.length > 0 ||
      storageVolumes.length > 0 ||
      marketOffers.length > 0 ||
      proProviders.length > 0 ||
      workspace !== null ||
      workspaceOrg !== null;
    if (!options?.background) {
      if (options?.silent || hasVisibleData) setRefreshing(true);
      else setLoading(true);
    }

    try {
      const balanceRequest = apiRequest<BackendCreditBalance>("/api/wallet/balance", {
        method: "GET",
        timeoutMs: 12000,
        cache: "no-store",
      }).then((payload) => {
        setBalance(payload);
        return payload;
      }).catch(() => balance);
      const ledgerRequest = apiRequest<{ entries?: WalletLedgerEntry[]; spend_chart?: WalletSpendChartPoint[] }>(
        "/api/wallet/ledger?limit=24",
        { method: "GET", timeoutMs: 12000, cache: "no-store" },
      ).then((payload) => {
        const fetchedEntries = Array.isArray(payload.entries) ? payload.entries : ledgerEntriesRef.current;
        const nextEntries = mergePersistentWalletLedgerEntries(ledgerEntriesRef.current, fetchedEntries);
        ledgerEntriesRef.current = nextEntries;
        setLedgerEntries(nextEntries);
        if (Array.isArray(payload.spend_chart)) setSpendChart(payload.spend_chart);
        return payload;
      }).catch(() => ({ entries: ledgerEntriesRef.current, spend_chart: spendChart }));

      const [
        balancePayload,
        ledgerPayload,
        jobsPayload,
        sessionsPayload,
        storagePayload,
        storageCreateRoutesPayload,
        snapshotsPayload,
        marketPayload,
        hardwarePayload,
        planPayload,
        proProvidersPayload,
        workspacePayload,
        workspaceDirectPayload,
      ] = await Promise.all([
        balanceRequest,
        ledgerRequest,
        apiRequest<{ jobs?: WalletProviderJobSummary[] }>("/api/wallet/accounts?action=provider_jobs&fast=1", { method: "GET", timeoutMs: PROVIDER_JOBS_VISIBLE_REFRESH_TIMEOUT_MS }).catch(() => ({ jobs: jobsRef.current })),
        apiRequest<{ sessions?: RuntimeSession[] }>("/api/v1/runtime/sessions", { method: "GET", timeoutMs: 18000 }).catch(() => ({ sessions: runtimeSessionsRef.current })),
        loadWalletStorageVolumes().catch(() => storageVolumesRef.current),
        loadWalletStorageCreateRoutes().catch(() => storageCreateRoutes),
        apiRequest<{ snapshots?: BackendRunSnapshot[] }>("/api/v1/run-snapshots?limit=50", { method: "GET", timeoutMs: 16000 }).catch(() => ({ snapshots })),
        apiRequest<{ offers?: BackendMarketOffer[] }>("/api/v1/market/offers", { method: "GET", timeoutMs: 20000 }).catch(() => null),
        loadWalletProviderHardware().catch(() => null),
        readProAccountPlan().catch(() => ({ plan: { mode: planMode } })),
        readProProviders().catch(() => ({ providers: proProvidersRef.current })),
        readWorkspaceOrg().catch(() => null),
        readWorkspace().catch(() => null),
      ]);

      const fetchedLedgerEntries = Array.isArray(ledgerPayload.entries) ? ledgerPayload.entries : ledgerEntriesRef.current;
      const nextLedgerEntries = mergePersistentWalletLedgerEntries(ledgerEntriesRef.current, fetchedLedgerEntries);
      const nextSpendChart = Array.isArray(ledgerPayload.spend_chart) ? ledgerPayload.spend_chart : spendChart;
      const fetchedJobs = Array.isArray(jobsPayload.jobs) ? jobsPayload.jobs : jobsRef.current;
      const nextJobs = mergePersistentProviderJobs(jobsRef.current, fetchedJobs);
      const fetchedRuntimeSessions = Array.isArray(sessionsPayload.sessions) ? sessionsPayload.sessions : runtimeSessionsRef.current;
      const nextRuntimeSessions = mergePersistentRuntimeSessions(runtimeSessionsRef.current, fetchedRuntimeSessions);
      const fetchedStorageVolumes = Array.isArray(storagePayload) ? storagePayload : storageVolumesRef.current;
      const nextStorageVolumes = managedBackendStorageVolumes(fetchedStorageVolumes);
      const fetchedMarketOffers = Array.isArray(marketPayload?.offers) ? marketPayload.offers : null;
      const fetchedHardwareOptions = Array.isArray(hardwarePayload?.hardware_options) ? hardwarePayload.hardware_options : null;
      const nextMarketOffers = fetchedMarketOffers ?? marketOffers;
      const nextHardwareOptions = resolveWalletRunHardwareOptions(
        fetchedHardwareOptions,
        fetchedMarketOffers,
        hardwareOptions,
      );
      const fetchedProProviders = Array.isArray(proProvidersPayload.providers)
        ? proProvidersPayload.providers
        : proProvidersRef.current;
      const nextProProviders = resolveProProviderRefreshResult(
        fetchedProProviders,
        proProvidersRef.current,
        proProviderMutationVersion,
        proProviderMutationVersionRef.current,
      );
      const workspaceState = resolveWorkspaceDashboardState({
        organization: workspacePayload,
        direct: workspaceDirectPayload,
        previousWorkspace: workspace,
        previousOrganization: workspaceOrg,
        previousInvites: workspaceInvites,
      });
      const nextSnapshot: WalletRouteSnapshot = {
        balance: balancePayload ?? balance,
        ledgerEntries: nextLedgerEntries,
        spendChart: nextSpendChart,
        jobs: nextJobs,
        runtimeSessions: nextRuntimeSessions,
        storageVolumes: nextStorageVolumes,
        snapshots: Array.isArray(snapshotsPayload.snapshots) ? snapshotsPayload.snapshots : snapshots,
        marketOffers: nextMarketOffers,
        hardwareOptions: nextHardwareOptions,
        workspace: workspaceState.workspace,
        workspaceOrg: workspaceState.organization,
        workspaceInvites: workspaceState.pendingInvites,
        planMode: planPayload?.plan?.mode === "pro" ? "pro" : "standard",
        proProviders: nextProProviders,
      };
      notifyNewTaskOutputs(nextSnapshot.jobs);
      applySnapshot(nextSnapshot);
      if (Array.isArray(storageCreateRoutesPayload)) setStorageCreateRoutes(storageCreateRoutesPayload);
      writeWalletDashboardSnapshot(nextSnapshot, user?.id);
      void reconcileProviderJobsInBackground();

      if (isWalletCloudMode()) {
        void apiRequest("/api/wallet/summary", {
          method: "POST",
          body: {},
          timeoutMs: 32000,
        }).catch(() => null);
      }
    } catch (error) {
      toast.error(error instanceof APIError ? error.detail || "Failed to refresh wallet" : "Failed to refresh wallet");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }

  function refresh(options?: { silent?: boolean; background?: boolean }) {
    if (dashboardRefreshInFlightRef.current) return dashboardRefreshInFlightRef.current;
    const pending = runDashboardRefresh(options).finally(() => {
      if (dashboardRefreshInFlightRef.current === pending) dashboardRefreshInFlightRef.current = null;
    });
    dashboardRefreshInFlightRef.current = pending;
    return pending;
  }
  refreshRef.current = refresh;

  async function refreshWorkStatus(options?: { background?: boolean }) {
    if (dashboardRefreshInFlightRef.current) return dashboardRefreshInFlightRef.current;
    if (!options?.background) setRefreshing(true);
    try {
      const [
        balancePayload,
        ledgerPayload,
        jobsPayload,
        sessionsPayload,
        snapshotsPayload,
      ] = await Promise.all([
        apiRequest<BackendCreditBalance>("/api/wallet/balance", { method: "GET", timeoutMs: 10000 }).catch(() => balance),
        apiRequest<{ entries?: WalletLedgerEntry[]; spend_chart?: WalletSpendChartPoint[] }>("/api/wallet/ledger?limit=24", { method: "GET", timeoutMs: 10000 }).catch(() => ({ entries: ledgerEntriesRef.current, spend_chart: spendChart })),
        apiRequest<{ jobs?: WalletProviderJobSummary[] }>("/api/wallet/accounts?action=provider_jobs&fast=1", { method: "GET", timeoutMs: PROVIDER_JOBS_VISIBLE_REFRESH_TIMEOUT_MS }).catch(() => ({ jobs: jobsRef.current })),
        apiRequest<{ sessions?: RuntimeSession[] }>("/api/v1/runtime/sessions", { method: "GET", timeoutMs: 12000 }).catch(() => ({ sessions: runtimeSessionsRef.current })),
        apiRequest<{ snapshots?: BackendRunSnapshot[] }>("/api/v1/run-snapshots?limit=50", { method: "GET", timeoutMs: 10000 }).catch(() => ({ snapshots })),
      ]);

      const fetchedLedgerEntries = Array.isArray(ledgerPayload.entries) ? ledgerPayload.entries : ledgerEntriesRef.current;
      const nextLedgerEntries = mergePersistentWalletLedgerEntries(ledgerEntriesRef.current, fetchedLedgerEntries);
      const nextSpendChart = Array.isArray(ledgerPayload.spend_chart) ? ledgerPayload.spend_chart : spendChart;
      const fetchedJobs = Array.isArray(jobsPayload.jobs) ? jobsPayload.jobs : jobsRef.current;
      const nextJobs = mergePersistentProviderJobs(jobsRef.current, fetchedJobs);
      const fetchedRuntimeSessions = Array.isArray(sessionsPayload.sessions) ? sessionsPayload.sessions : runtimeSessionsRef.current;
      const nextRuntimeSessions = mergePersistentRuntimeSessions(runtimeSessionsRef.current, fetchedRuntimeSessions);
      const nextSnapshot: WalletRouteSnapshot = {
        balance: balancePayload ?? balance,
        ledgerEntries: nextLedgerEntries,
        spendChart: nextSpendChart,
        jobs: nextJobs,
        runtimeSessions: nextRuntimeSessions,
        storageVolumes: storageVolumesRef.current,
        snapshots: Array.isArray(snapshotsPayload.snapshots) ? snapshotsPayload.snapshots : snapshots,
        marketOffers,
        hardwareOptions,
        workspace,
        workspaceOrg,
        workspaceInvites,
        planMode,
        proProviders: proProvidersRef.current,
      };
      notifyNewTaskOutputs(nextSnapshot.jobs);
      applySnapshot(nextSnapshot);
      writeWalletDashboardSnapshot(nextSnapshot, user?.id);
      void reconcileProviderJobsInBackground();
    } catch (error) {
      if (!options?.background) {
        toast.error(error instanceof APIError ? error.detail || "Failed to refresh wallet" : "Failed to refresh wallet");
      }
    } finally {
      if (!options?.background) setRefreshing(false);
    }
  }
  workRefreshRef.current = refreshWorkStatus;

  useEffect(() => {
    observedTaskJobIDsRef.current = new Set(
      (initialSnapshot?.jobs || []).map((job) => providerJobStableID(job)).filter(Boolean),
    );
    notifiedTaskOutputSignaturesRef.current = new Set(
      (initialSnapshot?.jobs || [])
        .map((job) => walletTaskOutputNotification(job)?.signature || "")
        .filter(Boolean),
    );
    if (initialSnapshot) {
      applySnapshot(initialSnapshot);
      setLoading(false);
      setRefreshing(false);
    }
  }, [initialSnapshot, user?.id]);

  useEffect(() => {
    void refresh({ background: Boolean(initialSnapshot) });
  }, [user?.id, initialSnapshot]);

  const workRefreshIntervalMs = useMemo(
    () => walletRefreshIntervalMs(jobs, runtimeSessions),
    [jobs, runtimeSessions],
  );

  useEffect(() => {
    let cancelled = false;
    let timer: number | undefined;
    const schedule = (delayMs: number) => {
      timer = window.setTimeout(() => {
        const active = walletHasActiveWork(jobsRef.current, runtimeSessionsRef.current);
        const runRefresh = active ? workRefreshRef.current : refreshRef.current;
        void runRefresh({ background: true }).finally(() => {
          if (cancelled) return;
          schedule(walletRefreshIntervalMs(jobsRef.current, runtimeSessionsRef.current));
        });
      }, delayMs);
    };
    schedule(workRefreshIntervalMs === IDLE_REFRESH_MS ? workRefreshIntervalMs : Math.min(500, workRefreshIntervalMs));
    return () => {
      cancelled = true;
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [workRefreshIntervalMs, user?.id]);

  useEffect(() => {
    if (!stripeStatus) return;
    if (stripeStatus === "success") {
      toast.success(stripeCheckoutID || stripeSessionID ? "Payment received. Credits will update after confirmation." : "Payment received.");
      void refresh({ silent: true });
    } else if (stripeStatus === "cancel") {
      toast.info("Credit checkout cancelled.");
    }
  }, [stripeStatus, stripeCheckoutID, stripeSessionID]);

  async function handleAddCredits(amountCredits: number) {
    try {
      const targetWorkspaceID = resolveWalletTopupWorkspace(workspace?.id, topupWorkspaceID);
      const payload = await createWorkspaceTopupCheckout(
        targetWorkspaceID,
        {
          credit_amount_micros: microsFromCredits(amountCredits),
          return_path: walletTopupReturnPath(targetWorkspaceID),
        },
        createBillingIdempotencyKey(`topup:${targetWorkspaceID}`),
      );
      const checkoutURL = String(payload.checkout_url || "").trim();
      if (!checkoutURL) throw new Error("Checkout URL missing from server response.");
      window.location.assign(checkoutURL);
    } catch (error) {
      toast.error(error instanceof APIError ? error.detail || "Failed to start checkout" : error instanceof Error ? error.message : "Failed to start checkout");
      throw error;
    }
  }

  async function handleSendCredits(input: { recipient: string; amountCredits: number; note: string }) {
    const recipient = String(input.recipient || "").trim();
    const members = workspaceOrg?.memberUsage || [];
    const match = members.find((member) =>
      [member.memberId, member.email, member.name].some((value) =>
        String(value || "").trim().toLowerCase() === recipient.toLowerCase().replace(/^@/, ""),
      ),
    );
    try {
      if (match) {
        await sendWorkspaceMemberCredits({
          memberId: match.memberId,
          email: match.email,
          amountCredits: input.amountCredits,
          note: input.note,
        });
      } else {
        await apiRequest("/api/wallet/credits/transfer", {
          method: "POST",
          body: {
            recipient,
            amountCredits: input.amountCredits,
            idempotencyKey: `ui_user_credit_transfer_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`,
            note: input.note,
          },
          cache: "no-store",
          timeoutMs: 20000,
        });
      }
      toast.success("Credits sent");
      await refresh({ silent: true });
    } catch (error) {
      toast.error(error instanceof APIError ? error.detail || "Could not send credits" : "Could not send credits");
      throw error;
    }
  }

  function persistProviderJob(job: WalletProviderJobSummary) {
    const nextJobs = mergePersistentProviderJobs(jobsRef.current, [job]);
    const nextLedgerEntries = mergePersistentWalletLedgerEntries(
      ledgerEntriesRef.current,
      [optimisticLedgerEntryForProviderJob(job, ledgerEntriesRef.current.length)],
    );
    jobsRef.current = nextJobs;
    ledgerEntriesRef.current = nextLedgerEntries;
    setLedgerEntries(nextLedgerEntries);
    setJobs(nextJobs);
    writeWalletDashboardSnapshot<WalletRouteSnapshot>({
      balance,
      ledgerEntries: nextLedgerEntries,
      spendChart,
      jobs: nextJobs,
      runtimeSessions: runtimeSessionsRef.current,
      storageVolumes: storageVolumesRef.current,
      snapshots,
      marketOffers,
      hardwareOptions,
      workspace,
      workspaceOrg,
      workspaceInvites,
      planMode,
      proProviders,
    }, user?.id);
  }

  function persistRuntimeSessions(nextRuntimeSessions: RuntimeSession[]) {
    runtimeSessionsRef.current = nextRuntimeSessions;
    setRuntimeSessions(nextRuntimeSessions);
    writeWalletDashboardSnapshot<WalletRouteSnapshot>({
      balance,
      ledgerEntries: ledgerEntriesRef.current,
      spendChart,
      jobs: jobsRef.current,
      runtimeSessions: nextRuntimeSessions,
      storageVolumes: storageVolumesRef.current,
      snapshots,
      marketOffers,
      hardwareOptions,
      workspace,
      workspaceOrg,
      workspaceInvites,
      planMode,
      proProviders,
    }, user?.id);
  }

  function markRuntimeSessionCloseRequested(sessionID: string) {
    const id = String(sessionID || "").trim();
    if (!id) return;
    let matched = false;
    const now = new Date().toISOString();
    const nextRuntimeSessions = runtimeSessionsRef.current.map((session) => {
      if (String(session.id || "").trim() !== id) return session;
      matched = true;
      return {
        ...session,
        state: "stopping",
        billing_status: "closing",
        updated_at: now,
      };
    });
    if (matched) persistRuntimeSessions(nextRuntimeSessions);
  }

  function markProviderJobCloseRequested(providerJobId: string) {
    const id = String(providerJobId || "").trim();
    if (!id) return;
    const current = jobsRef.current.find((job) => providerJobLookupKeys(job).has(id.toLowerCase()));
    if (!current) return;
    const now = new Date().toISOString();
    const launch = current.launch_request || {};
    const lease = current.lease || {};
    persistProviderJob({
      ...current,
      provider_status: firstString(current.provider_status, "USER_CLOSE_REQUESTED"),
      updated_at: now,
      launch_request: {
        ...launch,
        cleanup_status: "pending_provider_cleanup",
        wallet_price_state: "pending_provider_cleanup",
        close_requested_at: now,
      },
      lease: {
        ...lease,
        status: "cleanup_pending",
        cleanup_requested_at: firstString(lease.cleanup_requested_at, now),
        next_cleanup_at: firstString(lease.next_cleanup_at, now),
      },
    });
  }

  async function handleTerminateProviderJob(providerJobId: string) {
    const id = String(providerJobId || "").trim();
    if (!id) {
      toast.error("Resource is missing a provider job id.");
      throw new Error("Provider job id is required");
    }
    markProviderJobCloseRequested(id);
    toast.success("Provider resource close requested");
    void (async () => {
      try {
        const response = await apiRequest<{ job?: WalletProviderJobSummary }>(
          "/api/wallet/accounts?action=provider_jobs",
          {
            method: "DELETE",
            body: { id },
            timeoutMs: 32000,
            cache: "no-store",
          },
        );
        if (response.job) persistProviderJob(response.job);
        await refresh({ silent: true });
      } catch (error) {
        if (error instanceof APIError && proAccessCloseAlreadyScheduledError(`status=${error.status} ${error.detail || error.message}`)) {
          toast.success("Provider resource close already in progress");
          void refreshWorkStatus({ background: true }).catch(() => undefined);
          return;
        }
        toast.error(error instanceof APIError ? error.detail || "Could not close provider resource" : "Could not close provider resource");
        void refreshWorkStatus({ background: true }).catch(() => undefined);
      }
    })();
  }

  async function handleLaunchWorkload(input: DirectWalletLaunchInput): Promise<DirectWalletLaunchResult> {
    if (balance !== null && availableWalletCreditMicros(balance) <= 0) {
      toast.error("Purchase credits before running a task.");
      throw new APIError(402, "ERR_WALLET_CREDITS_REQUIRED", "Purchase credits before running a task.");
    }
    const gatewayMode = input.executionMode === "gateway";
    const proMode = input.runTab === "Direct";
    const sshTerminalMode = Boolean(input.sshTerminal);
    const proAccessMode = proMode ? String(input.proAccessMode || "vm") : "";
    const launchModeLabel = sshTerminalMode ? "SSH Terminal" : proMode ? "Direct" : gatewayMode ? "Gateway" : "VM/GPU";
    if (gatewayMode && input.files.length > 0) {
      toast.error("Files require VM/GPU mode.");
      throw new APIError(400, "ERR_VM_GPU_REQUIRED_FOR_FILES", "Files require VM/GPU mode.");
    }
    const snapshotSource = input.continueFromSnapshot;
    const snapshotPromptContextOnly = Boolean(snapshotSource?.promptContextOnly);
    const serviceName = gatewayMode ? "" : firstString(input.serviceName, snapshotSource?.title);
    if (!gatewayMode && !serviceName) {
      toast.error("Name this service before provisioning it.");
      throw new APIError(400, "ERR_SERVICE_NAME_REQUIRED", "Name this service before provisioning it.");
    }
    if (snapshotSource && !snapshotSource.snapshotId) {
      toast.error("Snapshot is still being saved. Refresh the ledger and try again.");
      throw new APIError(409, "ERR_RUN_SNAPSHOT_NOT_READY", "Snapshot is still being saved. Refresh the ledger and try again.");
    }
    setLaunchBusy(true);
    requestWorkloadNotificationPermission(user?.id);
    const launchToast = toast.loading(`Launching ${launchModeLabel} job...`);
    let clientLaunchID = "";
    try {
      const modelID = proMode ? "genrl-recommended" : walletDashboardModelID(input.model, input.executionMode);
      const hardware = hardwareLabelFromDashboard(input);
      const requestedProviderSlug = !gatewayMode && !proMode ? providerSlugFromDashboard(input) : "";
      const persistentMode = input.persistent && !gatewayMode && !proMode;
      const idleTimeoutSeconds = persistentMode ? normalizeRuntimeSessionIdleSeconds(input.idleTimeoutSeconds) : 0;
      const maxTTLSeconds = persistentMode ? runtimeSessionTTLSecondsForIdle(idleTimeoutSeconds) : 60 * 60;
      const quoteID = `quote_ui_${Date.now()}`;
      clientLaunchID = createClientLaunchID();
      const inputArtifacts: DashboardWorkloadArtifactUpload[] = [];
      if (input.files.length > 0) {
        toast.loading(`Uploading ${input.files.length} file${input.files.length === 1 ? "" : "s"}...`, { id: launchToast });
        for (const file of input.files) {
          inputArtifacts.push(await uploadDashboardWorkloadFile(file));
        }
        toast.loading("Launching GENRL compute...", { id: launchToast });
      }
      const inputArtifactRefs = inputArtifacts
        .map((artifact) => artifact.artifact_ref || "")
        .filter(Boolean);
      const snapshotPolicy = input.snapshots ? {
        mode: "outputs_and_logs",
        runtime_scope: persistentMode ? "session" : "single_run",
        source: snapshotSource ? "wallet_snapshot_continuation" : "wallet_dashboard",
      } : undefined;
      const contextRecall = snapshotSource ? {
        enabled: true,
        source: "wallet_task_snapshot",
        source_task_id: snapshotSource.taskId || "",
        source_snapshot_id: snapshotPromptContextOnly ? "" : snapshotSource.snapshotId || "",
        snapshot_id: snapshotPromptContextOnly ? "" : snapshotSource.snapshotId || "",
        prompt_context_snapshot_id: snapshotPromptContextOnly ? snapshotSource.snapshotId || "" : "",
        restore_mode: snapshotPromptContextOnly ? "prompt_context" : "object_download",
        source_provider_job_id: snapshotSource.providerJobId || "",
        provider_job_id: snapshotSource.providerJobId || "",
        source_client_launch_id: snapshotSource.clientLaunchId || "",
        client_launch_id: snapshotSource.clientLaunchId || "",
        source_runtime_session_id: snapshotSource.runtimeSessionId || "",
        runtime_session_id: snapshotSource.runtimeSessionId || "",
        source_title: snapshotSource.title || "",
      } : undefined;
      const restoreContext = snapshotSource?.snapshotId && !snapshotPromptContextOnly ? {
        snapshot_id: snapshotSource.snapshotId,
        include_roles: ["output_text", "log", "generated_artifact"],
        target_path: "/workspace/.genrl/snapshots",
      } : undefined;
      const clusterRequested = proMode && proAccessMode === "cluster";
      const clusterType = clusterRequested ? firstString(input.clusterType) : "";
      if (clusterRequested && !clusterType) {
        toast.error("Choose a supported cluster profile before launching.");
        throw new APIError(400, "ERR_CLUSTER_PROFILE_REQUIRED", "Choose a supported cluster profile before launching.");
      }
      const clusterNodeCount = Math.max(1, Math.round(Number(input.clusterNodeCount || 1)));
      const clusterTopology = clusterRequested ? {
        support_mode: firstString(input.proAccessOption?.supportMode, "native"),
        requested_profile: clusterType,
        scheduler: clusterType,
        node_count: clusterNodeCount,
        gpus_per_node: hardware.gpuCount,
      } : undefined;
      const proAccessContract = proMode ? walletProAccessLaunchContract({
        mode: proAccessMode,
        clusterType: clusterRequested ? clusterType : undefined,
        option: input.proAccessOption,
      }) : undefined;
      const missingProParameters = walletProAccessMissingRequiredParameters(proAccessContract);
      if (missingProParameters.length > 0) {
        const missingList = missingProParameters.join(", ");
        toast.error(`Missing provider launch parameter${missingProParameters.length === 1 ? "" : "s"}: ${missingList}`);
        throw new APIError(
          400,
          "ERR_PRO_ACCESS_PARAMETERS_REQUIRED",
          `Missing provider launch parameter${missingProParameters.length === 1 ? "" : "s"}: ${missingList}`,
        );
      }
      const proExecutionPlan = proAccessContract ? { pro_access_contract: proAccessContract } : undefined;
      const workloadName = gatewayMode
        ? shortText(input.prompt, 72) || "gateway-workload"
        : serviceName;
      const workloadType = proMode ? "raw_compute" : "text_generation";
      const basePayload: Record<string, unknown> = {
        name: workloadName,
        service_name: gatewayMode ? undefined : serviceName,
        workload_type: workloadType,
        model_id: proMode ? "" : modelID,
        runtime_model: proMode ? "" : modelID,
        prompt: proMode ? "" : input.prompt,
        gateway_messages: gatewayMode && input.gatewayMessages?.length ? input.gatewayMessages : undefined,
        gateway_latest_user_message: gatewayMode ? input.prompt : undefined,
        gateway_parent_provider_job_id: gatewayMode ? input.gatewayParentProviderJobId || "" : undefined,
        execution_mode: gatewayMode ? "gateway" : "vm_gpu",
        model_gateway_requested: gatewayMode,
        pro_capacity_requested: proMode,
        ssh_terminal_requested: sshTerminalMode,
        pro_access_mode: proAccessMode,
        cluster_type: clusterRequested ? clusterType : undefined,
        cluster_node_count: clusterRequested ? clusterNodeCount : undefined,
        cluster_topology: clusterTopology,
        execution_plan: proExecutionPlan,
        wallet_route_label: launchModeLabel,
        routing_preference: gatewayMode ? "gateway" : hardware.preference,
        max_price_credits: gatewayMode ? 0 : hardware.max,
        estimated_minutes: gatewayMode ? 1 : hardware.estimatedMinutes,
        gpu_type_id: gatewayMode ? "" : hardware.label,
        hardware_label: gatewayMode ? "Model gateway" : hardware.label,
        gpu_count: gatewayMode ? 0 : hardware.gpuCount,
        account_id: "",
        provider_slug: requestedProviderSlug,
        input_artifacts: inputArtifacts,
        input_artifact_refs: inputArtifactRefs,
        save_run_context: proMode ? false : input.snapshots,
        snapshot_policy: proMode ? undefined : snapshotPolicy,
        context_recall: proMode ? undefined : contextRecall,
        restore_context: proMode ? undefined : restoreContext,
        session_mode: persistentMode ? "persistent" : "one_shot",
        wallet_quote_id: quoteID,
        wallet_reserve_amount_micros: gatewayMode ? 0 : microsFromCredits(hardware.max),
        wallet_reserve_idempotency_key: `${quoteID}:${Math.random().toString(36).slice(2, 10)}`,
        lease_ttl_hours: persistentMode ? Math.max(1, maxTTLSeconds / 3600) : 1,
        require_attestation: !gatewayMode,
        wallet_route_type: gatewayMode ? "genrl_gateway" : "genrl_abstracted",
        launch_intent: "user_confirmed",
        client_launch_id: clientLaunchID,
      };
      const planResponse = await apiRequest<{ launch_request?: Record<string, unknown> }>("/api/v1/workloads/plan", {
        method: "POST",
        body: basePayload,
        timeoutMs: 20000,
      });
      const mergedExecutionPlan = {
        ...asRecord(planResponse.launch_request?.execution_plan),
        ...(proExecutionPlan || {}),
      };
      const launchPayload = {
        ...basePayload,
        ...(planResponse.launch_request || {}),
        execution_plan: Object.keys(mergedExecutionPlan).length > 0 ? mergedExecutionPlan : undefined,
        name: workloadName,
        service_name: gatewayMode ? undefined : serviceName,
        workload_type: workloadType,
        model_id: proMode ? "" : modelID,
        runtime_model: proMode ? "" : modelID,
        prompt: proMode ? "" : input.prompt,
        gateway_messages: gatewayMode && input.gatewayMessages?.length ? input.gatewayMessages : undefined,
        gateway_latest_user_message: gatewayMode ? input.prompt : undefined,
        gateway_parent_provider_job_id: gatewayMode ? input.gatewayParentProviderJobId || "" : undefined,
        execution_mode: gatewayMode ? "gateway" : "vm_gpu",
        model_gateway_requested: gatewayMode,
        pro_capacity_requested: proMode,
        ssh_terminal_requested: sshTerminalMode,
        pro_access_mode: proAccessMode,
        cluster_type: clusterRequested ? clusterType : undefined,
        cluster_node_count: clusterRequested ? clusterNodeCount : undefined,
        cluster_topology: clusterTopology,
        routing_preference: gatewayMode ? "gateway" : hardware.preference,
        max_price_credits: gatewayMode ? 0 : hardware.max,
        estimated_minutes: gatewayMode ? 1 : hardware.estimatedMinutes,
        gpu_type_id: gatewayMode ? "" : hardware.label,
        hardware_label: gatewayMode ? "Model gateway" : hardware.label,
        gpu_count: gatewayMode ? 0 : hardware.gpuCount,
        provider_slug: requestedProviderSlug,
        save_run_context: proMode ? false : input.snapshots,
        snapshot_policy: proMode ? undefined : snapshotPolicy,
        context_recall: proMode ? undefined : contextRecall,
        restore_context: proMode ? undefined : restoreContext,
        wallet_route_label: launchModeLabel,
        wallet_route_type: gatewayMode ? "genrl_gateway" : "genrl_abstracted",
        launch_intent: "user_confirmed",
        client_launch_id: clientLaunchID,
      };
      let providerJobID = "";
      let runtimeSessionID = "";
      let launchedProviderJob: WalletProviderJobSummary | undefined;
      if (persistentMode) {
        const response = await apiRequest<RuntimeSessionDetailPayload>("/api/v1/runtime/sessions", {
          method: "POST",
          body: {
            ...launchPayload,
            session_mode: "persistent",
            persist_vm: true,
            max_spend_micros: microsFromCredits(hardware.max),
            idle_timeout_seconds: idleTimeoutSeconds,
            max_ttl_seconds: maxTTLSeconds,
            auto_stop_idle: true,
          },
          timeoutMs: 120000,
        });
        providerJobID = firstString(response.session?.provider_job_id);
        runtimeSessionID = firstString(response.session?.id);
      } else {
        const response = await apiRequest<DashboardLaunchResponse>("/api/wallet/accounts?action=abstracted_provider_job_launch", {
          method: "POST",
          body: launchPayload,
          timeoutMs: 120000,
        });
        providerJobID = firstString(
          response.provider_job_id,
          response.genrl_job_id,
          response.id,
          response.job?.id,
          response.provider_job?.id,
          response.launch_request?.provider_job_id,
          response.launch_request?.genrl_job_id,
        );
        launchedProviderJob = response.job || response.provider_job;
      }
      toast.dismiss(launchToast);
      toast.success(`${launchModeLabel} job started`);
      const launchedAt = new Date().toISOString();
      const launchedJobID = providerJobID || runtimeSessionID || clientLaunchID;
      const launchRecord = launchPayload as Record<string, unknown>;
      if (launchedProviderJob) {
        persistProviderJob({
          ...launchedProviderJob,
          id: firstString(launchedProviderJob.id, providerJobID, launchedJobID),
          launch_request: {
            ...launchPayload,
            ...(launchedProviderJob.launch_request || {}),
            provider_job_id: firstString(launchedProviderJob.launch_request?.provider_job_id, providerJobID) || undefined,
            genrl_job_id: firstString(launchedProviderJob.launch_request?.genrl_job_id, providerJobID) || undefined,
            client_launch_id: firstString(launchedProviderJob.launch_request?.client_launch_id, clientLaunchID),
            runtime_session_id: firstString(launchedProviderJob.launch_request?.runtime_session_id, runtimeSessionID) || undefined,
          },
        });
      } else {
        persistProviderJob({
          id: launchedJobID,
          provider_slug: firstString(launchRecord.provider_slug, "genrl"),
          provider_name: firstString(launchRecord.provider_name, "GENRL"),
          status: "launching",
          gpu: gatewayMode ? "Model gateway" : hardware.label,
          created_at: launchedAt,
          updated_at: launchedAt,
          launch_request: {
            ...launchPayload,
            provider_job_id: providerJobID || undefined,
            genrl_job_id: providerJobID || undefined,
            client_launch_id: clientLaunchID,
            runtime_session_id: runtimeSessionID || undefined,
            managed_runtime_status: "launching",
          },
        });
      }
      pushWalletNotification(notifications.push, {
        kind: "job",
        title: persistentMode ? "Runtime session starting" : `${launchModeLabel} job started`,
        body: [
          serviceName || shortText(input.prompt, 68),
          gatewayMode ? "Gateway mode" : proMode ? "Direct-provider capacity" : hardware.label,
          clientLaunchID,
        ].filter(Boolean).join(" · "),
      }, user?.id);
      void refreshWorkStatus({ background: true }).catch(() => undefined);
      return {
        taskId: providerJobID || runtimeSessionID || clientLaunchID,
        providerJobId: providerJobID,
        clientLaunchId: clientLaunchID,
        runtimeSessionId: runtimeSessionID,
      };
    } catch (error) {
      toast.dismiss(launchToast);
      if (clientLaunchID && providerLaunchOutcomeMayBeUnknown(error)) {
        const recovered = await recoverProviderJobLaunch<WalletProviderJobSummary>(
          clientLaunchID,
          async () => {
            const payload = await apiRequest<{ jobs?: WalletProviderJobSummary[] }>(
              "/api/wallet/accounts?action=provider_jobs&fast=1",
              { method: "GET", timeoutMs: 12000, cache: "no-store" },
            );
            return Array.isArray(payload.jobs) ? payload.jobs : [];
          },
        );
        if (recovered) {
          persistProviderJob(recovered);
          const recoveredStatus = firstString(recovered.status).toLowerCase();
          if (["failed", "terminated", "cancelled", "canceled"].includes(recoveredStatus)) {
            toast.error(
              firstString(recovered.last_error, `${launchModeLabel} launch did not start`),
            );
          } else {
            toast.success(`${launchModeLabel} job started`);
          }
          void refreshWorkStatus({ background: true }).catch(() => undefined);
          const providerJobID = firstString(recovered.id);
          return {
            taskId: providerJobID || clientLaunchID,
            providerJobId: providerJobID,
            clientLaunchId: clientLaunchID,
            runtimeSessionId: "",
          };
        }
      }
      toast.error(
        error instanceof APIError
          ? error.detail || "Could not launch workload"
          : error instanceof Error
            ? error.message
            : "Could not launch workload",
      );
      throw error;
    } finally {
      setLaunchBusy(false);
    }
  }

  async function handleLoadRuntimeSession(sessionID: string): Promise<DirectWalletRuntimeSessionDetail> {
    const id = String(sessionID || "").trim();
    if (!id) throw new APIError(400, "ERR_RUNTIME_SESSION_REQUIRED", "Runtime session is missing.");
    const payload = await apiRequest<RuntimeSessionDetailPayload>(`/api/v1/runtime/sessions/${encodeURIComponent(id)}`, {
      method: "GET",
      cache: "no-store",
      timeoutMs: 20000,
    });
    return mapRuntimeSessionDetail(payload, id);
  }

  async function handleSendRuntimeSessionTurn(
    sessionID: string,
    input: DirectWalletRuntimeSessionTurnInput,
    stream?: DirectWalletRuntimeSessionTurnStreamHandlers,
  ): Promise<DirectWalletRuntimeSessionDetail> {
    const id = String(sessionID || "").trim();
    const prompt = String(input?.text || "").trim();
    const files = Array.isArray(input?.files) ? input.files : [];
    const restoreContext = input?.restoreContext && typeof input.restoreContext === "object" ? input.restoreContext : undefined;
    if (!id) throw new APIError(400, "ERR_RUNTIME_SESSION_REQUIRED", "Runtime session is missing.");
    if (!prompt) throw new APIError(400, "ERR_RUNTIME_INPUT_REQUIRED", "Input is missing.");
    requestWorkloadNotificationPermission(user?.id);
    const sendToast = toast.loading("Sending input...");
    try {
      const inputArtifacts: DashboardWorkloadArtifactUpload[] = [];
      if (files.length > 0) {
        toast.loading(`Uploading ${files.length} file${files.length === 1 ? "" : "s"}...`, { id: sendToast });
        for (const file of files) {
          inputArtifacts.push(await uploadDashboardWorkloadFile(file));
        }
        toast.loading("Sending input...", { id: sendToast });
      }
      let detail: DirectWalletRuntimeSessionDetail;
      const turnBody = {
        input_text: prompt,
        input_artifacts: inputArtifacts,
        restore_context: restoreContext,
      };
      if (stream) {
        try {
          detail = await streamRuntimeSessionTurn(id, turnBody, stream);
        } catch (error) {
          if (!runtimeStreamCanFallback(error)) throw error;
          await apiRequest<RuntimeSessionDetailPayload>(`/api/v1/runtime/sessions/${encodeURIComponent(id)}/turns`, {
            method: "POST",
            body: turnBody,
            cache: "no-store",
            timeoutMs: 120000,
          });
          detail = await handleLoadRuntimeSession(id);
        }
      } else {
        await apiRequest<RuntimeSessionDetailPayload>(`/api/v1/runtime/sessions/${encodeURIComponent(id)}/turns`, {
          method: "POST",
          body: turnBody,
          cache: "no-store",
          timeoutMs: 120000,
        });
        detail = await handleLoadRuntimeSession(id);
      }
      toast.success("Input recorded", { id: sendToast });
      void refreshWorkStatus({ background: true }).catch(() => undefined);
      return detail;
    } catch (error) {
      toast.error(error instanceof APIError ? error.detail || "Could not send input" : "Could not send input", { id: sendToast });
      throw error;
    }
  }

  async function handleCloseRuntimeSession(sessionID: string): Promise<DirectWalletRuntimeSessionDetail> {
    const id = String(sessionID || "").trim();
    if (!id) {
      toast.error("Runtime session is missing.");
      throw new APIError(400, "ERR_RUNTIME_SESSION_REQUIRED", "Runtime session is missing.");
    }
    markRuntimeSessionCloseRequested(id);
    const closeToast = toast.loading("Closing session...");
    try {
      const payload = await apiRequest<RuntimeSessionDetailPayload>(`/api/v1/runtime/sessions/${encodeURIComponent(id)}/stop`, {
        method: "POST",
        body: {},
        cache: "no-store",
        timeoutMs: 120000,
      });
      const detail = mapRuntimeSessionDetail(payload, id);
      toast.success("Session closed", { id: closeToast });
      void refreshWorkStatus({ background: true }).catch(() => undefined);
      return detail;
    } catch (error) {
      toast.error(error instanceof APIError ? error.detail || "Could not close session" : "Could not close session", { id: closeToast });
      void refreshWorkStatus({ background: true }).catch(() => undefined);
      throw error;
    }
  }

  async function handleCreateProProvider(
    input: DirectWalletProviderInput,
  ): Promise<DirectWalletProviderCreateResult> {
    try {
      const registry = await readProviderRegistry("pro");
      const descriptor = providerDescriptorForSlug(registry.providers || [], input.providerSlug);
      const authMode = String(input.auth?.mode || "").trim().toLowerCase();
      const typedSetup = authMode.length > 0;
      const supportedAuthMode = descriptor?.credentials.modes.some(
        (mode) => String(mode || "").trim().toLowerCase() === authMode,
      );
      if (typedSetup && (!descriptor || !supportedAuthMode)) {
        throw new APIError(
          400,
          "ERR_TYPED_PROVIDER_SETUP_UNAVAILABLE",
          "This provider credential mode is not available for Pro setup.",
        );
      }
      if (!typedSetup && !providerSupportsLegacyAPIKeySetup(descriptor)) {
        throw new APIError(
          400,
          "ERR_TYPED_PROVIDER_SETUP_REQUIRED",
          "This provider requires typed credentials. Connect it from Settings > Connected providers.",
        );
      }
      const label = String(input.providerName || descriptor?.display_name || input.providerSlug || "Provider").trim();
      const payload = await createProProvider({
        provider_slug: input.providerSlug,
        provider_name: label,
        display_name: input.displayName,
        api_key: typedSetup ? undefined : input.apiKey,
        auth: typedSetup ? input.auth : undefined,
        account: typedSetup ? input.account : undefined,
        visibility: typedSetup ? "private" : input.visibility,
        broadcast_mode: typedSetup || input.visibility === "private" ? "INTERNAL_ONLY" : "BOTH",
        pricing_mode: "PASSTHROUGH",
        provider_adjustment_bps: 0,
      });
      let connectedProvider = payload.provider;
      if (payload.provider?.id) {
        const validated = await validateProProvider(payload.provider.id).catch(() => null);
        connectedProvider = validated?.provider || connectedProvider;
      }
      if (connectedProvider?.id) {
        applyProProviderMutation(upsertProProviderConnection(proProvidersRef.current, connectedProvider));
      }
      let googleOnboardingHref = "";
      let googleOnboardingMessage = "";
      if (
        connectedProvider?.id &&
        isGoogleProviderSlug(connectedProvider.provider_slug)
      ) {
        try {
          const datasetProjectID = String(
            input.account?.dataset_project_id ||
              input.account?.billing_export_project_id ||
              "",
          ).trim();
          const discovery = await discoverProProviderCredit(
            connectedProvider.id,
            datasetProjectID
              ? { dataset_project_id: datasetProjectID }
              : {},
          );
          const onboarding = discovery.credit_onboarding;
          if (
            !onboarding ||
            typeof onboarding.state !== "string" ||
            typeof onboarding.next_action !== "string" ||
            typeof onboarding.safe_message !== "string"
          ) {
            throw new Error(
              "Google promotional credit discovery returned an invalid response.",
            );
          }
          googleOnboardingMessage = String(
            onboarding.safe_message || "",
          ).trim();
          storeProProviderCreditOnboarding(
            connectedProvider.id,
            onboarding,
          );
          if (onboarding.credit_status) {
            connectedProvider = {
              ...connectedProvider,
              credit_status: onboarding.credit_status,
            };
            applyProProviderMutation(
              upsertProProviderConnection(
                proProvidersRef.current,
                connectedProvider,
              ),
            );
          }
          if (
            onboarding.job?.id ||
            googleCreditOnboardingRequiresInput(
              onboarding.next_action,
            )
          ) {
            googleOnboardingHref = googleCreditOnboardingHref(
              connectedProvider.id,
            );
          }
        } catch (discoveryError) {
          googleOnboardingHref = googleCreditOnboardingHref(
            connectedProvider.id,
          );
          googleOnboardingMessage =
            discoveryError instanceof APIError
              ? discoveryError.detail ||
                "Finish Google promotional credit setup in Settings."
              : "Finish Google promotional credit setup in Settings.";
        }
      }
      if (typedSetup && connectedProvider?.status !== "active") {
        const status = String(connectedProvider?.status || "saved").replace(/_/g, " ");
        toast.warning(
          connectedProvider?.last_error
            ? `${label} saved as ${status}: ${connectedProvider.last_error}`
            : `${label} saved as ${status}. Review the connection before launching jobs.`,
        );
      } else {
        toast.success(typedSetup ? `${label} connected` : "Provider API key added");
      }
      const result = {
        providerId: connectedProvider.id,
        providerSlug: connectedProvider.provider_slug,
      };
      if (googleOnboardingHref) {
        toast.info(
          googleOnboardingMessage ||
            "Finish Google promotional credit setup in Settings.",
        );
        window.location.assign(googleOnboardingHref);
        return result;
      }
      await refresh({ silent: true });
      return result;
    } catch (error) {
      toast.error(error instanceof APIError ? error.detail || "Could not add provider" : "Could not add provider");
      throw error;
    }
  }

  async function handleUpdateProProvider(providerID: string, input: DirectWalletCapacityUpdate) {
    try {
      const payload = await updateProProvider(providerID, input);
      if (payload.provider?.id) {
        applyProProviderMutation(upsertProProviderConnection(proProvidersRef.current, payload.provider));
      }
      toast.success("Capacity settings saved");
      await refresh({ silent: true });
    } catch (error) {
      toast.error(error instanceof APIError ? error.detail || "Could not update capacity" : "Could not update capacity");
      throw error;
    }
  }

  async function handleValidateProProvider(providerID: string) {
    try {
      const payload = await validateProProvider(providerID);
      if (payload.provider?.id) {
        applyProProviderMutation(upsertProProviderConnection(proProvidersRef.current, payload.provider));
      }
      toast.success("Provider validation started");
      await refresh({ silent: true });
    } catch (error) {
      toast.error(error instanceof APIError ? error.detail || "Could not validate provider" : "Could not validate provider");
      throw error;
    }
  }

  async function handleRotateProProviderKey(providerID: string, apiKey: string) {
    try {
      const payload = await rotateProProviderKey(providerID, apiKey);
      if (payload.provider?.id) {
        applyProProviderMutation(upsertProProviderConnection(proProvidersRef.current, payload.provider));
      }
      toast.success("Provider API key updated");
      await refresh({ silent: true });
    } catch (error) {
      toast.error(error instanceof APIError ? error.detail || "Could not update provider key" : "Could not update provider key");
      throw error;
    }
  }

  async function handleDeleteProProvider(providerID: string) {
    try {
      await deleteProProvider(providerID);
      applyProProviderMutation(proProvidersRef.current.filter((provider) => provider.id !== providerID));
      toast.success("Provider removed");
      await refresh({ silent: true });
    } catch (error) {
      toast.error(error instanceof APIError ? error.detail || "Could not remove provider" : "Could not remove provider");
      throw error;
    }
  }

  async function handleLoadProProviderLedger(providerID: string): Promise<DirectWalletProProviderLedgerRow[]> {
    try {
      const payload = await readProProviderLedger(providerID);
      return mapProProviderLedgerEntries(payload.entries || []);
    } catch (error) {
      toast.error(error instanceof APIError ? error.detail || "Could not load provider ledger" : "Could not load provider ledger");
      throw error;
    }
  }

  async function loadStorageVolumeFiles(volume: DirectWalletVolume): Promise<DirectWalletStorageFile[]> {
    const volumeID = String(volume.id || "").trim();
    if (!volumeID) {
      throw new APIError(400, "ERR_STORAGE_VOLUME_ID_REQUIRED", "Storage volume is missing an id.");
    }
    const payload = await apiRequest<{ items?: BackendStorageItem[] }>(`/api/v1/storage/volumes/${encodeURIComponent(volumeID)}/data${storageVolumeQuery(volume)}`, {
      method: "GET",
      timeoutMs: 20_000,
      cache: "no-store",
    });
    return Array.isArray(payload.items) ? payload.items.map(mapWalletStorageFile) : [];
  }

  async function attachStorageVolumeArtifact(volume: DirectWalletVolume, artifactRef: string, filename: string) {
    const volumeID = String(volume.id || "").trim();
    const cleanArtifactRef = artifactRef.trim();
    const cleanFilename = filename.trim() || cleanArtifactRef.split("/").pop() || "file";
    if (!volumeID || !cleanArtifactRef) {
      throw new APIError(400, "ERR_STORAGE_FILE_INVALID", "Storage file upload is missing required data.");
    }
    await apiRequest<{ item?: BackendStorageItem }>(`/api/v1/storage/volumes/${encodeURIComponent(volumeID)}/data${storageVolumeQuery(volume)}`, {
      method: "POST",
      body: {
        artifact_ref: cleanArtifactRef,
        filename: cleanFilename,
        provider: storageDataProviderForWalletVolume(volume),
      },
      timeoutMs: 30_000,
      cache: "no-store",
    });
  }

  async function handleUploadStorageVolumeFiles(volume: DirectWalletVolume, files: File[]) {
    const selectedFiles = files.filter(Boolean);
    if (selectedFiles.length === 0) return loadStorageVolumeFiles(volume);
    const uploadToast = toast.loading(selectedFiles.length === 1 ? `Uploading ${selectedFiles[0].name}` : `Uploading ${selectedFiles.length} files`);
    try {
      for (const file of selectedFiles) {
        const upload = await uploadDashboardWorkloadFile(file);
        const artifactRef = String(upload.artifact_ref || "").trim();
        if (!artifactRef) {
          throw new APIError(500, "ERR_UPLOAD_ARTIFACT_MISSING", "GENRL could not create an uploaded artifact.");
        }
        await attachStorageVolumeArtifact(volume, artifactRef, upload.filename || file.name);
      }
      toast.success(selectedFiles.length === 1 ? "File uploaded" : "Files uploaded", { id: uploadToast });
      await refresh({ silent: true });
      return loadStorageVolumeFiles(volume);
    } catch (error) {
      toast.error(error instanceof APIError ? error.detail || "Upload failed" : "Upload failed", { id: uploadToast });
      throw error;
    }
  }

  async function handleImportStorageVolumeHF(volume: DirectWalletVolume, input: DirectWalletHFImportInput) {
    const repoID = input.repoID.trim();
    const filename = input.filename.trim();
    if (!repoID || !filename) {
      throw new APIError(400, "ERR_HF_IMPORT_INPUT_REQUIRED", "Enter a Hugging Face repo and filename.");
    }
    const importToast = toast.loading(`Importing ${filename}`);
    try {
      const session = await apiRequest<DashboardUploadSession>("/api/v1/weight-uploads/hf-import", {
        method: "POST",
        body: {
          model_id: repoID,
          filename,
          hf_token: input.hfToken?.trim() || undefined,
          artifact_kind: "data",
          expires_in_s: 86_400,
        },
        timeoutMs: 20_000,
        cache: "no-store",
      });
      const artifactRef = String(session.artifact_ref || "").trim();
      if (!artifactRef) {
        throw new APIError(500, "ERR_UPLOAD_SESSION_INVALID", "GENRL could not create an import artifact.");
      }
      await waitForDashboardUploadCompletion(session, filename.split("/").pop() || filename);
      await attachStorageVolumeArtifact(volume, artifactRef, filename.split("/").pop() || filename);
      toast.success("Hugging Face file imported", { id: importToast });
      await refresh({ silent: true });
      return loadStorageVolumeFiles(volume);
    } catch (error) {
      toast.error(error instanceof APIError ? error.detail || "Import failed" : "Import failed", { id: importToast });
      throw error;
    }
  }

  async function handleCreateStorageVolume(input: DirectWalletStorageVolumeInput) {
    if (balance !== null && availableWalletCreditMicros(balance) <= 0) {
      toast.error("Purchase credits before opening a new volume.");
      throw new APIError(402, "ERR_WALLET_CREDITS_REQUIRED", "Purchase credits before opening a new volume.");
    }
    const name = input.name.trim();
    const sizeGB = Math.max(1, Math.floor(Number(input.sizeGB || 0)));
    const volumeType = String(input.volumeType || "").trim();
    const environmentName = String(input.environmentName || "").trim();
    if (!name) {
      toast.error("Name this storage volume first");
      throw new Error("Storage volume name is required");
    }
    if (!Number.isFinite(sizeGB) || sizeGB < 1) {
      toast.error("Enter a valid storage size");
      throw new Error("Invalid storage volume size");
    }
    if (!volumeType) {
      toast.error("Select a storage type");
      throw new Error("Storage volume type is required");
    }
    if (!environmentName) {
      toast.error("Select a storage environment");
      throw new Error("Storage environment is required");
    }
    const body: Record<string, unknown> = {
      name,
      size_gb: sizeGB,
      volume_type: volumeType,
      environment_name: environmentName,
    };
    if (input.routeID) body.route_id = input.routeID;
    if (input.sourceSlug) body.source_slug = input.sourceSlug;
    if (input.providerSlug) body.provider_slug = input.providerSlug;
    try {
      const payload = await apiRequest<{ volume?: BackendStorageVolume }>("/api/v1/storage/volumes", {
        method: "POST",
        body,
        timeoutMs: 30000,
        cache: "no-store",
      });
      const createdVolume: BackendStorageVolume = payload.volume || {
        id: `local:${input.routeID || "genrl::managed-storage"}:${name}`,
        name,
        status: "creating",
        size_gb: sizeGB,
        route_id: input.routeID || "genrl::managed-storage",
        source_slug: input.sourceSlug || "genrl",
        provider_slug: input.providerSlug || "genrl",
        provider: "GENRL",
        environment_name: environmentName,
        updated_at: new Date().toISOString(),
      };
      const nextStorageVolumes = mergePersistentStorageVolumes(storageVolumesRef.current, [createdVolume]);
      storageVolumesRef.current = nextStorageVolumes;
      setStorageVolumes(nextStorageVolumes);
      writeStorageSnapshot(nextStorageVolumes);
      toast.success("Storage volume created");
      void refresh({ silent: true }).catch(() => undefined);
    } catch (error) {
      toast.error(error instanceof APIError ? error.detail || "Could not create storage volume" : "Could not create storage volume");
      throw error;
    }
  }

  async function handleDeleteStorageVolume(volume: DirectWalletVolume) {
    const volumeID = String(volume.id || "").trim();
    if (!volumeID) {
      toast.error("Storage volume is missing an id");
      throw new Error("Storage volume is missing an id");
    }
    try {
      await apiRequest(`/api/v1/storage/volumes/${encodeURIComponent(volumeID)}${storageVolumeQuery(volume)}`, {
        method: "DELETE",
        timeoutMs: 30000,
        cache: "no-store",
      });
      const nextStorageVolumes = storageVolumesRef.current.filter((item) => !sameBackendStorageVolume(item, volume));
      storageVolumesRef.current = nextStorageVolumes;
      setStorageVolumes(nextStorageVolumes);
      writeStorageSnapshot(nextStorageVolumes);
      toast.success("Storage volume deleted");
      await refresh({ silent: true });
    } catch (error) {
      toast.error(error instanceof APIError ? error.detail || "Could not delete storage volume" : "Could not delete storage volume");
      throw error;
    }
  }

  async function handleDeleteRunSnapshot(snapshot: DirectWalletSnapshot) {
    const snapshotID = String(snapshot.id || "").trim();
    if (!snapshotID) {
      toast.error("Snapshot is missing an id");
      throw new Error("Snapshot is missing an id");
    }
    try {
      await apiRequest(`/api/v1/run-snapshots/${encodeURIComponent(snapshotID)}`, {
        method: "DELETE",
        timeoutMs: 16000,
        cache: "no-store",
      });
      setSnapshots((current) => current.filter((item) => String(item.id || "") !== snapshotID));
      toast.success("Snapshot deleted");
      await refresh({ silent: true });
    } catch (error) {
      toast.error(error instanceof APIError ? error.detail || "Could not delete snapshot" : "Could not delete snapshot");
      throw error;
    }
  }

  function applyWorkspaceDashboardUpdate(input: {
    organization?: WorkspaceOrgResponse | null;
    direct?: WorkspaceResponse | null;
  }) {
    const next = resolveWorkspaceDashboardState({
      ...input,
      previousWorkspace: workspace,
      previousOrganization: workspaceOrg,
      previousInvites: workspaceInvites,
    });
    setWorkspace(next.workspace);
    setWorkspaceOrg(next.organization);
    setWorkspaceInvites(next.pendingInvites);
  }

  async function handleUpdateWorkspace(input: { companyName: string; billingEmail: string }) {
    try {
      const payload = await updateWorkspaceProfile({
        companyName: input.companyName,
        billingEmail: input.billingEmail,
      });
      applyWorkspaceDashboardUpdate({ direct: payload });
      toast.success("Workspace name updated");
      await refresh({ silent: true });
    } catch (error) {
      toast.error(workspaceErrorMessage(error));
      throw error;
    }
  }

  async function handleInviteWorkspaceMember(input: { name: string; email: string; role: WorkspaceRole }) {
    try {
      const payload = await inviteWorkspaceMember(input);
      applyWorkspaceDashboardUpdate({ direct: payload });
      toast.success("Workspace invite sent");
      await refresh({ silent: true });
    } catch (error) {
      toast.error(workspaceErrorMessage(error));
      throw error;
    }
  }

  async function handleAcceptWorkspaceInvite(inviteId: string) {
    try {
      const payload = await acceptWorkspaceInvite(inviteId);
      applyWorkspaceDashboardUpdate({ direct: payload });
      toast.success("Workspace invite accepted");
      await refresh({ silent: true });
    } catch (error) {
      toast.error(workspaceErrorMessage(error));
      throw error;
    }
  }

  async function handleDeclineWorkspaceInvite(inviteId: string) {
    try {
      const payload = await declineWorkspaceInvite(inviteId);
      applyWorkspaceDashboardUpdate({ direct: payload });
      toast.success("Workspace invite rejected");
      await refresh({ silent: true });
    } catch (error) {
      toast.error(workspaceErrorMessage(error));
      throw error;
    }
  }

  async function handleSetMemberBudget(input: { memberId: string; amountCredits: number }) {
    if (!Number.isFinite(input.amountCredits) || input.amountCredits <= 0) {
      toast.error("Enter a positive budget amount");
      throw new Error("Invalid budget amount");
    }
    try {
      const payload = await setWorkspaceMemberBudget({
        memberId: input.memberId,
        limitCredits: input.amountCredits,
        sourceType: "owner_wallet",
        status: "active",
      });
      applyWorkspaceDashboardUpdate({ organization: payload });
      toast.success("Member budget updated");
    } catch (error) {
      toast.error(workspaceErrorMessage(error));
      throw error;
    }
  }

  async function handleSetWorkspaceBudget(input: { amountCredits: number }) {
    if (!Number.isFinite(input.amountCredits) || input.amountCredits <= 0) {
      toast.error("Enter a positive workspace budget");
      throw new Error("Invalid workspace budget");
    }
    const usage = workspaceOrg?.memberUsage || [];
    const currentMemberId = workspaceOrg?.workspace?.currentMemberId || "";
    const eligible = usage.filter((member) =>
      member.status === "active" &&
      Boolean(member.userId) &&
      Boolean(member.memberId) &&
      member.memberId !== currentMemberId,
    );
    if (!eligible.length) {
      toast.error("Invite an active team member before setting a workspace budget");
      throw new Error("No budgetable workspace members");
    }
    const existingTotalMicros = eligible.reduce((sum, member) => sum + Number(member.budgetLimitMicros || 0), 0);
    let remaining = input.amountCredits;
    let payload: WorkspaceOrgResponse | null = null;
    for (let index = 0; index < eligible.length; index += 1) {
      const member = eligible[index];
      const isLast = index === eligible.length - 1;
      const share = isLast
        ? remaining
        : existingTotalMicros > 0
          ? input.amountCredits * (Number(member.budgetLimitMicros || 0) / existingTotalMicros)
          : input.amountCredits / eligible.length;
      const amountCredits = Math.max(0.01, Number(share.toFixed(6)));
      remaining = Math.max(0, remaining - amountCredits);
      payload = await setWorkspaceMemberBudget({
        memberId: member.memberId,
        limitCredits: amountCredits,
        sourceType: "owner_wallet",
        status: "active",
      });
    }
    if (payload) applyWorkspaceDashboardUpdate({ organization: payload });
    toast.success("Workspace budget updated");
  }

  function exportLedger() {
    const rows = [
      ["id", "type", "reference_type", "reference_id", "credit_micros", "debit_micros", "created_at"],
      ...ledgerEntries.map((entry) => [
        entry.id || "",
        entry.entry_type || "",
        entry.reference_type || "",
        entry.reference_id || "",
        entry.credit_micros || 0,
        entry.debit_micros || 0,
        entry.created_at || "",
      ]),
    ];
    const csv = rows.map((row) => row.map(csvCell).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `genrl-wallet-ledger-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  const activeJobs = useMemo(() => activeProviderJobs(jobs), [jobs]);
  const creditActionsDisabled = balance !== null && availableWalletCreditMicros(balance) <= 0;
  const dashboardProps: DirectWalletDashboardProps = {
    user: currentUser(user, workspaceOrg?.plan?.name || "Workspace"),
    balance: creditsFromMicros(displayedWalletBalanceMicros(balance)),
    reserved: creditsFromMicros(balance?.reserved_credit_micros),
    chartData: spendChart.length > 0 ? spendChart : walletSpendChart(ledgerEntries),
    ledger: mapLedgerEntries(ledgerEntries, jobs, snapshots, runtimeSessions),
    sessions: mapSessions(runtimeSessions, activeJobs),
    jobs: mapJobs(jobs),
    snapshots: mapSnapshots(snapshots),
    volumes: mapStorageVolumes(storageVolumes),
    storageRoutes: storageCreateRoutes,
    members: mapMembers(workspaceOrg, workspace),
    workspaceInvites: mapWorkspaceInvites(workspaceInvites),
    workspaceActivity: mapWorkspaceActivity(workspaceOrg),
    marketOffers: mapMarketOffers(marketOffers),
    hardwareOptions: mapHardwareOptions(hardwareOptions),
    proStats: proStats(proProviders),
    workspaceInfo: buildWorkspaceInfo(workspaceOrg, workspace),
    workspaceName: workspace?.companyName || workspace?.name || "Workspace",
    storageUsagePct: storageVolumes.length
      ? Math.round(
          storageVolumes.reduce((sum, volume) => sum + Number(volume.used_bytes || 0), 0) /
            Math.max(1, storageVolumes.reduce((sum, volume) => sum + Number(volume.used_bytes || 0) + Number(volume.available_bytes || 0), 0)) *
            100,
        )
      : 0,
    loading,
    refreshing,
    launchBusy,
    creditActionsDisabled,
    accountSessions,
    accountSessionsLoading,
    accountSessionsError,
    revokingAccountSessionId,
    onRefresh: () => {
      return refreshWorkStatus({ background: true });
    },
    onAddCredits: handleAddCredits,
    onSendCredits: handleSendCredits,
    onLaunchWorkload: handleLaunchWorkload,
    onLoadRuntimeSession: handleLoadRuntimeSession,
    onSendRuntimeSessionTurn: handleSendRuntimeSessionTurn,
    onCloseRuntimeSession: handleCloseRuntimeSession,
    onTerminateProviderJob: handleTerminateProviderJob,
    onRefreshAccountSessions: loadAccountSessions,
    onRevokeAccountSession: handleRevokeAccountSession,
    onRevokeOtherAccountSessions: handleRevokeOtherAccountSessions,
    onCreateProProvider: handleCreateProProvider,
    onUpdateProProvider: handleUpdateProProvider,
    onValidateProProvider: handleValidateProProvider,
    onRotateProProviderKey: handleRotateProProviderKey,
    onDeleteProProvider: handleDeleteProProvider,
    onLoadProProviderLedger: handleLoadProProviderLedger,
    onCreateStorageVolume: handleCreateStorageVolume,
    onDeleteStorageVolume: handleDeleteStorageVolume,
    onDeleteRunSnapshot: handleDeleteRunSnapshot,
    onLoadStorageVolumeFiles: loadStorageVolumeFiles,
    onUploadStorageVolumeFiles: handleUploadStorageVolumeFiles,
    onImportStorageVolumeHF: handleImportStorageVolumeHF,
    onUpdateWorkspace: handleUpdateWorkspace,
    onInviteWorkspaceMember: handleInviteWorkspaceMember,
    onAcceptWorkspaceInvite: handleAcceptWorkspaceInvite,
    onDeclineWorkspaceInvite: handleDeclineWorkspaceInvite,
    onSetWorkspaceBudget: handleSetWorkspaceBudget,
    onSetMemberBudget: handleSetMemberBudget,
    onUpdateUser: (input) => {
      updateUser({
        name: input.name || "Account",
        email: input.email || user?.email || "",
      });
      toast.success("Display name updated");
    },
    onExportLedger: exportLedger,
    onSignOut: logout,
  };

  return <DirectAIWalletDashboard {...dashboardProps} />;
}
